// Thakar Kitchen — theme.js

// ── A little something for the curious ──────────────────────────────────────
try {
  console.log('%c\u{1F35B}  Thakar Kitchen ', 'background:#E8581F;color:#FBF1DD;font-weight:700;font-size:14px;padding:5px 12px;border-radius:6px;');
  console.log('%cYou found the kitchen’s back door, curious one.\nHandcrafted in Gujarat — hand-drawn SVGs, real ingredient percentages, and probably too much attention to pixel-perfect chutney.\nSay hi → contact@thakarkitchen.com', 'color:#1B0F08;font-size:12px;line-height:1.6;');
} catch (e) {}

// ── Cart Drawer ──────────────────────────────────────────────────────────────
let tkCartAnimate = false;
let tkLastRecsSig = null;
let tkLastRecsDominant = null; // cuisine of the last full recs render, for relabels
let tkRecsPool = null;        // cached /collections/all/products.json pool
let tkRecsPoolPromise = null; // in-flight fetch deduplication
let tkCartSubtotal = 0;       // current subtotal in rupees, kept in sync with server
let tkCartHandleSet = new Set(); // product handles currently in cart

// ── Race-condition control ───────────────────────────────────────────────────
const _tkQtyTimers = {}; // per-item-key debounce timers for qty changes
let _tkRecsTimer = null; // debounce timer for rec recomputation
let _tkRecsGen = 0;     // generation counter — stale async renders self-abort

// FIFO mutation queue — only one Shopify write runs at a time.
// Coalesces same-key pending ops so rapid taps on the same button don't pile up.
const _tkMutQ = [];
let _tkMutLocked = false;
let _tkFetchVersion = 0;  // bumped on each fetchAndRenderCart call; stale responses self-abort
// A change the shopper made that the store has not answered yet: a queued write, a drawer tap
// waiting out its debounce, or a card stepper's wanted quantity. Any cart read in the meantime
// is out of date, so it must not be drawn: drawing it put the old quantity back in the row, and
// the debounced tap then sent that old number (close and reopen mid-burst: 12 went back to 8,
// cache/cart-torture.js T8). tkCartPending() is the check from inside a queued write.
function tkCartPending() {
  return _tkMutQ.length > 0 || Object.keys(_tkQtyTimers).length > 0 ||
    (typeof _tkStepWant !== 'undefined' && Object.keys(_tkStepWant).length > 0);
}
function tkCartBusy() { return _tkMutLocked || tkCartPending(); }
// Bumped as each queued write starts. A /cart.js read sent before a write can be answered after
// it (out of order on a patchy connection) and would draw the basket from before the change, so
// a read that saw the epoch move is not drawn: the reconcile reads again once the queue is idle.
let _tkWriteEpoch = 0;

// A store reply as JSON, or a throw when the store refused it. Shopify answers a refused change (422 out of
// stock, 429 or 5xx under load) with an error object; read as a cart it had no items, so the drawer drew an
// empty basket or an old number and stayed out of step with the store (cache/cart-torture.js FAIL=0.15).
function tkJsonOk(r) {
  if (r.ok) return r.json();
  return r.json().catch(function () { return {}; }).then(function (j) {
    var e = new Error('cart ' + r.status); e.tkReason = j && (j.description || j.message); e.tkStatus = r.status; throw e;
  });
}

// Set a dish's quantity in the basket. Two things Shopify does that the theme has to follow (both seen on
// the live store, 31 Oct 2026):
//  - it gives lines new keys when a discount line lands (the free Pav Bhaji's arrival re-keyed every dish),
//    and /cart/change.js refuses an old key with a 400: the taps after it were lost (Dal 8 fell to 4);
//  - it splits a dish into two lines when the Pav Bhaji's "buy ₹1,200" covers only part of it (Paneer
//    Butter Masala 3 + 1). The drawer shows such a dish as one row (tkMergeSplit), so a change to it sets
//    the whole dish: a fresh read of its lines, then one /cart/update.js with the first at the new
//    quantity and the rest at 0 (Shopify splits it again as it likes; the total is right). It must be a
//    fresh read: /cart/update.js does not refuse an old key, it applies it to the dish's first line.
// Resolves to the Response (a cart, as /cart/change.js answers).
function tkPostChange(k, qty) {
  return fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: k, quantity: qty }) });
}
function tkSetDish(oldKey, qty, failed) {
  var vid = String(oldKey).split(':')[0];
  return fetch('/cart.js').then(tkJsonOk).then(function (c) {
    var lines = (c.items || []).filter(function (i) { return String(i.variant_id) === vid && !tkIsGiftItem(i); });
    if (!lines.length) return qty === 0 ? fetch('/cart.js') : (failed || tkPostChange(oldKey, qty));   // already gone
    var keys = lines.map(function (i) { return i.key; });
    if (failed && keys.length === 1 && keys[0] === oldKey) return failed;   // same key: a real refusal
    tkRekey(oldKey, keys[0]);
    if (keys.length === 1) { delete tkKeyGroup[keys[0]]; return tkPostChange(keys[0], qty); }
    tkKeyGroup[keys[0]] = keys;
    var u = {}; keys.forEach(function (k, n) { u[k] = n === 0 ? qty : 0; });
    return fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates: u }) });
  }).catch(function () { return failed || tkPostChange(oldKey, qty); });
}
function tkChangeRes(key, qty) {
  var grp = tkKeyGroup[key];
  if (grp && grp.length > 1) return tkSetDish(key, qty, null);
  return tkPostChange(key, qty).then(function (r) {
    if (r.status !== 400 && r.status !== 404) return r;
    return tkSetDish(key, qty, r);
  });
}
// A dish Shopify split into several lines (same variant, same properties) as one line: quantities and line
// prices added up, the first line's key, every key in tkKeyGroup[first key]. Gift lines are never merged.
var tkKeyGroup = {};
function tkMergeSplit(items) {
  var out = [], at = {};
  (items || []).forEach(function (i) {
    if (!i || tkIsGiftItem(i)) { out.push(i); return; }
    var sig = i.variant_id + '|' + JSON.stringify(i.properties || {});
    var m = at[sig];
    if (!m) { m = at[sig] = Object.assign({}, i, { _keys: [i.key] }); out.push(m); return; }
    m._keys.push(i.key);
    m.quantity = tkSafeInt(m.quantity, 0) + tkSafeInt(i.quantity, 0);
    ['line_price', 'final_line_price', 'original_line_price'].forEach(function (f) { m[f] = tkSafeNum(m[f], 0) + tkSafeNum(i[f], 0); });
  });
  out.forEach(function (m) { if (m && m._keys) { if (m._keys.length > 1) tkKeyGroup[m.key] = m._keys.slice(); else delete tkKeyGroup[m.key]; } });
  return out;
}
// A line's new key, on the drawn row (its buttons carry the key) and in the card steppers' index.
function tkRekey(oldKey, newKey) {
  if (!oldKey || !newKey || oldKey === newKey) return;
  var list = document.getElementById('cart-items-list');
  var row = list && list.querySelector('.cart-item[data-key="' + (window.CSS && CSS.escape ? CSS.escape(oldKey) : oldKey) + '"]');
  if (row) {
    row.setAttribute('data-key', newKey);
    row.querySelectorAll('[onclick]').forEach(function (b) { b.setAttribute('onclick', b.getAttribute('onclick').split(oldKey).join(newKey)); });
  }
  if (typeof tkLines !== 'undefined') Object.keys(tkLines).forEach(function (v) { if (tkLines[v] && tkLines[v].key === oldKey) tkLines[v].key = newKey; });
  if (tkKeyGroup[oldKey]) { if (!tkKeyGroup[newKey]) tkKeyGroup[newKey] = tkKeyGroup[oldKey]; delete tkKeyGroup[oldKey]; }
}

function _tkEnqueue(asyncFn, coalesceKey) {
  if (coalesceKey) {
    for (var i = _tkMutQ.length - 1; i >= 0; i--) {
      if (_tkMutQ[i].key === coalesceKey) _tkMutQ.splice(i, 1);
    }
  }
  _tkMutQ.push({ fn: asyncFn, key: coalesceKey || null });
  _tkDrainMutQ();
}

function _tkDrainMutQ() {
  if (_tkMutLocked || !_tkMutQ.length) return;
  _tkMutLocked = true;
  _tkWriteEpoch++;
  var op = _tkMutQ.shift();
  try {
    Promise.resolve(op.fn()).catch(function () {}).finally(function () {
      _tkMutLocked = false;
      _tkDrainMutQ();
      // After the queue fully drains, if the drawer is open but empty (no real
      // rows), schedule a fresh fetch — fetchAndRenderCart was skipped while
      // this mutation held the lock and the response may have been discarded.
      if (!_tkMutLocked && !_tkMutQ.length) {
        var drawer = document.getElementById('cart-drawer');
        var list = document.getElementById('cart-items-list');
        if (drawer && drawer.classList.contains('open') && list && !list.querySelector('.cart-item:not(.cart-item-removing)')) {
          fetchAndRenderCart();
        } else if (drawer && drawer.classList.contains('open')) {
          // every burst ends on one read of the basket as the store has it, whatever the last step's own
          // answer did (a superseded read once left a declined gift's row hidden in the page)
          tkReconcileSoon();
        }
      }
    });
  } catch (e) {
    _tkMutLocked = false;
    _tkDrainMutQ();
  }
}

// ── Safe numeric helpers ─────────────────────────────────────────────────────
// NaN/undefined must never reach the DOM. These coerce any bad value to a safe
// fallback so "₹NaN" and "· undefined" are structurally impossible.
function tkSafeNum(val, fallback) {
  var n = Number(val);
  return Number.isFinite(n) ? n : (typeof fallback === 'number' ? fallback : 0);
}
function tkSafeInt(val, fallback) {
  var n = parseInt(val, 10);
  return Number.isFinite(n) ? n : (typeof fallback === 'number' ? fallback : 0);
}
// Normalize a raw Shopify cart API response — missing fields default to 0/[].
function tkNormalizeCart(raw) {
  if (!raw || typeof raw !== 'object') return { items: [], item_count: 0, items_subtotal_price: 0 };
  return {
    items: Array.isArray(raw.items) ? tkMergeSplit(raw.items) : [],
    item_count: tkSafeInt(raw.item_count, 0),
    items_subtotal_price: tkSafeNum(raw.items_subtotal_price, 0),
  };
}

// Strip "Ready To Eat" prefix; normalize all-caps to Title Case
function tkCleanTitle(str) {
  if (!str) return '';
  let s = String(str).replace(/^Ready To Eat\s+/i, '').trim();
  if (s === s.toUpperCase() && /[A-Z]/.test(s)) {
    s = s.toLowerCase().replace(/\b([a-z])/g, function (m, c) { return c.toUpperCase(); });
  }
  return s;
}

// HTML-escape any value before it is placed into an innerHTML string or a
// double-quoted attribute (defense-in-depth against markup in product data).
function tkEsc(v) {
  return String(v == null ? '' : v)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

// Pre-fetch and cache the products pool; deduplicate concurrent calls
function tkFetchRecsPool() {
  if (tkRecsPool) return Promise.resolve(tkRecsPool);
  if (tkRecsPoolPromise) return tkRecsPoolPromise;
  tkRecsPoolPromise = fetch('/collections/all/products.json?limit=50')
    .then(tkJsonOk)
    .then(data => { tkRecsPool = data.products || []; tkRecsPoolPromise = null; return tkRecsPool; })
    .catch(() => { tkRecsPoolPromise = null; return []; });
  return tkRecsPoolPromise;
}

// Look up a variant's compare-at price (MRP) from the cached products pool.
// Returns rupees (int), or 0 if the pool isn't loaded / variant not found / not on sale.
function tkGetCompareAt(variantId, handle) {
  if (!tkRecsPool) return 0;
  const vid = String(variantId);
  for (let i = 0; i < tkRecsPool.length; i++) {
    const p = tkRecsPool[i];
    if (handle && p.handle !== handle) continue;
    const vs = p.variants || [];
    for (let j = 0; j < vs.length; j++) {
      if (String(vs[j].id) === vid) {
        return vs[j].compare_at_price ? Math.round(parseFloat(vs[j].compare_at_price)) : 0;
      }
    }
  }
  return 0;
}
function tkGetWeightLabel(item) {
  var weight = (window.tkWeightMap && window.tkWeightMap[item.handle]) ? window.tkWeightMap[item.handle] : '';
  var pack = (item.variant_title && item.variant_title !== 'Default Title') ? item.variant_title : '';
  // Show the pack the buyer chose (e.g. "Pack of 3") so multi-pack orders are
  // never mistaken for a single pouch. Append the per-pouch weight for context;
  // "each" disambiguates that the weight is per pouch, not the pack total.
  if (pack && weight) {
    var m = pack.match(/(\d+)/);
    var multi = m && parseInt(m[1], 10) > 1;
    return pack + ' · ' + weight + (multi ? ' each' : '');
  }
  return pack || weight;
}

// Add/refresh the struck-through MRP on cart rows once the products pool is known.
// Update one row's price column from its data-* attrs: line total (unit \u00d7 qty),
// line-level struck MRP, and a "\u20b9X each" subtitle when qty > 1. Used on render
// and on every qty change so the displayed price always matches the subtotal.
function tkRenderRowPrice(row) {
  if (!row) return;
  if (row.classList.contains('cart-item--gift')) return; // gift row shows "FREE", never a computed price
  const pricesEl = row.querySelector('.cart-item-prices');
  if (!pricesEl) return;
  const qty  = tkSafeInt(row.dataset.qty, 1) || 1;
  const unit = Math.round(tkSafeInt(row.dataset.price, 0) / 100);
  const priceEl = pricesEl.querySelector('.cart-item-price');
  if (priceEl) priceEl.textContent = '\u20b9' + (unit * qty);
  // line-level MRP strike (needs the pool for compare-at)
  const mrp = tkRecsPool ? tkGetCompareAt(row.dataset.variant, row.dataset.handle) : 0;
  let mrpEl = pricesEl.querySelector('.cart-item-mrp');
  if (mrp > unit) {
    if (!mrpEl) {
      mrpEl = document.createElement('span');
      mrpEl.className = 'cart-item-mrp';
      pricesEl.insertBefore(mrpEl, pricesEl.firstChild);
    }
    mrpEl.textContent = '\u20b9' + (mrp * qty);
  } else if (mrpEl) {
    mrpEl.remove();
  }
  // "\u20b9X each" subtitle only when qty > 1
  let eachEl = pricesEl.querySelector('.cart-item-each');
  if (qty > 1) {
    if (!eachEl) {
      eachEl = document.createElement('span');
      eachEl.className = 'cart-item-each';
      pricesEl.appendChild(eachEl);
    }
    eachEl.textContent = '\u20b9' + unit + ' each';
  } else if (eachEl) {
    eachEl.remove();
  }
}

function tkPatchCartPrices() {
  const list = document.getElementById('cart-items-list');
  if (!list) return;
  list.querySelectorAll('.cart-item:not(.cart-item-removing)').forEach(tkRenderRowPrice);
}

// Free-delivery economics — single source of truth for the progress bar.
var TK_FREE_SHIP_THRESHOLD = 799;

// Free-gift economics — mirrors the Shopify automatic Buy-X-Get-Y discount
// "Free Pav Bhaji on orders over ₹1,200". The discount zeroes the gift at
// checkout; the cart JS auto-adds/removes it and drives the gift progress strip.
// These two must agree with that discount: the theme only ADDS and LABELS the
// line, it never prices it. Point this id at a product the discount does not
// grant and the shopper is charged for a row the cart calls FREE.
var TK_GIFT_VARIANT_ID = 48300505628927; // Pav Bhaji (product pav-bhaji) — 1 Pack (₹160); was Veg Fried Rice 48934038012159 until Oct 2026
var TK_GIFT_NAME = 'Pav Bhaji';          // what the drawer calls the gift
var TK_GIFT_HANDLE = 'pav-bhaji';
var TK_GIFT_THRESHOLD  = 1200;           // ₹ of qualifying (non-gift) food to unlock
// The surprise at ₹2,000 (Shopify: "Surprise: free Rajma on orders over ₹2,000", Buy X Get Y, combines with
// the Pav Bhaji's): a second free line, `_free_gift: 'surprise'`, that sits wrapped in the basket until the
// shopper taps to unwrap it. The layout sets window.TK_SUR_OK from the dish's stock, so a sold-out surprise
// is never promised. Same rule as the gift: change the discount and these together.
var TK_SUR_VARIANT_ID = 48300506513663; // Raseela Rajma (product rajma) — 1 Pack (₹160)
var TK_SUR_NAME = 'Raseela Rajma';
var TK_SUR_HANDLE = 'rajma';
var TK_SUR_THRESHOLD = 2000;
function tkSurOn() { return window.TK_SUR_OK !== false; }

// Lightweight loading placeholder — shimmer lines, no images, no network.
// Shown instantly on first open so the user sees content, not a blank sheet,
// while the /cart.js fetch is in flight.
function tkCartSkeleton() {
  var row = '<div class="cart-skeleton-row">' +
    '<div class="cart-skel-img skel-pulse"></div>' +
    '<div class="cart-skel-lines">' +
    '<div class="cart-skel-line skel-pulse" style="width:65%"></div>' +
    '<div class="cart-skel-line skel-pulse" style="width:40%;margin-top:6px"></div>' +
    '</div></div>';
  return row + row + row; // 3 placeholder rows
}

function openCartDrawer() {
  // The basket the theme last read (the idle pre-warm, an add, a stepper) is drawn before the
  // sheet moves, so the dishes come up with it instead of after a /cart.js round trip behind a
  // skeleton; the fetch below then only confirms it. No known basket (or one older than ten
  // minutes): the skeleton, as before.
  const list = document.getElementById('cart-items-list');
  const footer = document.getElementById('cart-footer');
  tkCartAnimate = true;
  tkDrawerOpenedAt = performance.now();   // before the render: its suggestions wait for the slide
  if (tkLastCart && Date.now() - tkLastCartAt < 600000 && list && !tkCartBusy()) {
    renderDrawerCart(tkLastCart);
  } else if (list && !list.children.length) {
    list.innerHTML = tkCartSkeleton();
    if (footer) footer.style.display = 'none';
  }
  const drawer = document.getElementById('cart-drawer');
  document.getElementById('cart-overlay').classList.add('open');
  drawer.classList.add('open');
  // The page-wide changes (the body class that hides the sticky bars and chat button, and the
  // scroll lock) restyle and re-lay out the whole page: a frame later, once the sheet's slide
  // is running on the compositor, so they never hold up its first frame.
  requestAnimationFrame(function () {
    if (!drawer.classList.contains('open')) return;
    document.body.classList.add('tk-cart-open');
    document.body.style.overflow = 'hidden';
  });
  setTimeout(tkPlayCelebrate, 600); // pending free-delivery shimmer, after slide-in
  setTimeout(tkTryRevealGift, 750); // pending gift reveal, once rows have settled
  tkFetchRecsPool(); // warm up pool — already non-blocking (no-op if cached)

  // Start the data fetch AFTER handing control back to the browser so the
  // opening CSS transition gets at least one uncontested frame. Double rAF:
  // first rAF = browser processes the .open class + starts the CSS transition;
  // second rAF = transition is running, now safe to kick off the fetch without
  // fighting the animation frame.
  requestAnimationFrame(function () {
    requestAnimationFrame(function () {
      fetchAndRenderCart();
    });
  });
}
var tkDrawerOpenedAt = 0;

function closeCartDrawer() {
  var drawer = document.getElementById('cart-drawer');
  var overlay = document.getElementById('cart-overlay');
  document.body.classList.remove('tk-cart-open');
  document.body.style.overflow = '';
  if (overlay) overlay.classList.remove('open');
  if (drawer) {
    drawer.classList.remove('open');
    drawer.classList.remove('tk-dragging');
    drawer.style.transform = '';
    drawer.style.transition = '';
  }
}

// Navigate from inside the cart drawer: close with animation, THEN route.
// Prevents the race where position:fixed drawer layers remain visible over
// the PDP sticky header during Android Chrome's native page transition.
function tkCartNavTo(url, event) {
  var drawer = document.getElementById('cart-drawer');
  if (!drawer || !drawer.classList.contains('open')) return;
  if (event) { event.preventDefault(); event.stopPropagation(); }
  closeCartDrawer();
  var done = false;
  function navigate() { if (done) return; done = true; window.location.href = url; }
  drawer.addEventListener('transitionend', navigate, { once: true });
  setTimeout(navigate, 420); // hard cap — slightly over the longest CSS transition
}

// ── Swipe-down-to-dismiss (mobile bottom sheet) ───────────────────────────────
// Drag originates from the pill handle or header. Coordinates with body scroll:
// the sheet only drags when its scrollable body is already at the top and the
// finger is moving down — otherwise the touch is left to native scrolling.
(function () {
  var drawer = null;          // resolved lazily (DOM ready by interaction time)
  var sheetBody = null;
  var startY = 0;
  var startScrollTop = 0;
  var lastY = 0;
  var lastT = 0;
  var startT = 0;
  var dragging = null;        // null = undecided, true = dragging, false = defer to scroll
  var DAMP = 0.85;            // elastic resistance — sub-1:1 tracking

  function reduced() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
  function isMobileSheet() {
    return window.matchMedia('(max-width: 900px)').matches;
  }

  function onStart(e) {
    if (!isMobileSheet()) return;
    drawer = drawer || document.getElementById('cart-drawer');
    if (!drawer || !drawer.classList.contains('open')) return;
    sheetBody = sheetBody || drawer.querySelector('.cart-sheet-body');
    var t = e.touches ? e.touches[0] : e;
    startY = lastY = t.clientY;
    startT = lastT = (e.timeStamp || Date.now());
    startScrollTop = sheetBody ? sheetBody.scrollTop : 0;
    dragging = null; // direction not yet decided
  }

  function onMove(e) {
    if (!drawer || !drawer.classList.contains('open')) return;
    var t = e.touches ? e.touches[0] : e;
    var dy = t.clientY - startY;
    lastY = t.clientY;
    lastT = (e.timeStamp || Date.now());

    // First meaningful move decides the gesture, then we stick with it.
    if (dragging === null) {
      if (Math.abs(dy) < 4) return; // ignore jitter until intent is clear
      // Only hijack as a drag-to-dismiss when the body is at the very top
      // and the finger is travelling downward. Anything else is scroll.
      if (startScrollTop <= 0 && dy > 0) {
        dragging = true;
        drawer.classList.add('tk-dragging');
        drawer.style.transition = 'none';
      } else {
        dragging = false; // let the body scroll naturally
        return;
      }
    }
    if (!dragging) return;

    // Active drag: follow the finger with elastic resistance, no upward travel.
    var offset = Math.max(0, dy * DAMP);
    if (e.cancelable) e.preventDefault(); // stop scroll/rubber-band fighting us
    drawer.style.transform = 'translateY(' + offset + 'px)';
  }

  function onEnd() {
    if (!drawer) return;
    if (!dragging) { dragging = null; return; }
    dragging = null;
    drawer.classList.remove('tk-dragging');

    var dy = lastY - startY;
    var elapsed = Math.max(1, lastT - startT);
    var velocity = dy / elapsed; // px per ms (positive = downward)

    var shouldDismiss = velocity > 0.4 || dy > 120;

    if (shouldDismiss) {
      if (reduced()) {
        // Skip the animation but still honor the swipe-release.
        drawer.style.transition = '';
        drawer.style.transform = '';
        closeCartDrawer();
      } else {
        tkDismissWithVelocity(Math.max(velocity, 0.4));
      }
    } else {
      // Snap back to resting position.
      if (reduced()) {
        drawer.style.transition = '';
        drawer.style.transform = '';
      } else {
        drawer.style.transition = 'transform .32s cubic-bezier(.22,.61,.36,1)';
        drawer.style.transform = 'translateY(0)';
        var d = drawer;
        setTimeout(function () { d.style.transition = ''; d.style.transform = ''; }, 340);
      }
    }
  }

  function bind() {
    var pill = document.querySelector('.cart-sheet-pill');
    var hd = document.querySelector('.cart-sheet-hd');
    [pill, hd].forEach(function (el) {
      if (!el) return;
      el.addEventListener('touchstart', onStart, { passive: true });
      // non-passive so preventDefault works once a drag is committed
      el.addEventListener('touchmove', onMove, { passive: false });
      el.addEventListener('touchend', onEnd, { passive: true });
      el.addEventListener('touchcancel', onEnd, { passive: true });
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bind);
  else bind();
})();

// Animate the sheet out at a velocity-scaled speed, then formally close.
function tkDismissWithVelocity(velocityY) {
  var drawer = document.getElementById('cart-drawer');
  if (!drawer) { closeCartDrawer(); return; }
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    drawer.style.transition = '';
    drawer.style.transform = '';
    closeCartDrawer();
    return;
  }
  var duration = Math.max(180, Math.min(320, 200 / velocityY)); // faster flick = faster close
  drawer.style.transition = 'transform ' + duration + 'ms cubic-bezier(.32,.72,0,1)';
  drawer.style.transform = 'translateY(110%)';
  setTimeout(function () {
    // Drop the .open class first so the inline-style reset can't snap the
    // sheet back to translateY(0) for a frame before it's hidden.
    closeCartDrawer();
    drawer.style.transition = '';
    drawer.style.transform = '';
  }, duration);
}

function fetchAndRenderCart(attempt) {
  attempt = attempt || 0;
  const version = ++_tkFetchVersion, epoch = _tkWriteEpoch;
  fetch('/cart.js')
    .then(function(r) { if (!r.ok) throw new Error('cart ' + r.status); return r.json(); })
    .then(function(cart) {
      // Abort if a newer fetch started, or a mutation is in-flight / pending.
      // Mutations manage their own authoritative reconcile via renderDrawerCart.
      if (version !== _tkFetchVersion) return;
      if (epoch !== _tkWriteEpoch) { tkReconcileSoon(); return; } // a write started since: stale
      tkSetLines(cart);
      // A change is still on its way: this answer may already be out of date, so redraw once the queue is
      // done instead. (Dropping it left nothing to draw the final state when this read had already
      // superseded the reconcile: a gift row taken out stayed in the page, cache/cart-torture.js.)
      if (tkCartBusy()) { tkReconcileSoon(); return; }
      renderDrawerCart(cart);
    })
    .catch(function() {
      // A failed /cart.js (Shopify answers 503 under load) used to leave the
      // skeleton up forever, so an added item looked like it never arrived.
      // Retry twice, then say so instead of shimmering indefinitely.
      if (version !== _tkFetchVersion) return;
      if (attempt < 2) { setTimeout(function() { fetchAndRenderCart(attempt + 1); }, 600 * (attempt + 1)); return; }
      var list = document.getElementById('cart-items-list');
      if (list && list.querySelector('.cart-skeleton-row')) {
        list.innerHTML = '<div class="cart-empty"><p class="cart-empty-text">We couldn\'t load your basket just now.</p>' +
          '<button type="button" class="btn btn-primary" onclick="fetchAndRenderCart()">Try again</button></div>';
      }
    });
}

// Coalesced authoritative reconcile. Optimistic UI already updates instantly per
// action; this collapses a burst of server round-trips into a SINGLE settled
// render once the mutation queue goes idle — so adding several items in a row no
// longer re-renders (and re-animates) the whole cart 2-3 times.
var _tkReconcileTimer = null;
var _tkReconcileFails = 0;
function tkReconcileSoon() {
  if (_tkReconcileTimer) return;                       // one reconcile already pending
  _tkReconcileTimer = setTimeout(function run() {
    if (tkCartBusy()) {                                // queue still busy — wait it out
      _tkReconcileTimer = setTimeout(run, 90);
      return;
    }
    _tkReconcileTimer = null;
    _tkFetchVersion++;
    var v = _tkFetchVersion, epoch = _tkWriteEpoch;
    fetch('/cart.js').then(tkJsonOk).then(function (raw) {
      if (v !== _tkFetchVersion) return;               // superseded by a newer fetch/reconcile
      if (epoch !== _tkWriteEpoch) { tkReconcileSoon(); return; } // a write started since: read again
      if (tkCartBusy()) { tkReconcileSoon(); return; } // new burst — retry
      _tkReconcileFails = 0;
      renderDrawerCart(raw);
    }).catch(function () {
      // the store did not answer (429/5xx under load): try again a little later, three times
      if (++_tkReconcileFails <= 3) setTimeout(tkReconcileSoon, 700 * _tkReconcileFails);
      else _tkReconcileFails = 0;
    });
  }, 150);
}

var _tkSheetTop = 0;
function renderDrawerCart(rawCart) {
  const cart = tkNormalizeCart(rawCart);
  tkSetLines(cart);

  const list = document.getElementById('cart-items-list');
  const footer = document.getElementById('cart-footer');
  const countEl = document.getElementById('nav-cart-count');
  const mobileCount = document.getElementById('mobile-cart-count');
  const sheetCount = document.getElementById('cart-sheet-count');

  // Guard: don't flash empty state while optimistic rows still exist in DOM.
  // Use the DOM (not tkCartHandleSet) as ground truth — the handle set can
  // lag behind during remove animations. Exclude rows that are already
  // collapsing (.cart-item-removing) so removing the last item works correctly.
  const activeRows = list
    ? Array.from(list.querySelectorAll('.cart-item')).filter(function (el) {
        return !el.classList.contains('cart-item-removing');
      })
    : [];
  if (cart.items.length === 0 && activeRows.length > 0) return;

  const safeCount = tkSafeInt(cart.item_count, 0);
  if (countEl) countEl.textContent = safeCount;
  if (mobileCount) mobileCount.textContent = '(' + safeCount + ')';

  if (cart.items.length === 0) {
    list.innerHTML = '<div class="cart-empty">' +
      '<div class="cart-empty-art"><svg viewBox="0 0 64 48" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" filter="url(#hd-ink)"><ellipse cx="32" cy="30" rx="20" ry="7"/><ellipse cx="32" cy="28" rx="12" ry="4"/><path d="M16 6v8M13 6v5M19 6v5M16 14v22"/><path d="M48 6c3.5 0 3.5 8 0 8s-3.5-8 0-8M48 14v22"/></svg></div>' +
      '<div class="cart-empty-title">Your table is waiting</div>' +
      '<span class="cart-empty-note">nothing on the plate yet</span>' +
      '<p class="cart-empty-text">Nothing here yet. Pick a comforting, home-style meal and we\'ll get it warming.</p>' +
      '<a href="/collections/all" onclick="tkCartNavTo(this.href,event)" class="btn btn-primary">Explore meals →</a>' +
      '</div>';
    if (footer) footer.style.display = 'none';
    if (sheetCount) sheetCount.textContent = '';
    tkCartSubtotal = 0;
    tkCartHandleSet = new Set();
    renderCartProgress(0);
    _tkScheduleRecs([]);
    return;
  }

  if (footer) footer.style.display = 'block';
  if (sheetCount) sheetCount.textContent = safeCount > 0 ? '· ' + safeCount : '';

  // Update global state from server-authoritative response
  tkCartSubtotal = tkSafeNum(tkComputeFoodSubtotal(cart), 0);
  tkCartHandleSet = new Set(cart.items.map(i => i.handle));

  // Keep the sheet where the shopper left it. The position comes from its scroll events: reading
  // scrollTop here, and writing it back when it was 0, forced two whole layouts in every render.
  const sheetBody = document.querySelector('.cart-sheet-body');
  if (sheetBody && !sheetBody._tkScroll) { sheetBody._tkScroll = true; sheetBody.addEventListener('scroll', function () { _tkSheetTop = sheetBody.scrollTop; }, { passive: true }); }
  const prevScroll = _tkSheetTop;
  const useEnterAnim = tkCartAnimate;
  if (tkCartAnimate) tkCartAnimate = false;
  tkPatchCartItems(list, cart.items, useEnterAnim);
  // the locked next gift sits after the rows; a skeleton or an empty state in between wiped it
  var _food = tkComputeFoodSubtotal(cart);
  tkPaintGhostGift(_food);
  if (sheetBody && prevScroll > 0) sheetBody.scrollTop = prevScroll;
  if (tkRecsPool) { tkPatchCartPrices(); tkRenderSavingsSeal(); }
  else tkFetchRecsPool().then(function () { tkPatchCartPrices(); tkRenderSavingsSeal(); var gh = list.querySelector('.cart-ghost'); if (gh) gh.remove(); tkPaintGhostGift(_food); }).catch(function () {});

  const _subEl = document.getElementById('cart-subtotal');
  if (_subEl) _subEl.textContent = '₹' + tkCartSubtotal.toFixed(0);
  renderCartProgress(tkCartSubtotal);
  // a gift row hidden on its way out comes back if the store still has it (it was hidden on a snapshot that
  // later taps overtook); the sync below hides it again if it really is going
  if (!window.tkGiftSyncing) {
    var _keys = {}; cart.items.forEach(function (i) { _keys[i.key] = 1; });
    list.querySelectorAll('.cart-item--gift[hidden]').forEach(function (r) { if (_keys[r.dataset.key]) { r.hidden = false; r.classList.remove('cart-item-removing'); } });
  }
  tkSyncFreeGift(cart); // server-authoritative: the free Pav Bhaji (₹1,200) and the surprise (₹2,000)
  tkTryRevealGift();    // one-shot: the gift row exists by now (optimistic insert above)
  _tkScheduleRecs(cart.items);
}

// FLIP: existing rows glide into place; new rows fade+rise in
function tkFlipCartItems(list, oldTops) {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const rows = Array.prototype.slice.call(list.querySelectorAll('.cart-item'));
  const plan = rows.map(el => ({
    el: el,
    key: el.getAttribute('data-key'),
    newTop: el.getBoundingClientRect().top
  }));
  let hasNew = false;
  plan.forEach(p => {
    const old = oldTops[p.key];
    if (old != null) {
      const dy = old - p.newTop;
      if (dy) { p.el.style.transition = 'none'; p.el.style.transform = 'translateY(' + dy + 'px)'; }
    } else {
      hasNew = true;
      p.el.style.transition = 'none';
      p.el.style.opacity = '0';
      p.el.style.transform = 'translateY(10px) scale(.985)';
    }
  });
  if (!hasNew && plan.every(p => oldTops[p.key] == null || oldTops[p.key] === p.newTop)) {
    plan.forEach(p => { p.el.style.transition = ''; p.el.style.transform = ''; });
    return;
  }
  requestAnimationFrame(() => {
    plan.forEach(p => {
      p.el.style.transition = 'transform .24s cubic-bezier(.22,.61,.36,1), opacity .22s ease';
      p.el.style.transform = '';
      p.el.style.opacity = '';
    });
    setTimeout(() => { plan.forEach(p => { p.el.style.transition = ''; }); }, 300);
  });
}

// ── Cart row HTML builder (single item) ────────────────────────────────────
function tkCartRowHTML(item) {
  if (tkIsGiftItem(item)) return tkGiftRowHTML(item);
  const imgSrc = item.image ? item.image.replace(/(\.(png|jpe?g|gif|webp))(\?.*)?$/i, '_120x120$1$3') : '';
  const name = tkCleanTitle(item.product_title);
  const unit = Math.round(item.price / 100);
  const qty = item.quantity;
  const lineTotal = unit * qty;
  const mrp = tkGetCompareAt(item.variant_id, item.handle);
  // Prices are line-level (unit × qty) so they reconcile with the subtotal; a
  // "₹X each" subtitle disambiguates when qty > 1.
  const mrpHTML  = mrp > unit ? `<span class="cart-item-mrp">₹${mrp * qty}</span>` : '';
  const eachHTML = qty > 1 ? `<span class="cart-item-each">₹${unit} each</span>` : '';
  const weightLabel = tkGetWeightLabel(item);
  const down = item.quantity <= 1
    ? `drawerRemoveItem('${item.key}')`
    : `drawerChangeQty('${item.key}', ${item.quantity - 1})`;
  return `<div class="cart-item" data-key="${item.key}" data-variant="${item.variant_id}" data-qty="${item.quantity}" data-price="${item.price}" data-handle="${item.handle}" data-name="${tkEsc(name)}">
  <div class="cart-item-img"><a href="${item.url}" onclick="tkCartNavTo(this.href,event)" tabindex="-1">${imgSrc ? `<img src="${imgSrc}" alt="${tkEsc(name)}" loading="lazy" width="88" height="88">` : '<div class="cart-img-ph"></div>'}</a></div>
  <div class="cart-item-info">
    <div class="cart-item-top">
      <div class="cart-item-title"><a href="${item.url}" onclick="tkCartNavTo(this.href,event)" class="cart-item-link">${tkEsc(name)}</a></div>
      <div class="cart-item-prices">${mrpHTML}<span class="cart-item-price">₹${lineTotal}</span>${eachHTML}</div>
    </div>
    ${weightLabel ? `<div class="cart-item-variant">${weightLabel}</div>` : ''}
    <div class="cart-item-foot">
      <div class="cart-item-qty">
        <button class="qty-btn" onclick="${down}" aria-label="Remove one">−</button>
        <span class="qty-num">${item.quantity}</span>
        <button class="qty-btn" onclick="drawerChangeQty('${item.key}', ${item.quantity + 1})" aria-label="Add one">+</button>
      </div>
      <button class="cart-item-remove" onclick="drawerRemoveItem('${item.key}')" aria-label="Remove item">×</button>
    </div>
  </div>
</div>`;
}

// ── Patch a single row in-place (qty changed, no DOM teardown) ────────────────
function tkPatchCartRow(el, item) {
  const prevQty = parseInt(el.dataset.qty || '1', 10);
  if (prevQty === item.quantity) return;
  el.dataset.qty = item.quantity;
  tkRenderRowPrice(el); // sync line total / "each" on server-reconciled qty
  const qtyNum = el.querySelector('.qty-num');
  if (qtyNum) qtyNum.textContent = item.quantity;
  const btns = el.querySelectorAll('.qty-btn');
  if (btns[0]) btns[0].setAttribute('onclick', item.quantity <= 1
    ? `drawerRemoveItem('${item.key}')`
    : `drawerChangeQty('${item.key}', ${item.quantity - 1})`);
  if (btns[1]) btns[1].setAttribute('onclick', `drawerChangeQty('${item.key}', ${item.quantity + 1})`);
}

// ── Key-diff cart patcher (C1+C2 fix) ────────────────────────────────────────
// Phase 1 (read):  snapshot all old row positions — one reflow, zero writes
// Phase 2 (write): remove stale rows, patch existing rows, insert new rows
// Phase 3 (read):  snapshot new positions — one reflow, zero writes
// Phase 4 (write): apply FLIP offsets, rAF to final positions
function tkPatchCartItems(list, items, useEnterAnim) {
  // The rows already say exactly this (the usual answer to the open's confirming fetch): leave
  // them be, without the two layout reads the glide below needs.
  const shown = list.querySelectorAll('.cart-item');
  if (shown.length === items.length && !list.querySelector('.cart-skeleton-row, .cart-empty')
      && items.every(function (it, i) { const el = shown[i]; return el.dataset.key === it.key && +el.dataset.qty === it.quantity && !el.classList.contains('cart-item-removing'); })) return;
  const serverKeys = new Set(items.map(function(i) { return i.key; }));
  // a dish whose line came back under a new key (Shopify re-keys lines when a discount lands) is the same
  // row: give it the new key rather than dropping it and fading a copy in
  items.forEach(function (it) {
    if (tkIsGiftItem(it) || list.querySelector('.cart-item[data-key="' + (window.CSS && CSS.escape ? CSS.escape(it.key) : it.key) + '"]')) return;
    const vid = String(it.variant_id) + ':';
    const old = [...list.querySelectorAll('.cart-item:not(.cart-item--gift):not(.cart-item-removing)')].filter(function (el) {
      return !serverKeys.has(el.dataset.key) && String(el.dataset.key || '').indexOf(vid) === 0;
    })[0];
    if (old) tkRekey(old.dataset.key, it.key);
  });
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Phase 1: read all current positions (one batched reflow)
  const oldTops = new Map();
  if (!useEnterAnim && !reducedMotion) {
    list.querySelectorAll('.cart-item:not(.cart-item-removing)').forEach(function(el) {
      oldTops.set(el.dataset.key, el.getBoundingClientRect().top);
    });
  }

  // Phase 2a: remove skeleton placeholders, a stale empty-state block, and
  // rows absent from server response
  list.querySelectorAll('.cart-skeleton-row, .cart-empty').forEach(function(el) { el.remove(); });
  list.querySelectorAll('.cart-item').forEach(function(el) {
    if (!serverKeys.has(el.dataset.key)) el.remove();
  });

  // Phase 2b: index remaining rows by key
  const domRows = new Map();
  list.querySelectorAll('.cart-item').forEach(function(el) {
    domRows.set(el.dataset.key, el);
  });

  // Phase 2c: patch existing rows + insert new rows in server order
  items.forEach(function(item, idx) {
    const existing = domRows.get(item.key);
    if (existing) {
      tkPatchCartRow(existing, item);
      const sibling = list.children[idx];
      if (sibling && sibling !== existing) list.insertBefore(existing, sibling);
    } else {
      const tmp = document.createElement('div');
      tmp.innerHTML = tkCartRowHTML(item);
      const newEl = tmp.firstElementChild;
      if (useEnterAnim) {
        newEl.style.animationDelay = (idx * 45) + 'ms';
        newEl.classList.add('cart-item-enter');
      } else if (!reducedMotion) {
        newEl.style.opacity = '0';
        newEl.style.transform = 'translateY(10px) scale(.985)';
      }
      const sibling = list.children[idx];
      if (sibling) list.insertBefore(newEl, sibling);
      else list.appendChild(newEl);
    }
  });

  if (useEnterAnim || reducedMotion) return;

  // Phase 3: read new positions (one batched reflow)
  const plan = [];
  list.querySelectorAll('.cart-item:not(.cart-item-removing)').forEach(function(el) {
    plan.push({ el: el, key: el.dataset.key, newTop: el.getBoundingClientRect().top });
  });

  // Phase 4a: apply displacement transforms (batch write, no reflow)
  plan.forEach(function(p) {
    const oldTop = oldTops.get(p.key);
    if (oldTop != null) {
      const dy = oldTop - p.newTop;
      if (dy) { p.el.style.transition = 'none'; p.el.style.transform = 'translateY(' + dy + 'px)'; }
    }
  });

  // Skip rAF if nothing moved and no new rows
  const hasAnimation = plan.some(function(p) {
    const old = oldTops.get(p.key);
    return old == null || old !== p.newTop;
  });
  if (!hasAnimation) {
    plan.forEach(function(p) { p.el.style.transition = ''; p.el.style.transform = ''; });
    return;
  }

  // Phase 4b: rAF — animate to final positions, fade in new rows
  requestAnimationFrame(function() {
    plan.forEach(function(p) {
      p.el.style.transition = 'transform .24s cubic-bezier(.22,.61,.36,1), opacity .22s ease';
      p.el.style.transform = '';
      if (!oldTops.has(p.key)) p.el.style.opacity = '';
    });
    setTimeout(function() {
      plan.forEach(function(p) { p.el.style.transition = ''; });
    }, 300);
  });
}

// Unlock moment on the rewards bar (frac 0–1 along the track): the milestone pip
// presses in and throws a brass ring, a gleam runs along the fill, the message
// rises in, and foil flakes (brass, marigold, cream; sindoor too for the gift)
// lift off the pip and flutter down. Each flake's flight is sampled from a small
// physics model (launch, air drag, gravity, a paper-like sway), so it follows a
// true arc instead of straight legs; an inner layer flips it in 3D so the foil
// catches the light. Transform and opacity only, built off-DOM and appended once,
// each piece removes itself. Off in lite mode and with reduced motion.
function tkCartConfetti(frac) {
  var host = document.getElementById('cart-confetti');
  var track = document.getElementById('cart-reward-track');
  if (!host || !track || !host.animate) return;                      // no WAAPI → skip
  var still = window.tkMotion ? !window.tkMotion()
    : (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  if (still) return;
  var f = (typeof frac === 'number') ? frac : 0.7;
  var gift = f >= 0.999;
  var fill = document.getElementById('cart-reward-fill');
  var pip = document.getElementById(gift ? 'cart-pip-gift' : 'cart-pip-ship');
  var msg = document.getElementById('cart-progress-msg');
  // Read everything first, write afterwards.
  var hostRect = host.getBoundingClientRect();
  var trackRect = track.getBoundingClientRect();
  if (!trackRect.width) return;                                       // not laid out yet
  var fillW = fill ? fill.getBoundingClientRect().width : 0;
  var ox = (trackRect.left - hostRect.left) + trackRect.width * f;
  var oy = (trackRect.top - hostRect.top) + trackRect.height / 2;
  var EASE = 'cubic-bezier(.2,.7,.2,1)';                             // --hp-ease

  if (pip) pip.animate([
    { transform: 'translate(-50%,-50%) scale(1)' },
    { transform: 'translate(-50%,-50%) scale(.84)', offset: 0.18 },
    { transform: 'translate(-50%,-50%) scale(1.18)', offset: 0.5 },
    { transform: 'translate(-50%,-50%) scale(1)' }
  ], { duration: 620, easing: EASE });
  if (msg) msg.animate([
    { opacity: 0, transform: 'translateY(5px)' },
    { opacity: 1, transform: 'none' }
  ], { duration: 420, easing: EASE });

  var frag = document.createDocumentFragment();
  var anims = [];
  function piece(cls, x, y) {
    var el = document.createElement('span');
    el.className = cls;
    el.style.left = x + 'px';
    el.style.top = y + 'px';
    frag.appendChild(el);
    return el;
  }
  for (var r = 0; r < 2; r++) {
    anims.push([piece('cart-foil-ring', ox, oy), [
      { transform: 'translate(-50%,-50%) scale(.7)', opacity: 0.85 },
      { transform: 'translate(-50%,-50%) scale(' + (r ? 3.4 : 2.4) + ')', opacity: 0 }
    ], { duration: r ? 1000 : 760, delay: r ? 140 : 60, easing: EASE }]);
  }
  var gleam = null;
  if (fill && fillW > 30) {
    gleam = document.createElement('span');
    gleam.className = 'cart-foil-gleam';
    anims.push([gleam, [
      { transform: 'translateX(-48px)', opacity: 0 },
      { transform: 'translateX(' + (fillW * 0.15) + 'px)', opacity: 1, offset: 0.2 },
      { transform: 'translateX(' + fillW + 'px)', opacity: 0.9 }
    ], { duration: 900, easing: 'cubic-bezier(.4,0,.2,1)' }]);
  }

  var kinds = ['brass', 'brass', 'marigold', 'cream', 'brass', 'marigold'];
  if (gift) kinds.push('sindoor');
  var shapes = ['rib', 'rib', 'sq', 'dot'];
  var n = gift ? 26 : 16;
  var G = 760, K = 3.6, STEPS = 18;                                  // px/s², drag 1/s
  for (var i = 0; i < n; i++) {
    // Fan upward; the gift pip sits at the drawer's right edge, so lean its fan left.
    var ang = -Math.PI / 2 - (gift ? 0.4 : 0) + (Math.random() - 0.5) * (gift ? 2.1 : 1.9);
    var v = (gift ? 360 : 300) + Math.random() * 230;
    var vx = Math.cos(ang) * v, vy = Math.sin(ang) * v;
    var T = 1.45 + Math.random() * 0.75;                              // seconds
    var sway = 5 + Math.random() * 11, swf = 1.6 + Math.random() * 1.6;
    var ph = Math.random() * 6.283, s0 = 0.75 + Math.random() * 0.5;
    var frames = [];
    for (var k = 0; k <= STEPS; k++) {
      var t = T * k / STEPS, e = 1 - Math.exp(-K * t);
      var x = vx / K * e + sway * Math.sin(swf * 6.283 * t + ph) * Math.min(1, t / 0.4);
      var y = (vy + G / K) / K * e - G / K * t;
      var p = k / STEPS;
      var sc = p < 0.08 ? 0.3 + (s0 - 0.3) * (p / 0.08) : s0;
      frames.push({
        transform: 'translate(' + x.toFixed(1) + 'px,' + y.toFixed(1) + 'px) scale(' + sc.toFixed(2) + ')',
        opacity: p < 0.7 ? 1 : Math.max(0, 1 - (p - 0.7) / 0.3)
      });
    }
    var wrap = piece('cart-foil', ox, oy);
    var bit = document.createElement('span');
    bit.className = 'cart-foil-bit cart-foil--' + kinds[i % kinds.length] + ' cart-foil--' + shapes[i % shapes.length];
    wrap.appendChild(bit);
    var delay = Math.round(i * (gift ? 9 : 12) + Math.random() * 30);
    anims.push([wrap, frames, { duration: T * 1000, delay: delay, easing: 'linear', fill: 'backwards' }]);
    var ax = (Math.random() * 2 - 1).toFixed(2), ay = (Math.random() * 2 - 1).toFixed(2);
    var z0 = Math.round(Math.random() * 360);
    var spin = Math.round((540 + Math.random() * 720) * (Math.random() < 0.5 ? -1 : 1));
    anims.push([bit, [
      { transform: 'rotate(' + z0 + 'deg) rotate3d(' + ax + ',' + ay + ',0.4,0deg)' },
      { transform: 'rotate(' + z0 + 'deg) rotate3d(' + ax + ',' + ay + ',0.4,' + spin + 'deg)' }
    ], { duration: T * 1000, delay: delay, easing: 'cubic-bezier(.3,.6,.5,1)' }]);
  }

  host.appendChild(frag);
  if (gleam) fill.appendChild(gleam);
  anims.forEach(function (a) {
    var el = a[0];
    var anim = el.animate(a[1], a[2]);
    if (el.parentNode === host || el === gleam) anim.onfinish = function () { el.remove(); };
  });
}

// Celebrate a reward crossing: burst now if the drawer is open, else remember the
// spot and fire on the next open (a shopper who crosses via the PDP/recs still
// gets the moment when they look at the cart).
function tkCelebrateAt(frac) {
  var drawer = document.getElementById('cart-drawer');
  // Open drawer: let the fill (.55s) reach the pip first.
  if (drawer && drawer.classList.contains('open')) setTimeout(function () { tkCartConfetti(frac); }, 480);
  else window._tkCelebratePending = frac;
}

// Called on drawer open: fire any confetti that was deferred while it was shut.
function tkPlayCelebrate() {
  var pending = window._tkCelebratePending;
  if (pending === undefined || pending === false) return;
  var drawer = document.getElementById('cart-drawer');
  if (!drawer || !drawer.classList.contains('open')) return;         // wait for next open
  window._tkCelebratePending = false;
  tkCartConfetti(typeof pending === 'number' ? pending : 0.7);
}

// While the festival notice runs (the top bar's tkFest), the ₹1,200 reward is worded as the
// festival's gift ("our Diwali gift: a free Pav Bhaji"): same reward, same threshold.
function tkFestName() { var f = typeof window.tkFest === 'function' ? window.tkFest() : null; return f ? f.name : ''; }
// Diwali stock-up (only while the festival notice runs): ₹300 off a basket of ₹3,500 and more, on top
// of free delivery and the gift. The theme only shows it; a Shopify automatic discount must give it
// ("Amount off order", ₹300, minimum ₹3,500, combines with the gift's product discount), created in
// the same minute this goes live, or the basket promises a saving checkout does not make.
var TK_BULK_THRESHOLD = 3500;
var TK_BULK_OFF = 300;
// The offer ends with the Shopify discount (12 Nov 2026, 23:59 India time): keep the two in step.
var TK_BULK_ENDS = Date.parse('2026-11-12T23:59:59+05:30');
// The offer runs on its Shopify discount's own dates, not the festival notice's: the notice ends the
// morning the kitchen reopens (12 Nov), the discount that night.
var TK_BULK_STARTS = Date.parse('2026-10-31T00:00:00+05:30');
function tkBulkName() { var n = Date.now(); return n >= TK_BULK_STARTS && n < TK_BULK_ENDS ? (tkFestName() || 'Diwali') : ''; }
window.tkBulkName = tkBulkName;
function tkBulkOn() { return !!tkBulkName(); }
function tkBulkSaving(sub) { return tkBulkOn() && sub >= TK_BULK_THRESHOLD ? TK_BULK_OFF : 0; }
// "ends in 12 days" / "ends tomorrow night" / "ends tonight", counted in India time
function tkBulkWhen(short) {
  var IST = 5.5 * 36e5, day = function (t) { return Math.floor((t + IST) / 864e5); };
  var d = day(TK_BULK_ENDS) - day(Date.now());
  if (short) return d <= 0 ? 'ends tonight' : d === 1 ? 'ends tomorrow' : d + ' days left';
  return d <= 0 ? 'ends tonight' : d === 1 ? 'ends tomorrow night' : 'ends in ' + d + ' days';
}
window.tkBulkWhen = tkBulkWhen;
// The limited-time pill over the basket's reward line (drawer #cart-offer, /cart #hb-offer)
function tkBulkPill(el, sub, short) {
  if (!el) return;
  var on = tkBulkOn();
  el.hidden = !on;
  if (!on) return;
  var t = el.querySelector('span') || el;
  // short (the drawer, where it shares a line with the next step): "₹300 off at ₹3,500 · 12 days left"
  if (short) t.textContent = (sub >= TK_BULK_THRESHOLD ? tkBulkName() + ' offer' : '₹' + TK_BULK_OFF + ' off at ₹' + TK_BULK_THRESHOLD.toLocaleString('en-IN')) + ' · ' + tkBulkWhen(true);
  else t.textContent = sub >= TK_BULK_THRESHOLD ? '₹' + TK_BULK_OFF + ' ' + tkBulkName() + ' offer · ' + tkBulkWhen()
    : tkBulkName() + ' offer: ₹' + TK_BULK_OFF + ' off from ₹' + TK_BULK_THRESHOLD.toLocaleString('en-IN') + ' · ' + tkBulkWhen();
}
window.tkBulkPill = tkBulkPill;
window.tkBulkSaving = tkBulkSaving;
var _tkProgLast = null;
function renderCartProgress(subtotalRs) {
  const SHIP = TK_FREE_SHIP_THRESHOLD;   // ₹799 — tier 1: free delivery
  const GIFT = TK_GIFT_THRESHOLD;        // ₹1,200 — tier 2: the free gift (finish)
  const wrap    = document.getElementById('cart-progress-wrap');
  const msg     = document.getElementById('cart-progress-msg');
  const fill    = document.getElementById('cart-reward-fill');
  const track   = document.getElementById('cart-reward-track');
  const pipShip = document.getElementById('cart-pip-ship');
  const pipGift = document.getElementById('cart-pip-gift');
  const pipBulk = document.getElementById('cart-pip-bulk');
  const BULK = TK_BULK_THRESHOLD;
  if (!wrap) return;
  // Guard: any non-finite value (NaN, Infinity, undefined) collapses to 0.
  const sub = Number.isFinite(subtotalRs) ? subtotalRs : 0;
  window.tkCartSubtotal = sub;
  window.dispatchEvent(new CustomEvent('cart:subtotal'));
  // Same total as last time (the confirming fetch after an open, a stepper echo): the bar and
  // message already say it; rewriting them restyled the footer and cut short the unlock moment.
  if (sub > 0 && sub === _tkProgLast && wrap.style.display !== 'none') return;
  _tkProgLast = sub;
  const shipUnlocked = sub >= SHIP;
  const giftUnlocked = sub >= GIFT;
  // The bar shows one stretch at a time: 1, ₹0 → ₹1,200 (four steps, free delivery on the way); 2, ₹1,200 →
  // ₹2,000, the wrapped surprise; 3 (festival), the surprise → ₹3,500, the stock-up saving. Past the last
  // reward the last stretch stays full.
  const SUR = TK_SUR_THRESHOLD, surOn = tkSurOn();
  const surUnlocked = surOn && sub >= SUR;
  const S3 = surOn ? SUR : GIFT;
  const stage = !giftUnlocked ? 1 : (surOn && !surUnlocked) ? 2 : tkBulkOn() ? 3 : surOn ? 2 : 1;
  const bulkStage = stage === 3;
  const pipSur = document.getElementById('cart-pip-sur');
  const bulkOff = tkBulkSaving(sub);
  const bulkUnlocked = bulkOff > 0;

  // Footer total: no shipping fee is added — it's the food subtotal, less the Diwali saving.
  const totalVal = document.getElementById('cart-total-val');
  if (totalVal) {
    totalVal.textContent = '₹' + Math.round(sub - bulkOff).toLocaleString('en-IN');
  }
  const bulkRow = document.getElementById('cart-bulk-row');
  if (bulkRow) {
    bulkRow.hidden = !bulkUnlocked;
    const bv = document.getElementById('cart-bulk-val');
    if (bv) bv.textContent = '−₹' + TK_BULK_OFF;
  }
  tkBulkPill(document.getElementById('cart-offer'), sub, true);
  if (sub <= 0) {
    tkPaintGhostGift(0);
    wrap.style.display = 'none';
    window._tkGiftUnlocked = false;
    window._tkShipUnlockedState = false;
    return;
  }
  wrap.style.display = '';

  // One continuous bar to the ₹1,200 finish; the truck pip marks the free-delivery
  // milestone at SHIP / GIFT (about 67%), the gift pip the finish. Fill grows toward whichever the
  // shopper hasn't reached yet.
  // In the festival's second stage the bar re-bases to ₹1,200 → ₹3,500: free delivery is behind
  // the shopper (its pip steps aside), the gift pip marks the start and the ₹ pip the finish.
  // Before that, four even steps: the two every order already has (No preservatives, Keeps 12 months:
  // ticked pips at 0% and 33.3%, so the bar opens a third full), free delivery at 66.6%, the gift at 100%.
  const pct = stage === 3 ? Math.min(100, ((sub - S3) / (BULK - S3)) * 100)
    : stage === 2 ? Math.min(100, ((sub - GIFT) / (SUR - GIFT)) * 100)
    : sub < SHIP ? 33.3 + 33.3 * (sub / SHIP)
    : Math.min(100, 66.6 + 33.4 * ((sub - SHIP) / (GIFT - SHIP)));
  if (fill) fill.style.width = pct + '%';
  if (track) {
    track.setAttribute('aria-valuenow', String(Math.round(sub)));
    track.setAttribute('aria-valuemin', String(stage === 3 ? S3 : stage === 2 ? GIFT : 0));
    track.setAttribute('aria-valuemax', String(stage === 3 ? BULK : stage === 2 ? SUR : GIFT));
  }
  if (pipShip) { pipShip.classList.toggle('cart-reward-pip--unlocked', shipUnlocked); pipShip.hidden = stage !== 1; }
  if (pipGift) { pipGift.classList.toggle('cart-reward-pip--unlocked', giftUnlocked); pipGift.style.left = stage === 1 ? '100%' : '0%'; pipGift.hidden = stage === 3 && surOn; }
  if (pipSur) {
    pipSur.hidden = !(surOn && stage >= 2);
    pipSur.style.left = stage === 3 ? '0%' : '100%';
    pipSur.classList.toggle('cart-reward-pip--unlocked', surUnlocked);
    // the wrapped box gives a little shake when it is within ₹300 (motion allowed, house.css)
    pipSur.classList.toggle('is-near', stage === 2 && !surUnlocked && SUR - sub <= 300);
  }
  if (pipBulk) { pipBulk.hidden = !bulkStage; pipBulk.classList.toggle('cart-reward-pip--unlocked', bulkUnlocked); }
  wrap.classList.toggle('is-stage2', stage >= 2);
  wrap.classList.toggle('is-ship', shipUnlocked);

  // Confetti on each NEW upward crossing. First render only seeds the state
  // (prev === undefined) so an already-qualifying cart doesn't celebrate on load.
  const prevShip = window._tkShipUnlockedState;
  const prevGift = window._tkGiftUnlocked;
  if (prevShip !== undefined && shipUnlocked && !prevShip && !giftUnlocked) tkCelebrateAt(SHIP / GIFT);
  const prevBulk = window._tkBulkUnlocked;
  if (prevBulk !== undefined && bulkUnlocked && !prevBulk) tkCelebrateAt(1);
  window._tkBulkUnlocked = bulkUnlocked;
  // each pip is celebrated where it sits once the bar has moved on: at the start of the next stretch,
  // or at the finish when it is the last reward
  const prevSur = window._tkSurUnlocked;
  window._tkSurUnlocked = surUnlocked;
  if (prevSur !== undefined && surUnlocked && !prevSur) {
    tkCelebrateAt(stage === 3 ? 0 : 1);
    window._tkGiftRevealPending = 'sur';  // scroll the wrapped surprise into view
    tkTryRevealGift();
  } else if (prevGift !== undefined && giftUnlocked && !prevGift) {
    tkCelebrateAt(stage >= 2 ? 0 : 1);
    window._tkGiftRevealPending = 'gift'; // scroll the gift row into view + flash it
    tkTryRevealGift();
  }
  window._tkShipUnlockedState = shipUnlocked;
  window._tkGiftUnlocked = giftUnlocked;

  // Message always names the NEXT concrete reward. Once everything is unlocked the
  // line doubles as a control that jumps to the gift row, so reset the interactive
  // bits every render and re-arm them only in that state.
  msg.onclick = null;
  msg.onkeydown = null;
  msg.removeAttribute('role');
  msg.removeAttribute('tabindex');
  msg.removeAttribute('title');
  msg.className = 'cart-progress-msg';
  if (giftUnlocked) {
    var festN = tkFestName();
    // Short lines: the footer is pinned under the dishes, so every line here is a line of basket the
    // shopper cannot see. The pips say what is already unlocked; the line names only the next step.
    if (bulkUnlocked) msg.innerHTML = '<span class="cart-reward-done">₹' + TK_BULK_OFF + ' saving applied</span>';
    else if (bulkStage) msg.innerHTML = 'Add <b>₹' + Math.ceil(BULK - sub).toLocaleString('en-IN') + '</b> to save ₹' + TK_BULK_OFF;
    else if (stage === 2 && !surUnlocked) msg.innerHTML = SUR - sub <= 250 ? '<b>₹' + Math.ceil(SUR - sub) + '</b> from your surprise dish' : 'Add <b>₹' + Math.ceil(SUR - sub).toLocaleString('en-IN') + '</b> to unwrap a surprise';
    else if (surUnlocked) msg.innerHTML = '<span class="cart-reward-done">' + TK_GIFT_NAME + ' + a surprise unlocked</span>';
    else msg.innerHTML = '<span class="cart-reward-done">' + (festN ? 'Free delivery + our ' + festN + ' gift unlocked' : 'Free delivery + free ' + TK_GIFT_NAME + ' unlocked') + '</span>';
    msg.classList.add('cart-progress-msg--reward');
    msg.setAttribute('role', 'button');
    msg.setAttribute('tabindex', '0');
    var showK = surUnlocked ? 'sur' : 'gift';
    msg.setAttribute('title', surUnlocked ? 'See your surprise' : festN ? 'See your ' + festN + ' gift, a free ' + TK_GIFT_NAME : 'See your free ' + TK_GIFT_NAME);
    msg.onclick = function () { tkRevealGiftRow(showK); };
    msg.onkeydown = function (e) {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); tkRevealGiftRow(showK); }
    };
  } else if (shipUnlocked) {
    const remG = Math.ceil(GIFT - sub);
    msg.innerHTML = '<span class="cart-step-n">3 of 4 done</span> · ' + (remG <= 250 ? '<b>₹' + remG + '</b> from your free ' + TK_GIFT_NAME : 'Add <b>₹' + remG + '</b> for a free ' + TK_GIFT_NAME);
  } else {
    const remS = Math.ceil(SHIP - sub);
    msg.innerHTML = '<span class="cart-step-n">2 of 4 done</span> · Add <b>₹' + remS + '</b> for free delivery';
  }
  tkPaintGhostGift(sub);
  tkRenderSavingsSeal();
}

// Loss-aversion savings stamp: total ₹ saved vs MRP across cart rows. Shown
// only when there's something to celebrate.
function tkRenderSavingsSeal() {
  const el = document.getElementById('cart-savings-seal');
  if (!el) return;
  const list = document.getElementById('cart-items-list');
  if (!list || !tkRecsPool) { el.style.display = 'none'; return; }
  let saved = 0;
  list.querySelectorAll('.cart-item:not(.cart-item-removing)').forEach(function (row) {
    // The gift's whole value is saved, but it has no compare-at to derive that
    // from — the gift isn't discounted, so the mrp > unit test scored it 0 and
    // the most tangible saving in the cart never reached the stamp.
    if (row.classList.contains('cart-item--gift')) {
      saved += tkSafeInt(row.dataset.giftWorth, 0);
      return;
    }
    const qty  = tkSafeInt(row.dataset.qty, 1) || 1;
    const unit = Math.round(tkSafeInt(row.dataset.price, 0) / 100);
    const mrp  = tkGetCompareAt(row.dataset.variant, row.dataset.handle);
    if (mrp > unit) saved += (mrp - unit) * qty;
  });
  if (saved > 0) {
    el.innerHTML = "You're saving <b>₹" + saved + '</b>';
    el.style.display = '';
  } else {
    el.style.display = 'none';
  }
}

// ─── Free gift (TK_GIFT_NAME) on ₹1,200+ ────────────────────────────────────────
// The Shopify automatic Buy-X-Get-Y discount makes the gift ₹0 at checkout;
// this code keeps the cart in sync with that rule. The gift line carries a
// hidden `_free_gift` property so it's never mistaken for a paid one the
// shopper deliberately bought (that one counts toward the threshold as normal).
function tkIsGiftItem(item) {
  return !!(item && item.properties &&
    (item.properties._free_gift === 'true' || item.properties._free_gift === true || item.properties._free_gift === 'surprise'));
}
// The ₹2,000 surprise (TK_SUR_*) is a gift line of its own kind.
function tkIsSurItem(item) {
  return !!(item && item.properties && item.properties._free_gift === 'surprise');
}

// THE cart subtotal: every line EXCEPT the free-gift line, in rupees. Drives the
// displayed Subtotal and Total, both thresholds, the savings seal and the recs
// label — one number, so display and gift logic can never disagree.
//
// Correct whichever way the store reports the gift line: once Shopify's automatic
// discount has zeroed it the exclusion is a no-op, and before that it drops the
// ₹100 that would otherwise be charged on screen for an item marked FREE. Reading
// items_subtotal_price instead used to inflate the total by the gift's price and,
// worse, let the progress bar call the reward unlocked on money the gift itself
// contributed — which tkSyncFreeGift then disagreed with, yanking the row back out.
function tkComputeFoodSubtotal(cart) {
  var sum = 0;
  (cart && cart.items ? cart.items : []).forEach(function (i) {
    if (tkIsGiftItem(i)) return;
    sum += Number.isFinite(i.final_line_price)
      ? i.final_line_price
      : (tkSafeInt(i.price, 0) * tkSafeInt(i.quantity, 1));
  });
  return sum / 100;
}

// Reconcile the gift line against the ₹1,200 rule. MUST be called only with a
// SERVER-authoritative cart (from a committed mutation / full render) — never
// from the optimistic mid-edit path, or its re-render would clobber an
// uncommitted quantity and fight the user (the "stuck" bug). Guarded by
// tkGiftSyncing + a coalesced queue key so it can't double-fire.
// "No thanks" has to outlive the page: this is a multi-page storefront, so a
// window flag was forgotten the moment the shopper opened another product and
// the declined gift silently came back. sessionStorage keeps it for the visit
// and still lets a fresh session re-offer.
function tkGiftDismissed(sur) {
  var k = sur ? 'tk_sur_declined' : 'tk_gift_declined';
  if (window['_' + k]) return true;
  try { return sessionStorage.getItem(k) === '1'; } catch (e) { return false; }
}
function tkSetGiftDismissed(on, sur) {
  var k = sur ? 'tk_sur_declined' : 'tk_gift_declined';
  window['_' + k] = on;
  try {
    if (on) sessionStorage.setItem(k, '1');
    else sessionStorage.removeItem(k);
  } catch (e) {}
}

// What the gift lines should be for a cart: remove (line keys) and add (cart/add items). Two tiers, the
// Pav Bhaji at ₹1,200 and the surprise at ₹2,000, one line each; a line for a different product than the
// one on offer is stale (a cart built before a gift was switched) and goes.
function tkGiftPlan(cart, resetDismissed) {
  var items = (cart && cart.items) ? cart.items : [];
  var all = items.filter(tkIsGiftItem), food = tkComputeFoodSubtotal(cart);
  var tiers = [
    { sur: false, id: TK_GIFT_VARIANT_ID, at: TK_GIFT_THRESHOLD, on: true },
    { sur: true, id: TK_SUR_VARIANT_ID, at: TK_SUR_THRESHOLD, on: tkSurOn() }
  ];
  var plan = { remove: [], add: [], addKinds: [] };
  tiers.forEach(function (T) {
    var lines = all.filter(function (g) { return tkIsSurItem(g) === T.sur; });
    if (T.on && food >= T.at) {
      var good = lines.filter(function (g) { return g.variant_id === T.id; });
      // declined ("No thanks") goes even if it is still there: the removal itself may have been refused
      var no = tkGiftDismissed(T.sur);
      lines.forEach(function (g) { if (no || g.variant_id !== T.id || g !== good[0]) plan.remove.push(g.key); });
      if (!good.length && !no) {
        plan.add.push({ id: T.id, quantity: 1, properties: { _free_gift: T.sur ? 'surprise' : 'true' } });
        plan.addKinds.push(T.sur);
      }
    } else {
      if (resetDismissed) tkSetGiftDismissed(false, T.sur);   // re-offer the next time they cross the line
      lines.forEach(function (g) { plan.remove.push(g.key); });
    }
  });
  return plan;
}

// Reconcile the gift lines with the cart, both tiers in ONE pass: the rows change on screen at once (a gift
// that is going disappears, one that is coming appears), and one queued step reads the live cart, removes
// in one /cart/update.js and adds in one /cart/add.js, then reconciles. Before, each tier took its own
// read, write and redraw in turn, so crossing both lines quickly took up to six round trips (10 to 18 s on
// a slow connection) with a gift row left showing or missing meanwhile (cache/qty-stress.js). Call it only
// with a SERVER-authoritative cart; the queued step works from the live cart anyway, so a stale snapshot
// can only mis-paint, never mis-order.
function tkSyncFreeGift(cart) {
  var plan = tkGiftPlan(cart, true);
  if (!plan.remove.length && !plan.add.length) return;
  // the screen first
  plan.remove.forEach(function (k) {
    var row = document.querySelector('#cart-items-list .cart-item--gift[data-key="' + (window.CSS && CSS.escape ? CSS.escape(k) : k) + '"]');
    if (row) { row.classList.add('cart-item-removing'); row.hidden = true; }
  });
  plan.addKinds.forEach(function (sur) { tkInsertOptimisticGiftRow(sur); });
  if (_tkGiftStep) return;   // the step already queued reads the live cart when it runs
  _tkGiftStep = true; tkGiftOp(1);
  var post = function (url, body) {
    return fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
      .then(function (r) { if (!r.ok) throw new Error('gift ' + r.status); return r.json(); });
  };
  _tkEnqueue(function () {
    return fetch('/cart.js').then(tkJsonOk).then(function (live) {
      var p1 = tkGiftPlan(live, false);
      if (!p1.remove.length) return live;
      var u = {}; p1.remove.forEach(function (k) { u[k] = 0; });
      return post('/cart/update.js', { updates: u });
    }).then(function (afterRemove) {
      var p2 = tkGiftPlan(afterRemove, false);
      if (!p2.add.length) return null;
      return post('/cart/add.js', { items: p2.add }).then(function (res) {
        // give the drawn rows their real keys, so the next render patches them in place
        ((res && res.items) || []).forEach(function (it) {
          var opt = document.querySelector('.cart-item--gift[data-key="' + (tkIsSurItem(it) ? 'sur' : 'gift') + '-optimistic"]');
          if (opt && it.key) opt.setAttribute('data-key', it.key);
        });
        tkPulseCartCount();
      });
    }).then(function () {
      _tkGiftStep = false; tkGiftOp(-1);
      tkReconcileSoon();
    }).catch(function () {
      _tkGiftStep = false; tkGiftOp(-1);
      document.querySelectorAll('.cart-item--gift[data-key$="-optimistic"]').forEach(function (r) { r.remove(); });
      document.querySelectorAll('#cart-items-list .cart-item--gift[hidden]').forEach(function (r) { r.hidden = false; r.classList.remove('cart-item-removing'); });
      tkReconcileSoon();
    });
  }, 'giftsync');
}

// Show the gift row immediately (before the network round-trip) so crossing ₹1,200
// feels instant; the real cart re-render reconciles it in place via the key.
function tkInsertOptimisticGiftRow(sur) {
  var list = document.getElementById('cart-items-list');
  if (!list || list.querySelector(sur ? '.cart-item--sur' : '.cart-item--gift:not(.cart-item--sur)')) return;
  var img = '', handle = sur ? TK_SUR_HANDLE : TK_GIFT_HANDLE;
  if (tkRecsPool) {
    for (var i = 0; i < tkRecsPool.length; i++) {
      if (tkRecsPool[i].handle === handle) {
        var im = tkRecsPool[i].images && tkRecsPool[i].images[0];
        img = im ? (typeof im === 'string' ? im : im.src) : '';
        break;
      }
    }
  }
  var item = sur
    ? { key: 'sur-optimistic', variant_id: TK_SUR_VARIANT_ID, product_title: TK_SUR_NAME, handle: TK_SUR_HANDLE, image: img, properties: { _free_gift: 'surprise' } }
    : { key: 'gift-optimistic', variant_id: TK_GIFT_VARIANT_ID, product_title: TK_GIFT_NAME, handle: TK_GIFT_HANDLE, image: img, properties: { _free_gift: 'true' } };
  // Append (server returns the newest line last) so it never jumps top→bottom on reconcile.
  list.insertAdjacentHTML('beforeend', tkGiftRowHTML(item));
}

// Gift lines being changed: the sync's step and any "No thanks" removals, counted, so the first to finish
// does not declare the others done (one shared flag did, and a render in between could leave a row hidden).
// window.tkGiftSyncing is true while any is running: renders leave hidden gift rows hidden meanwhile.
var _tkGiftOps = 0, _tkGiftStep = false, _tkGiftRemoving = {};
function tkGiftOp(d) { _tkGiftOps = Math.max(0, _tkGiftOps + d); window.tkGiftSyncing = _tkGiftOps > 0; }
function tkGiftRemove(key) {
  if (_tkGiftRemoving[key]) return;   // already on its way out (a second op under the same queue key would replace the first)
  _tkGiftRemoving[key] = 1; tkGiftOp(1);
  // Mark the row on its way out, the way tkCommitRemove does. renderDrawerCart
  // refuses to paint the empty state while any row is still "active", and this
  // path never touched the DOM — so a gift auto-removed as the LAST line left a
  // phantom free-gift row sitting in a cart the server had already emptied.
  var giftRow = document.querySelector('.cart-item--gift[data-key="' +
    (window.CSS && CSS.escape ? CSS.escape(key) : key) + '"]');
  if (giftRow) { giftRow.classList.add('cart-item-removing'); giftRow.hidden = true; }   // gone from sight at once
  _tkEnqueue(function () {
    return fetch('/cart/change.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 }),
    })
      .then(function () { delete _tkGiftRemoving[key]; tkGiftOp(-1); tkReconcileSoon(); })
      .catch(function () { delete _tkGiftRemoving[key]; tkGiftOp(-1); tkReconcileSoon(); });
    // Keyed per line, not a shared 'giftsync': the coalescer drops pending ops
    // sharing a key, so removing several duplicate gift lines under one key
    // would silently discard all but the last and leave the extras in the cart.
  }, 'giftsync-' + key);
}

// "No thanks" on the gift row — remove it and don't re-add until they re-cross ₹1,200.
// Takes the button and reads the key off the row at click time. The row is first
// drawn optimistically under a placeholder key that is swapped for the real one
// once /cart/add.js answers, so a key baked into the markup goes stale and the
// removal silently fails against a line the server never had.
function tkDeclineGift(elOrKey) {
  var key = elOrKey;
  if (elOrKey && elOrKey.nodeType === 1) {
    var row = elOrKey.closest('.cart-item--gift');
    key = row ? row.getAttribute('data-key') : null;
  }
  if (!key || key === 'gift-optimistic' || key === 'sur-optimistic') return; // add still in flight — ignore the tap
  var sur = !!(elOrKey && elOrKey.nodeType === 1 && elOrKey.closest('.cart-item--sur'));
  var nm = sur ? TK_SUR_NAME : TK_GIFT_NAME, at = sur ? TK_SUR_THRESHOLD : TK_GIFT_THRESHOLD;
  tkOpenConfirm({
    key: key,
    title: sur ? 'Give up your surprise?' : 'Give up your free gift?',
    body: 'Remove your <strong>free ' + tkEsc(nm).replace(/ /g, '&nbsp;') + '</strong>? We won’t offer it again unless your order drops below ₹' + at.toLocaleString('en-IN') + ' and passes it a second time.',
    confirmLabel: 'Remove it',
    onConfirm: function () { tkSetGiftDismissed(true, sur); tkGiftRemove(key); }
  });
}

// The surprise stays wrapped (a drawn gift box, "A surprise dish") until the shopper taps it; unwrapping
// is remembered for the visit (sessionStorage tk-sur-open) so it is opened once.
function tkVariantPrice(id) {
  for (var i = 0; tkRecsPool && i < tkRecsPool.length; i++) {
    var vs = tkRecsPool[i].variants || [];
    for (var j = 0; j < vs.length; j++) if (String(vs[j].id) === String(id)) return Math.round(parseFloat(vs[j].price));
  }
  return 0;
}
function tkSurOpened() { try { return sessionStorage.getItem('tk-sur-open') === '1'; } catch (e) { return !!window._tkSurOpen; } }
function tkSurBoxHTML() {
  return '<span class="cart-sur-box" aria-hidden="true">' +
    '<svg class="cart-sur-base" viewBox="0 0 48 48"><rect x="9" y="20" width="30" height="22" rx="2.5" fill="#1F4468"/><rect x="21.5" y="20" width="5" height="22" fill="#F2B829"/></svg>' +
    '<svg class="cart-sur-lid" viewBox="0 0 48 48"><rect x="6.5" y="13" width="35" height="8" rx="2" fill="#1F4468"/><rect x="21.5" y="13" width="5" height="8" fill="#F2B829"/><path d="M24 13c-3.5-6-10-6.5-10-2.5 0 3 5.5 2.5 10 2.5Zm0 0c3.5-6 10-6.5 10-2.5 0 3-5.5 2.5-10 2.5Z" fill="none" stroke="#F2B829" stroke-width="2.4" stroke-linejoin="round"/></svg>' +
    '</span>';
}
// ─── Don't miss your gift (loss aversion, honest) ────────────────────────────
// The next free dish already sits at the end of the basket, locked: a greyed row with a lock, "Locked · add
// ₹230 to unlock it" (Pav Bhaji from ₹600, the surprise between ₹1,200 and ₹2,000). It is not a cart line;
// tapping it opens the gap sheet. And once per visit, a tap on Checkout within ₹250 of a free dish opens the
// same sheet first: "Your free Pav Bhaji is ₹230 away", dishes that close the gap in one tap, and "Check out
// anyway", which goes straight on. Nothing is charged or added without a tap.
function tkNextGift(sub) {
  if (!(sub > 0)) return null;
  if (sub < TK_GIFT_THRESHOLD) return tkGiftDismissed(false) ? null
    : { sur: false, at: TK_GIFT_THRESHOLD, gap: Math.ceil(TK_GIFT_THRESHOLD - sub), name: TK_GIFT_NAME, handle: TK_GIFT_HANDLE };
  if (tkSurOn() && sub < TK_SUR_THRESHOLD) return tkGiftDismissed(true) ? null
    : { sur: true, at: TK_SUR_THRESHOLD, gap: Math.ceil(TK_SUR_THRESHOLD - sub), name: 'surprise dish', handle: TK_SUR_HANDLE };
  return null;
}
function tkPoolImg(handle, size) {
  var p = (tkRecsPool || []).filter(function (x) { return x.handle === handle; })[0];
  var im = p && p.images && p.images[0];
  var raw = im ? (typeof im === 'string' ? im : im.src) : '';
  return raw ? raw.replace(/(\.(png|jpe?g|gif|webp))(\?.*)?$/i, '_' + size + 'x' + size + '$1$3') : '';
}
var TK_LOCK_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></svg>';
function tkPaintGhostGift(sub) {
  var list = document.getElementById('cart-items-list');
  if (!list) return;
  var g = tkNextGift(sub), el = list.querySelector('.cart-ghost');
  var show = g && (g.sur || sub >= 600) && list.querySelector('.cart-item');
  if (!show) { if (el) el.remove(); return; }
  var kind = g.sur ? 'sur' : 'gift';
  if (!el || el.dataset.kind !== kind) {
    if (el) el.remove();
    var img = g.sur ? '' : tkPoolImg(g.handle, 120);
    var fest = tkFestName();
    el = document.createElement('div');
    el.className = 'cart-ghost'; el.dataset.kind = kind;
    el.setAttribute('role', 'button'); el.tabIndex = 0;
    el.innerHTML = '<span class="cart-item-img cart-ghost-img">' + (g.sur ? tkSurBoxHTML() : img ? '<img src="' + tkEsc(img) + '" alt="" width="64" height="64" loading="lazy">' : '<span class="cart-img-ph"></span>') +
      '<span class="cart-ghost-lock">' + TK_LOCK_SVG + '</span></span>' +
      '<span class="cart-ghost-txt"><span class="cart-item-title">' + (g.sur ? 'A surprise dish' : tkEsc(g.name)) + ' <span class="cart-gift-badge">' + (g.sur ? 'SURPRISE' : fest ? tkEsc(fest.toUpperCase()) + ' GIFT' : 'FREE GIFT') + '</span></span>' +
      '<span class="cart-ghost-s"></span></span><span class="cart-ghost-free">FREE</span>';
    el.addEventListener('click', function () { tkGapOpen(null, 'ghost'); });
    el.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); tkGapOpen(null, 'ghost'); } });
    list.appendChild(el);
  }
  var t = el.querySelector('.cart-ghost-s'), h = 'Locked · add <b>₹' + g.gap.toLocaleString('en-IN') + '</b> to unlock it';
  if (t.innerHTML !== h) t.innerHTML = h;
}
function tkGapSeen() { try { return sessionStorage.getItem('tk-gap-seen') === '1'; } catch (e) { return !!window._tkGapSeen; } }
var tkGapPass = null;
function tkGapOpen(btn, from) {
  var sub = window.tkCartSubtotal || 0, g = tkNextGift(sub);
  if (!g || document.getElementById('tk-gap')) return false;
  if (from === 'checkout') { try { sessionStorage.setItem('tk-gap-seen', '1'); } catch (e) { window._tkGapSeen = true; } }
  var scrim = document.createElement('div');
  scrim.className = 'tk-confirm-scrim'; scrim.id = 'tk-gap-scrim';
  var m = document.createElement('div');
  m.className = 'tk-confirm tk-terms tk-gap'; m.id = 'tk-gap';
  m.setAttribute('role', 'dialog'); m.setAttribute('aria-modal', 'true'); m.setAttribute('aria-labelledby', 'tk-gap-t');
  var what = g.sur ? 'surprise dish' : 'free ' + g.name;
  m.innerHTML = '<div class="tk-confirm-card">' +
    '<button type="button" class="tk-confirm-x" data-gap-x aria-label="Close">&#215;</button>' +
    '<span class="tk-terms-kick">' + (from === 'checkout' ? 'Before you go' : 'So close') + '</span>' +
    '<h3 class="tk-confirm-title" id="tk-gap-t"></h3>' +
    '<p class="tk-confirm-body tk-gap-sub"></p>' +
    '<div class="tk-gap-list"></div>' +
    '<div class="tk-confirm-actions">' +
      '<button type="button" class="tk-confirm-btn tk-confirm-cancel" data-gap-x>' + (from === 'checkout' ? 'Check out anyway' : 'Not now') + '</button>' +
      '<button type="button" class="tk-confirm-btn tk-confirm-go tk-gap-go" hidden>' + (from === 'checkout' ? 'Continue to checkout' : 'Lovely') + '</button>' +
    '</div></div>';
  document.body.appendChild(scrim); document.body.appendChild(m);
  var title = m.querySelector('#tk-gap-t'), subEl = m.querySelector('.tk-gap-sub'), list = m.querySelector('.tk-gap-list');
  var go = m.querySelector('.tk-gap-go'), back = m.querySelector('.tk-confirm-cancel');
  var done = false;
  var paint = function () {
    var s = window.tkCartSubtotal || 0;
    if (s >= g.at) {
      if (done) return; done = true;
      title.textContent = 'Unlocked! ' + (g.sur ? 'A surprise dish is in your basket.' : 'Your free ' + g.name + ' is in.');
      subEl.textContent = g.sur ? 'Open it in your basket.' : 'It’s on us, with your order.';
      list.hidden = true; go.hidden = false; back.hidden = true;
      try { go.focus({ preventScroll: true }); } catch (e) {}
      return;
    }
    title.textContent = 'Your ' + what + ' is ₹' + Math.ceil(g.at - s).toLocaleString('en-IN') + ' away.';
    subEl.textContent = 'Add one of these and it’s yours, free.';
  };
  paint();
  var onSub = function () { paint(); };
  window.addEventListener('cart:subtotal', onSub);
  (typeof tkFetchRecsPool === 'function' ? tkFetchRecsPool() : Promise.resolve([])).then(function (pool) {
    var inCart = {};
    document.querySelectorAll('#cart-items-list .cart-item').forEach(function (r) { inCart[r.dataset.handle] = 1; });
    var gap = Math.ceil(g.at - (window.tkCartSubtotal || 0));
    var c = (pool || []).filter(function (p) { return !inCart[p.handle] && p.handle !== TK_GIFT_HANDLE && p.handle !== TK_SUR_HANDLE && tkBuyableVariant(p); })
      .map(function (p) { return { p: p, price: Math.round(parseFloat(tkBuyableVariant(p).price)) }; });
    var closes = c.filter(function (x) { return x.price >= gap; }).sort(function (a, b) { return a.price - b.price; });
    var pick = (closes.length ? closes : c.sort(function (a, b) { return b.price - a.price; })).slice(0, 3);
    list.innerHTML = pick.map(function (x) { return tkRecCardHTML(x.p); }).join('');
  }).catch(function () {});
  var close = function (thenGo) {
    window.removeEventListener('cart:subtotal', onSub);
    document.removeEventListener('keydown', onKey, true);
    m.classList.remove('open'); scrim.classList.remove('open');
    setTimeout(function () { m.remove(); scrim.remove(); }, 350);
    if (thenGo && btn) { tkGapPass = btn; btn.click(); }
  };
  var onKey = function (e) { if (e.key === 'Escape') { e.stopPropagation(); close(false); } };
  document.addEventListener('keydown', onKey, true);
  scrim.addEventListener('click', function () { close(false); });
  m.addEventListener('click', function (e) {
    if (e.target === m) return close(false);
    if (e.target.closest && e.target.closest('[data-gap-x]')) close(e.target.classList.contains('tk-confirm-cancel') && from === 'checkout');
  });
  go.addEventListener('click', function () { close(from === 'checkout'); });
  requestAnimationFrame(function () { scrim.classList.add('open'); m.classList.add('open'); });
  return true;
}

// Tapping the wrapped row opens the reveal: a sheet with a big gift box that bobs, a clue (TK_SUR_HINT), and on a
// tap the box shakes, the lid flies off, light rays turn behind it, foil bursts out and the dish rises from the box.
// Transform and opacity only; with reduced motion or lite mode the sheet opens already revealed. Closing it marks the
// surprise opened for the visit and the row in the basket shows the dish.
var TK_SUR_HINT = 'a Punjabi favourite';
function tkUnwrapSurprise(el) {
  var row = el && el.closest ? el.closest('.cart-item--sur') : null;
  if (!row || document.getElementById('tk-sur')) return;
  var still = window.tkMotion ? !window.tkMotion() : matchMedia('(prefers-reduced-motion: reduce)').matches;
  var worth = tkSafeInt(row.dataset.giftWorth, 0) || 160;
  var img = row.getAttribute('data-img') || '';
  var imgL = img ? img.replace(/(\.(png|jpe?g|gif|webp))(\?.*)?$/i, '_480x480$1$3') : '';
  var m = document.createElement('div');
  m.className = 'tk-sur' + (still ? ' is-open is-still' : '');
  m.id = 'tk-sur';
  m.setAttribute('role', 'dialog'); m.setAttribute('aria-modal', 'true'); m.setAttribute('aria-labelledby', 'tk-sur-t');
  m.innerHTML = '<div class="tk-sur-scrim" data-sur-x></div>' +
    '<div class="tk-sur-card">' +
      '<span class="tk-sur-kick">Your ₹' + TK_SUR_THRESHOLD.toLocaleString('en-IN') + ' surprise</span>' +
      '<div class="tk-sur-stage">' +
        '<span class="tk-sur-rays" aria-hidden="true"></span>' +
        (imgL ? '<img class="tk-sur-dish" src="' + tkEsc(imgL) + '" alt="' + tkEsc(TK_SUR_NAME) + '" width="240" height="240" decoding="async">' : '') +
        '<button type="button" class="tk-sur-gift" aria-label="Open your surprise">' + tkSurBoxHTML() + '</button>' +
        '<span class="tk-sur-burst" aria-hidden="true"></span>' +
      '</div>' +
      '<h3 class="tk-sur-t" id="tk-sur-t">' + (still ? tkEsc(TK_SUR_NAME) : 'Tap the box to open it') + '</h3>' +
      '<p class="tk-sur-p">' + (still ? 'Worth ₹' + worth + ' · free with your order' : 'Hint: ' + tkEsc(TK_SUR_HINT) + ', worth ₹' + worth) + '</p>' +
      '<button type="button" class="hp-atc tk-sur-done" data-sur-x>Lovely, it’s in my basket</button>' +
    '</div>';
  document.body.appendChild(m);
  var opened = false;
  var mark = function () {
    try { sessionStorage.setItem('tk-sur-open', '1'); } catch (e) { window._tkSurOpen = true; }
    var r = document.querySelector('#cart-items-list .cart-item--sur');
    if (!r) return;
    var item = { key: r.getAttribute('data-key'), variant_id: r.getAttribute('data-variant'), product_title: TK_SUR_NAME,
      handle: TK_SUR_HANDLE, image: r.getAttribute('data-img') || '', price: worth * 100, properties: { _free_gift: 'surprise' } };
    var tmp = document.createElement('div');
    tmp.innerHTML = tkGiftRowHTML(item);
    var fresh = tmp.firstChild;
    fresh.classList.add('is-revealed');
    r.replaceWith(fresh);
  };
  if (still) { opened = true; mark(); }
  var close = function () {
    if (!opened) return void (m.classList.remove('is-in'), setTimeout(function () { m.remove(); }, 300));
    m.classList.remove('is-in');
    document.removeEventListener('keydown', onKey, true);
    setTimeout(function () { m.remove(); }, 300);
  };
  var onKey = function (e) { if (e.key === 'Escape') { e.stopPropagation(); close(); } };
  document.addEventListener('keydown', onKey, true);
  m.addEventListener('click', function (e) { if (e.target.closest && e.target.closest('[data-sur-x]')) close(); });
  var gift = m.querySelector('.tk-sur-gift');
  gift.addEventListener('click', function () {
    if (opened) return;
    opened = true;
    m.classList.add('is-shake');
    try { if (navigator.vibrate) navigator.vibrate([12, 60, 12, 60, 24]); } catch (e) {}
    setTimeout(function () {
      m.classList.remove('is-shake');
      m.classList.add('is-open');
      m.querySelector('.tk-sur-t').textContent = TK_SUR_NAME;
      m.querySelector('.tk-sur-p').textContent = 'Worth ₹' + worth + ' · free with your order';
      tkSurBurst(m.querySelector('.tk-sur-burst'));
      mark();
      setTimeout(function () { var d = m.querySelector('.tk-sur-done'); if (d) try { d.focus({ preventScroll: true }); } catch (e) {} }, 500);
    }, 620);
  });
  requestAnimationFrame(function () { m.classList.add('is-in'); setTimeout(function () { try { (still ? m.querySelector('.tk-sur-done') : gift).focus({ preventScroll: true }); } catch (e) {} }, 80); });
}
// Foil flakes out of the box: each its own element, one transform + opacity animation, removed when done.
function tkSurBurst(host) {
  if (!host || !host.animate) return;
  var cols = ['#F2B829', '#B4904E', '#FBF1DD', '#C8442F', '#F2B829'];
  for (var i = 0; i < 26; i++) {
    var f = document.createElement('i');
    f.style.background = cols[i % cols.length];
    if (i % 3 === 0) f.style.borderRadius = '50%';
    host.appendChild(f);
    var a = (i / 26) * Math.PI * 2 + Math.random() * 0.4, d = 90 + Math.random() * 80;
    var x = Math.cos(a) * d, y = Math.sin(a) * d * 0.8 - 30, r = (Math.random() * 540 - 270) | 0;
    f.animate([{ transform: 'translate(0,0) rotate(0) scale(1)', opacity: 1 },
      { transform: 'translate(' + x + 'px,' + y + 'px) rotate(' + r + 'deg) scale(1)', opacity: 1, offset: .55 },
      { transform: 'translate(' + (x * 1.1) + 'px,' + (y + 70) + 'px) rotate(' + (r * 1.4) + 'deg) scale(.6)', opacity: 0 }],
      { duration: 1100 + Math.random() * 400, easing: 'cubic-bezier(.2,.7,.3,1)', fill: 'forwards' })
      .onfinish = (function (el) { return function () { el.remove(); }; })(f);
  }
}

function tkGiftRowHTML(item) {
  if (tkIsSurItem(item)) return tkSurRowHTML(item);
  var imgSrc = item.image ? item.image.replace(/(\.(png|jpe?g|gif|webp))(\?.*)?$/i, '_120x120$1$3') : '';
  var name = tkCleanTitle(item.product_title);
  var keyEsc = tkEsc(item.key);
  // Struck value comes from the cart line (falling back to the catalogue) rather
  // than a literal, so it can't go stale if the gift product is ever repriced.
  var worth = Math.round(tkSafeInt(item.original_line_price, 0) / 100)
           || Math.round(tkSafeInt(item.price, 0) / 100)
           || tkGetCompareAt(item.variant_id, item.handle)
           || 100;
  return '<div class="cart-item cart-item--gift" data-key="' + keyEsc + '" data-variant="' + item.variant_id + '" data-qty="1" data-price="0" data-gift-worth="' + worth + '" data-handle="' + tkEsc(item.handle) + '" data-name="' + tkEsc(name) + '">' +
    '<div class="cart-item-img">' + (imgSrc ? '<img src="' + imgSrc + '" alt="' + tkEsc(name) + '" loading="lazy" width="88" height="88">' : '<div class="cart-img-ph"></div>') + '</div>' +
    '<div class="cart-item-info">' +
      '<div class="cart-item-top">' +
        '<div class="cart-item-title">' + tkEsc(name) + ' <span class="cart-gift-badge">' + (tkFestName() ? tkEsc(tkFestName().toUpperCase()) + ' GIFT' : 'FREE GIFT') + '</span></div>' +
        '<div class="cart-item-prices"><span class="cart-item-mrp">₹' + worth + '</span><span class="cart-item-price cart-item-price--free">FREE</span></div>' +
      '</div>' +
      '<div class="cart-item-variant">Our thank-you for your ₹1,200+ order</div>' +
      '<div class="cart-item-foot"><button class="cart-gift-decline" onclick="tkDeclineGift(this)">No thanks</button></div>' +
    '</div>' +
  '</div>';
}

function tkSurRowHTML(item) {
  var imgSrc = item.image ? item.image.replace(/(\.(png|jpe?g|gif|webp))(\?.*)?$/i, '_120x120$1$3') : '';
  var name = tkCleanTitle(item.product_title);
  // what it sells for (never its MRP): the line's price, else the catalogue's, else the listed ₹160
  var worth = Math.round(tkSafeInt(item.original_line_price, 0) / 100) || Math.round(tkSafeInt(item.price, 0) / 100)
           || tkVariantPrice(item.variant_id) || 160;
  var attrs = ' data-key="' + tkEsc(item.key) + '" data-variant="' + item.variant_id + '" data-qty="1" data-price="0" data-gift-worth="' + worth +
    '" data-handle="' + tkEsc(item.handle) + '" data-name="' + tkEsc(name) + '" data-img="' + tkEsc(item.image || '') + '"';
  if (!tkSurOpened()) {
    // Wrapped: honest about what it is (a dish, its worth, free), quiet about which one.
    return '<div class="cart-item cart-item--gift cart-item--sur is-wrapped"' + attrs + '>' +
      '<button type="button" class="cart-sur-open" onclick="tkUnwrapSurprise(this)" aria-label="Unwrap your surprise dish">' +
        '<span class="cart-item-img">' + tkSurBoxHTML() + '</span>' +
        '<span class="cart-sur-txt"><span class="cart-item-title">A surprise dish <span class="cart-gift-badge">SURPRISE</span></span>' +
        '<span class="cart-item-variant">Worth ₹' + worth + ' · free on ₹' + TK_SUR_THRESHOLD.toLocaleString('en-IN') + '+</span>' +
        '<span class="cart-sur-cta">Tap to unwrap</span></span>' +
        '<span class="cart-item-price cart-item-price--free">FREE</span>' +
      '</button>' +
    '</div>';
  }
  return '<div class="cart-item cart-item--gift cart-item--sur"' + attrs + '>' +
    '<div class="cart-item-img">' + (imgSrc ? '<img src="' + imgSrc + '" alt="' + tkEsc(name) + '" loading="lazy" width="88" height="88">' : '<div class="cart-img-ph"></div>') + '</div>' +
    '<div class="cart-item-info">' +
      '<div class="cart-item-top">' +
        '<div class="cart-item-title">' + tkEsc(name) + ' <span class="cart-gift-badge">SURPRISE</span></div>' +
        '<div class="cart-item-prices"><span class="cart-item-mrp">₹' + worth + '</span><span class="cart-item-price cart-item-price--free">FREE</span></div>' +
      '</div>' +
      '<div class="cart-item-variant">Our surprise for your ₹2,000+ order</div>' +
      '<div class="cart-item-foot"><button class="cart-gift-decline" onclick="tkDeclineGift(this)">No thanks</button></div>' +
    '</div>' +
  '</div>';
}

// ─── Reveal the free-gift row ───────────────────────────────────────────────
// The reward line is pinned in the sticky footer while the gift line sits in the
// scrolling list, so shoppers get the claim without the proof — and session
// recordings show them tapping that text expecting it to take them there. This
// scrolls the gift row into view inside the cart body ONLY (never the page, which
// would fight the mobile bottom-sheet) and flashes it. Returns false when it
// couldn't run, so callers can stay pending.
function tkRevealGiftRow(kind) {
  var drawer = document.getElementById('cart-drawer');
  if (!drawer || !drawer.classList.contains('open')) return false;
  var body = document.querySelector('.cart-sheet-body');
  var row  = document.querySelector(kind === 'sur' ? '#cart-items-list .cart-item--sur'
    : '#cart-items-list .cart-item--gift:not(.cart-item--sur)') || document.querySelector('#cart-items-list .cart-item--gift');
  if (!body || !row) return false;
  // Rect math rather than offsetTop so it holds regardless of offsetParent.
  var rowRect  = row.getBoundingClientRect();
  var bodyRect = body.getBoundingClientRect();
  var delta = (rowRect.top - bodyRect.top) - (body.clientHeight - rowRect.height) / 2;
  var max = Math.max(0, body.scrollHeight - body.clientHeight);
  // Jump, don't animate: a smooth scroll here shares frames with the FLIP pass and
  // the reconcile render, and any render landing mid-flight cancels it anyway.
  body.scrollTop = Math.max(0, Math.min(body.scrollTop + delta, max));
  // Static highlight — a plain class toggle, two paints total, no keyframes.
  clearTimeout(window._tkGiftFlashTimer);
  row.classList.add('cart-item--gift-flash');
  window._tkGiftFlashTimer = setTimeout(function () {
    row.classList.remove('cart-item--gift-flash');
  }, 1400);
  return true;
}

// One-shot auto-reveal for the unlock moment. Mirrors _tkCelebratePending: if the
// gift was crossed with the drawer shut, it stays pending until the next open.
function tkTryRevealGift() {
  if (!window._tkGiftRevealPending) return;
  // Sequence after the cart settles. Revealing while the gift add is still in
  // flight puts a smooth scroll, a full FLIP pass and the reconcile render on the
  // same frames; waiting for idle lets each run alone.
  if (_tkMutLocked || _tkMutQ.length) {
    clearTimeout(window._tkGiftRevealTimer);
    window._tkGiftRevealTimer = setTimeout(tkTryRevealGift, 120);
    return;
  }
  if (tkRevealGiftRow(window._tkGiftRevealPending)) window._tkGiftRevealPending = false;
}

function tkTagsOf(p) {
  if (!p || !p.tags) return [];
  const arr = Array.isArray(p.tags) ? p.tags : String(p.tags).split(',');
  return arr.map(t => t.trim().toLowerCase());
}

var TK_RATINGS = {
  'undhiyu':[4.9,193],'rajwadi-undhiyu':[4.9,193],
  'gujrati-dal':[4.8,312],'gujarati-dal':[4.8,312],
  'pav-bhaji':[4.8,428],'pau-bhaji':[4.8,428],
  'paneer-butter-masala':[4.8,412],'paneer-butter':[4.8,412],'paneer-but-masala':[4.8,412],'panner-butter-masala':[4.8,412],'panner-butter':[4.8,412],
  'shahi-paneer':[4.8,198],
  'dal-tadka':[4.6,248],'dal-taraka':[4.6,248],
  'paneer-tikka-masala':[4.5,287],'paneer-tikka':[4.5,287],'panner-tikka-masala':[4.5,287],'panner-tikka':[4.5,287],
  'palak-paneer':[4.7,234],
  'chole':[4.6,224],'punjabi-chhole':[4.6,224],'punjabi-chole':[4.6,224],'chhole':[4.6,224],
  'veg-rajwadi':[4.5,168],
  'rajwadi-bateka':[4.2,156],'rajwadi-bateta':[4.2,156],'bateta':[4.2,156],'bateka':[4.2,156],
  'bharela-bateka':[4.1,149],'bharela-bateta':[4.1,149],
  'paneer-rajwadi':[4.3,156],'panner-rajwadi':[4.3,156],
  'kaju-curry':[4.4,134],'kaju-masala':[4.4,134],'kaju-butter-masala':[4.4,134],
  'mutter-paneer':[4.6,198],'mutter-panner':[4.6,198],
  'deshi-chana':[4.6,178],'desi-chana':[4.6,178],
  'rajwadi-dhokali':[4.6,142],'rajwadi-dhokli':[4.6,142],
  'veg-kholapuri':[4.6,142],'veg-kolhapuri':[4.6,142],
  'bharela-ringana':[4.5,87],
  'vaghareli-khichadi':[4.5,72],'vaghareli-khichdi':[4.5,72],'vakhareli-khichadi':[4.5,72],'vakhareli-khichdi':[4.5,72],'khichdi':[4.5,72],
  'methi-papad':[4.5,58],
  'kathiyawadi-kadhi':[4.4,89],
  'mag-masala':[4.4,64],'mag':[4.4,64],'mug-masala':[4.4,64],'mug':[4.4,64],
  'sev-tameta':[4.0,97],'sev-tameta-jain':[4.0,97],
  'rajma':[4.6,143],
  'surti-undhiyu':[4.6,167],
  'vatana-masala':[4.4,61],
  'ghee-khichadi':[4.6,98],
  'dalmasala-khichadi':[4.5,68],
  'kaju-gathiya':[4.6,98],
  'lasaniya-bateka':[4.5,72],
  'suki-bhaji':[4.3,47],
  'gujarati-kadhi':[4.5,113],
  'kadhi-pakoda':[4.6,129],
  'dal-dhokali':[4.6,87],
  'paneer-bhurji':[4.6,156],
  'veg-handi':[4.4,68],
  'gajar-halwa':[4.3,162],
  'moong-dal-halwa':[4.6,88],
  'kobich-vatana-jain':[4.2,39],
  'paneer-bt-masala-jain':[4.6,74],
  'amritsari-dal-makhani':[4.4,142],
  'paneer-bhurji-jain':[4.5,52],
  'dal-fry-jain':[4.4,66],
  'veg-pulav':[4.5,121],
  'jeera-rice':[4.5,46],
  'steam-rice':[4.3,58],
  'hyderabadi-biryani':[4.6,203],
  'udad-dal':[4.4,49],
  'guvar-dhokali':[4.3,41],
  'panchratna-dal':[4.6,77],
  'jain-dhokali':[4.4,56],
  'dahi-bhindi':[4.5,54],
  'jain-vaal':[4.3,44]
};
function tkGetRating(handle) { return TK_RATINGS[handle] || [4.7, 124]; }

// The variant a shopper can actually buy, or null when the product is sold out.
// Resolved against the server-rendered window.tkBuyable map: /products.json marks a
// zero-stock "continue"-policy variant as available:true and omits
// inventory_quantity, so the client cannot determine this on its own. Also avoids
// assuming variants[0] is the buyable one, which is wrong whenever the first pack
// is the sold-out one.
function tkBuyableVariant(p) {
  if (!p || !p.variants || !p.variants.length) return null;
  const map = window.tkBuyable;
  if (map) {
    if (!Object.prototype.hasOwnProperty.call(map, p.handle)) return null;
    const id = String(map[p.handle]);
    for (let i = 0; i < p.variants.length; i++) {
      if (String(p.variants[i].id) === id) return p.variants[i];
    }
    return null;
  }
  // Map missing (a stale cached layout): fall back to the old first-variant test
  // rather than emptying the rail entirely.
  const v0 = p.variants[0];
  return (v0 && v0.available !== false) ? v0 : null;
}

// Shared builder for a single recommendation product card (used by full render + swap).
function tkRecCardHTML(p) {
  // Price, MRP and the Add button all come off the SAME variant, so a card can
  // never quote one pack and add another.
  const v = tkBuyableVariant(p) || p.variants[0];
  const raw = p.images && p.images[0] ? (p.images[0].src || p.images[0]) : '';
  const img = raw ? raw.replace(/(\.(?:png|jpe?g|webp|gif))(\?.*)?$/i, '_300x300$1$2') : '';
  const price = Math.round(parseFloat(v.price));
  const mrp = v.compare_at_price ? Math.round(parseFloat(v.compare_at_price)) : 0;
  const name = tkCleanTitle(p.title);
  const [rating, rcount] = tkGetRating(p.handle);
  const safeTitle = tkEsc(name);
  const oldPrice = mrp > price ? `<span class="cart-rec-price-old">₹${mrp}</span>` : '';
  const wLabel = (window.tkWeightMap && window.tkWeightMap[p.handle]) ? window.tkWeightMap[p.handle] : '';
  return `<div class="cart-rec-card" data-handle="${p.handle}" data-title="${safeTitle}" data-price="${price}">
    <a class="cart-rec-img-link" href="/products/${p.handle}" onclick="tkCartNavTo(this.href,event)">
      <div class="cart-rec-img-wrap">
        <span class="cart-rec-rating"><span class="cart-rec-star">★</span> ${rating} <span class="cart-rec-rating-n">(${rcount})</span></span>
        ${img ? `<img class="cart-rec-img" src="${img}" alt="${tkEsc(name)}" loading="lazy">` : '<div class="cart-rec-img-ph"></div>'}
      </div>
    </a>
    <div class="cart-rec-body">
      <a class="cart-rec-name" href="/products/${p.handle}" onclick="tkCartNavTo(this.href,event)">${tkEsc(name)}</a>
      ${wLabel ? `<div class="cart-rec-weight">${wLabel}</div>` : ''}
      <div class="cart-rec-prices"><span class="cart-rec-price">₹${price}</span>${oldPrice}</div>
      <button class="cart-rec-atc" onclick="event.stopPropagation();event.preventDefault();addRecToCart(${v.id},this)" aria-label="Add ${tkEsc(name)}">ADD</button>
    </div>
  </div>`;
}

// Trailing "See all meals" tile — escape hatch to the full catalogue for
// buyers who want more than the rail shows.
function tkRecMoreCardHTML() {
  return '<a class="cart-rec-more" href="/collections/all" onclick="tkCartNavTo(this.href,event)" aria-label="See all meals">' +
    '<span class="cart-rec-more-ico" aria-hidden="true"><svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg></span>' +
    '<span class="cart-rec-more-txt">See all<br>meals</span>' +
  '</a>';
}

// Debounce rec recomputation: during a burst of cart updates the function
// never even starts until things settle (200ms after the last call).
function _tkScheduleRecs(cartItems) {
  _tkRecsGen++;
  clearTimeout(_tkRecsTimer);
  const gen = _tkRecsGen;
  // never while the sheet is still sliding up (it takes .36s): eight cards and their pictures
  // landing mid-slide were the drawer's longest frames
  const wait = Math.max(200, 420 - (performance.now() - tkDrawerOpenedAt));
  _tkRecsTimer = setTimeout(function () { renderCartRecs(cartItems, gen); }, wait);
}

// Single source for the recs heading, so the relabel-only fast path and the full
// remount can never drift apart.
function tkRecsLabelText(remaining, dominant) {
  if (remaining > 0 && remaining <= 180) {
    return '₹' + Math.ceil(remaining) + ' away from free delivery — add one more?';
  }
  if (dominant === 'gujarati') return 'Perfect with your Gujarati meal';
  if (dominant === 'north-indian') return 'A must-have North Indian combo';
  return 'Complete your comfort meal';
}

// Render recs using cached pool; sig guard makes this a no-op on pure qty changes
async function renderCartRecs(cartItems, gen) {
  const recsWrap = document.getElementById('cart-recs');
  const recsList = document.getElementById('cart-recs-list');
  if (!recsWrap || !recsList) return;
  if (!cartItems || cartItems.length === 0) { recsWrap.style.display = 'none'; tkLastRecsSig = null; return; }
  const remaining = TK_FREE_SHIP_THRESHOLD - tkCartSubtotal;
  const recsLabelEl = document.getElementById('cart-recs-label');
  const sig = cartItems.map(i => i.handle).slice().sort().join(',');
  if (sig === tkLastRecsSig && recsList.children.length) {
    // Handles unchanged — relabel only, skip the full remount. Always reassign:
    // rewriting solely inside the "nearly there" window meant a qty bump that
    // CROSSED free delivery left "₹120 away from free delivery" on screen.
    if (recsLabelEl) recsLabelEl.textContent = tkRecsLabelText(remaining, tkLastRecsDominant);
    return;
  }
  tkLastRecsSig = sig;
  try {
    const pool = await tkFetchRecsPool();
    if (gen !== _tkRecsGen) return; // newer _tkScheduleRecs fired while pool was fetching
    const cartHandles = new Set(cartItems.map(i => i.handle));
    const cuisineOf = p => {
      const tags = tkTagsOf(p);
      return (tags.includes('north-indian') || tags.includes('north indian')) ? 'north-indian' : 'gujarati';
    };
    const poolByHandle = new Map(pool.map(function(p) { return [p.handle, p]; }));
    const counts = { 'gujarati': 0, 'north-indian': 0 };
    cartItems.forEach(function(it) {
      const prod = poolByHandle.get(it.handle);
      if (prod) counts[cuisineOf(prod)] += (it.quantity || 1);
    });
    const dominant = counts['north-indian'] > counts['gujarati'] ? 'north-indian'
                   : counts['gujarati'] > 0 ? 'gujarati' : null;
    let candidates = pool.filter(p => {
      if (cartHandles.has(p.handle)) return false;
      return tkBuyableVariant(p) !== null;
    });
    if (dominant) {
      const same = candidates.filter(p => cuisineOf(p) === dominant);
      const rest = candidates.filter(p => cuisineOf(p) !== dominant);
      candidates = same.concat(rest);
    }
    // Near ₹799 threshold: bubble up items whose price helps cross free delivery
    if (remaining > 0 && remaining <= 250) {
      candidates.sort(function(a, b) {
        const pa = Math.round(parseFloat(a.variants[0].price));
        const pb = Math.round(parseFloat(b.variants[0].price));
        const aHelps = pa >= remaining && pa <= remaining + 100;
        const bHelps = pb >= remaining && pb <= remaining + 100;
        if (aHelps && !bHelps) return -1;
        if (!aHelps && bHelps) return 1;
        return 0;
      });
    }
    const recs = candidates.slice(0, 8);
    if (recs.length === 0) { recsWrap.style.display = 'none'; return; }
    tkLastRecsDominant = dominant;   // so the relabel-only path above can reuse it
    if (recsLabelEl) recsLabelEl.textContent = tkRecsLabelText(remaining, dominant);
    recsWrap.style.display = '';
    recsList.innerHTML = recs.map(tkRecCardHTML).join('') + tkRecMoreCardHTML();
    tkAnimateRecsIn(recsList);
  } catch(e) {
    tkLastRecsSig = null;
    if (recsWrap) recsWrap.style.display = 'none';
  }
}

function tkAnimateRecsIn(recsList) {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const cards = Array.prototype.slice.call(recsList.querySelectorAll('.cart-rec-card'));
  cards.forEach(el => { el.style.opacity = '0'; el.style.transform = 'translateY(6px)'; });
  requestAnimationFrame(() => {
    cards.forEach((el, i) => {
      const d = (i * 45) + 'ms';
      el.style.transition = 'opacity .24s ease ' + d + ', transform .24s cubic-bezier(.22,.61,.36,1) ' + d;
      el.style.opacity = '';
      el.style.transform = '';
    });
    setTimeout(() => cards.forEach(el => { el.style.transition = ''; }), 360 + cards.length * 45);
  });
}

// "Fly to cart": clone the rec image and animate it up to the "My Cart" title,
// so the buyer sees the product travel into the cart (not just vanish). The
// image rect is captured synchronously before any layout shift; the clone is a
// fixed-position element that animates independently. Pops the count on landing.
function tkFlyToCart(imgEl) {
  if (!imgEl || !imgEl.src) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const drawer = document.getElementById('cart-drawer');
  // Fly to the open cart drawer's title if it's open (cart context); otherwise
  // to the header cart icon (PDP / collection context, drawer closed).
  let target = (drawer && drawer.classList.contains('open')) ? drawer.querySelector('.cart-sheet-title') : null;
  if (!target || !target.getBoundingClientRect().width) {
    target = document.querySelector('.header-cart') || document.getElementById('nav-cart-count');
  }
  if (!target) return;
  const s = imgEl.getBoundingClientRect();
  const t = target.getBoundingClientRect();
  if (!s.width || !t.width) return;

  const fly = document.createElement('img');
  fly.src = imgEl.src;
  fly.className = 'tk-fly';
  fly.style.left = s.left + 'px';
  fly.style.top = s.top + 'px';
  fly.style.width = s.width + 'px';
  fly.style.height = s.height + 'px';
  document.body.appendChild(fly);

  const tx = (t.left + t.width / 2) - (s.left + s.width / 2);
  const ty = (t.top + t.height / 2) - (s.top + s.height / 2);

  const popCount = function () {
    const c = document.getElementById('cart-sheet-count');
    if (!c) return;
    c.classList.remove('tk-count-pop'); void c.offsetWidth; c.classList.add('tk-count-pop');
    setTimeout(function () { c.classList.remove('tk-count-pop'); }, 420);
  };
  let done = false;
  const cleanup = function () {
    if (done) return; done = true;
    if (fly.parentNode) fly.remove();
    popCount();
  };

  void fly.offsetWidth; // commit start position before transitioning
  requestAnimationFrame(function () {
    fly.style.transform = 'translate(' + tx + 'px,' + ty + 'px) scale(.16) rotate(-8deg)';
    fly.style.opacity = '.2';
    fly.style.borderRadius = '50%';
  });
  fly.addEventListener('transitionend', cleanup, { once: true });
  setTimeout(cleanup, 760); // safety net if transitionend doesn't fire
}

// Optimistic rec add: inserts cart row + swaps rec card before network.
// Key is upgraded to real Shopify key on POST response; background /cart.js reconciles state.
function addRecToCart(variantId, btn) {
  const card = btn ? btn.closest('.cart-rec-card') : null;
  const name = card ? (card.dataset.title || '') : '';
  const priceRs = card ? parseInt(card.dataset.price || '0', 10) : 0;
  const handle = card ? (card.dataset.handle || '') : '';
  const imgEl = card ? card.querySelector('.cart-rec-img') : null;
  const imgSrc = imgEl ? imgEl.src : '';

  if (btn) { btn.disabled = true; btn.classList.add('atc-loading'); }

  // Fly the product image up to the cart title (capture rect before layout shifts).
  tkFlyToCart(imgEl);

  // Pre-advance rec sig so reconcile won't re-render recs unnecessarily
  if (handle && tkLastRecsSig !== null) {
    const handles = tkLastRecsSig.split(',').filter(Boolean).concat(handle).sort();
    tkLastRecsSig = handles.join(',');
  }

  // Optimistic insert + rec swap happen immediately (before network)
  const tempKey = 'optimistic-' + variantId + '-' + Date.now();
  _tkInsertOptimisticRow(tempKey, String(variantId), name, priceRs, imgSrc, handle);
  if (card) _tkSwapRecCard(card, handle);

  // Network goes through queue — only fires after any pending qty changes finish
  _tkEnqueue(function () {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 }),
    })
      .then(tkJsonOk)
      .then(function (item) {
        // Upgrade temp key to real Shopify cart key
        const realKey = item.key;
        const tempRow = document.querySelector('[data-key="' + tempKey + '"]');
        if (tempRow && realKey) {
          tempRow.setAttribute('data-key', realKey);
          const btns = tempRow.querySelectorAll('.qty-btn');
          if (btns[0]) btns[0].setAttribute('onclick', `drawerRemoveItem('${realKey}')`);
          if (btns[1]) btns[1].setAttribute('onclick', `drawerChangeQty('${realKey}',2)`);
          const removeBtn = tempRow.querySelector('.cart-item-remove');
          if (removeBtn) removeBtn.setAttribute('onclick', `drawerRemoveItem('${realKey}')`);
        }
        tkPulseCartCount();
        // Optimistic row already shows the item; reconcile once after the burst.
        tkReconcileSoon();
      })
      .catch(function (err) {
        // Rollback: remove optimistic row, restore subtotal
        const tempRow = document.querySelector('[data-key="' + tempKey + '"]');
        if (tempRow) {
          tempRow.style.transition = 'opacity .2s ease';
          tempRow.style.opacity = '0';
          setTimeout(() => tempRow.remove(), 200);
        }
        tkCartSubtotal = Math.max(0, tkCartSubtotal - priceRs);
        tkCartHandleSet.delete(handle);
        const subtotalEl = document.getElementById('cart-subtotal');
        if (subtotalEl) subtotalEl.textContent = '₹' + Math.round(tkCartSubtotal);
        renderCartProgress(tkCartSubtotal);
        showToast((err && err.tkReason) || 'Could not add. Please try again.', 'error');
        tkReconcileSoon();
      });
  }, 'add-' + variantId);
}

// Optimistic qty change: patches DOM immediately, POSTs in background.
// Only falls back to full re-render if server disagrees by more than ₹1.
function drawerChangeQty(key, newQty) {
  const safeKey = window.CSS && CSS.escape ? CSS.escape(key) : key;
  const row = document.querySelector('.cart-item[data-key="' + safeKey + '"]');

  if (!row) {
    // Row gone from DOM — just enqueue a server sync
    _tkEnqueue(function () {
      return tkChangeRes(key, newQty).then(tkJsonOk).then(function (cart) { if (tkCartPending()) tkReconcileSoon(); else renderDrawerCart(cart); }).catch(function () { tkReconcileSoon(); });
    }, 'qty-' + key);
    return;
  }

  // ── Part A: immediate optimistic DOM patch ──
  const oldQty = parseInt(row.dataset.qty || '1', 10);
  const unitPriceCents = tkSafeInt(row.dataset.price, 0);
  const delta = newQty - oldQty;

  const qtyNumEl = row.querySelector('.qty-num');
  if (qtyNumEl) qtyNumEl.textContent = newQty;
  row.dataset.qty = newQty;
  tkRenderRowPrice(row); // keep the line total / "each" in sync with qty

  const btns = row.querySelectorAll('.qty-btn');
  if (btns[0]) btns[0].setAttribute('onclick',
    newQty <= 1 ? `drawerRemoveItem('${key}')` : `drawerChangeQty('${key}',${newQty - 1})`
  );
  if (btns[1]) btns[1].setAttribute('onclick', `drawerChangeQty('${key}',${newQty + 1})`);

  const unitPriceRs = tkSafeNum(unitPriceCents / 100, 0);
  if (!Number.isFinite(tkCartSubtotal)) tkCartSubtotal = 0;
  tkCartSubtotal += delta * unitPriceRs;
  const subtotalEl = document.getElementById('cart-subtotal');
  if (subtotalEl) subtotalEl.textContent = '₹' + Math.round(tkCartSubtotal);
  renderCartProgress(tkCartSubtotal);

  // ── Part B: debounced network via queue ──
  // 220ms window coalesces rapid taps. At fire time, reads the LIVE qty from DOM
  // (not the closed-over newQty) so the final request always carries the latest value.
  clearTimeout(_tkQtyTimers[key]);
  _tkQtyTimers[key] = setTimeout(function () {
    delete _tkQtyTimers[key];
    const liveRow = document.querySelector('.cart-item[data-key="' + safeKey + '"]');
    const currentQty = liveRow ? parseInt(liveRow.dataset.qty || '1', 10) : newQty;
    _tkEnqueue(function () {
      return tkChangeRes(key, currentQty)
        .then(tkJsonOk)
        .then(function (rawCart) {
          const cart = tkNormalizeCart(rawCart);
          tkSetLines(cart);
          const serverRs = tkSafeNum(tkComputeFoodSubtotal(cart), tkCartSubtotal);
          // If more mutations are queued after this one, or a tap is still waiting out its
          // debounce, they represent user intent that this server response does NOT reflect
          // yet. Update internal state but skip DOM updates — the final op will reconcile.
          // (The buttons stay live during the request: disabling them swallowed a second tap.)
          if (_tkMutQ.length > 0 || Object.keys(_tkQtyTimers).length > 0) {
            tkCartSubtotal = serverRs;
            tkCartHandleSet = new Set(cart.items.map(function(i) { return i.handle; }));
            return;
          }
          tkCartSubtotal = serverRs;
          const el = document.getElementById('cart-subtotal');
          const displayedRs = parseFloat(((el && el.textContent) || '0').replace(/[^0-9.]/g, ''));
          if (Math.abs(serverRs - displayedRs) > 1) {
            renderDrawerCart(rawCart);
          } else {
            if (el) el.textContent = '₹' + Math.round(serverRs);
            renderCartProgress(serverRs);
            tkSyncFreeGift(cart); // server-authoritative gift reconcile (this was the missing trigger)
            const safeCount = tkSafeInt(cart.item_count, 0);
            const countEl = document.getElementById('nav-cart-count');
            if (countEl) countEl.textContent = safeCount;
            const sheetCount = document.getElementById('cart-sheet-count');
            if (sheetCount) sheetCount.textContent = safeCount > 0 ? '· ' + safeCount : '';
          }
        })
        .catch(function (err) {
          // Refused: the basket is redrawn from the store. A later tap on this row sends its own number,
          // so say so only when none is coming (Shopify's reason when it gives one, e.g. stock).
          if (!tkCartPending()) showToast((err && err.tkReason) || 'Could not update your basket. Please try again.', 'error');
          tkReconcileSoon();
        });
    }, 'qty-' + key);
  }, 220);
}

// Removal is confirm-first: tapping × (or − at qty 1) opens a confirmation
// modal. Nothing touches the server until the shopper presses "Remove".
function drawerRemoveItem(key) {
  openRemoveConfirm(key);
}

// Shared confirm dialog (#tk-remove-modal in the layout, so it is available on
// the cart page too). Callers supply their own copy and the action to run on
// confirm; the defaults are restored on close so the next opener starts clean.
var TK_CONFIRM_TITLE  = 'Remove item from cart?';
var TK_CONFIRM_GO     = 'Remove';
var TK_CONFIRM_CANCEL = 'Cancel';

// How long a sold-out batch takes to come back. Same figure as batch_lead on the
// PDP's small-batch strip, from the kitchen's stated ~7-day production time —
// keep the two in step or the cart and the product page will promise different
// waits for the same pouch.
var TK_RESTOCK_LEAD = 'about a week';

function tkOpenConfirm(opts) {
  opts = opts || {};
  const modal = document.getElementById('tk-remove-modal');
  const scrim = document.getElementById('tk-remove-scrim');
  const body  = document.getElementById('tk-remove-body');
  const title = document.getElementById('tk-remove-title');
  // No dialog in the DOM — do the thing rather than swallow the click.
  if (!modal || !scrim) { if (opts.onConfirm) opts.onConfirm(); return; }
  if (title) title.textContent = opts.title || TK_CONFIRM_TITLE;
  if (body)  body.innerHTML = opts.body || '';
  const go = modal.querySelector('.tk-confirm-go');
  if (go) go.textContent = opts.confirmLabel || TK_CONFIRM_GO;
  const cancel = modal.querySelector('.tk-confirm-cancel');
  if (cancel) cancel.textContent = opts.cancelLabel || TK_CONFIRM_CANCEL;
  modal.dataset.key = opts.key || '';
  window._tkConfirmAction = opts.onConfirm || null;
  scrim.classList.add('open');
  modal.classList.add('open');
  modal.setAttribute('aria-hidden', 'false');
  if (go) go.focus();
}

// Copy for the "are you sure" on removing an item, shared by the drawer and the
// /cart page so the two cannot drift apart — the free-gift dialog is duplicated
// across those two surfaces and had to be edited twice when the gift changed.
//
// Loss aversion, not confirmshaming. The pull comes from naming the work the
// shopper is handing back to themselves — chopping, cleanup, deciding what to
// cook — because that burden is the actual thing this product removes, so it
// lands as a reminder of the value rather than a guilt trip. Three short lines
// instead of a paragraph: this is read in a hurry, mid-action, and a block of
// prose in a modal gets skipped wholesale.
//
// Deliberately NOT a self-blaming confirm label ("No thanks, I'll cook") — that
// pattern reads as contempt for the shopper and a premium brand pays for it in
// trust far longer than it gains in retained carts. The scarcity line stays
// factual: a cart genuinely reserves nothing (Shopify commits stock at
// checkout), and there is still no claim the item WILL sell out.
function tkRemoveConfirmCopy(name) {
  return {
    title: 'That’s one less easy night.',
    body: '<span class="tk-confirm-line"><strong>' + tkEsc(name) + '</strong> — five minutes from hot.</span>' +
          '<span class="tk-confirm-line">No chopping. No cleanup. Nothing to plan.</span>' +
          '<span class="tk-confirm-line">Nothing’s reserved. Next batch: ' + TK_RESTOCK_LEAD + '.</span>',
    cancelLabel: 'Keep it',
    confirmLabel: 'Remove anyway'
  };
}

function openRemoveConfirm(key) {
  const el = document.querySelector('.cart-item[data-key="' + (window.CSS && CSS.escape ? CSS.escape(key) : key) + '"]');
  if (!el) { tkCommitRemove(key); return; }  // row already gone — just sync server
  const name = el.getAttribute('data-name') || 'this item';
  const opts = tkRemoveConfirmCopy(name);
  opts.key = key;
  opts.onConfirm = function () { tkCommitRemove(key); };
  tkOpenConfirm(opts);
}

function closeRemoveConfirm() {
  const modal = document.getElementById('tk-remove-modal');
  const scrim = document.getElementById('tk-remove-scrim');
  window._tkConfirmAction = null;
  if (scrim) scrim.classList.remove('open');
  if (modal) {
    modal.classList.remove('open');
    modal.setAttribute('aria-hidden', 'true');
    const title = document.getElementById('tk-remove-title');
    const go = modal.querySelector('.tk-confirm-go');
    const cancel = modal.querySelector('.tk-confirm-cancel');
    if (title) title.textContent = TK_CONFIRM_TITLE;
    if (go) go.textContent = TK_CONFIRM_GO;
    if (cancel) cancel.textContent = TK_CONFIRM_CANCEL;
  }
}

function confirmRemoveItem() {
  // Captured before closing — closeRemoveConfirm clears the pending action.
  const action = window._tkConfirmAction;
  const modal = document.getElementById('tk-remove-modal');
  const key = modal ? modal.dataset.key : null;
  closeRemoveConfirm();
  if (typeof action === 'function') { action(); return; }
  if (key) tkCommitRemove(key);
}

// Collapse animation + server commit (qty 0). Reused on confirm.
function tkCommitRemove(key) {
  const el = document.querySelector('.cart-item[data-key="' + (window.CSS && CSS.escape ? CSS.escape(key) : key) + '"]');
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // The request leaves at once, alongside the row's fold (it used to wait for it), and the
  // answer is drawn once the fold is over, so the row never jumps out mid-animation.
  const folded = new Promise(function (res) { setTimeout(res, el && !reduced ? 360 : 0); });
  _tkEnqueue(function () {
    return tkChangeRes(key, 0)
      .then(tkJsonOk)
      .then(cart => folded.then(function () {
        // a tap still on its way (another row's +): its answer, or the reconcile, draws the end state
        if (tkCartPending()) { tkSetLines(tkNormalizeCart(cart)); tkReconcileSoon(); return; }
        renderDrawerCart(cart);
      }))
      .catch(() => folded.then(function () {
        // still in the basket: bring the row back whole, and say so
        const r = document.querySelector('.cart-item.cart-item-removing[data-key="' + (window.CSS && CSS.escape ? CSS.escape(key) : key) + '"]');
        if (r) r.remove();
        showToast('Could not remove it. Please try again.', 'error');
        tkReconcileSoon();
      }));
  }, 'remove-' + key);
  if (!el) return;

  // Totals, reward bar and counts follow the tap, not the network.
  if (!el.classList.contains('cart-item--gift')) {
    const q = tkSafeInt(el.dataset.qty, 1);
    tkCartSubtotal = Math.max(0, tkCartSubtotal - tkSafeNum(el.dataset.price / 100, 0) * q);
    const subEl = document.getElementById('cart-subtotal');
    if (subEl) subEl.textContent = '₹' + Math.round(tkCartSubtotal);
    renderCartProgress(tkCartSubtotal);
    const countEl = document.getElementById('nav-cart-count');
    const n = Math.max(0, tkSafeInt(countEl && countEl.textContent, 0) - q);
    if (countEl) countEl.textContent = n;
    const sheetCount = document.getElementById('cart-sheet-count');
    if (sheetCount) sheetCount.textContent = n > 0 ? '· ' + n : '';
  }
  // Reduced motion: the row goes at once. (It used to stay until the answer, and the last dish
  // never went: the guard against flashing the empty basket counted it as still there.)
  if (reduced) { el.classList.add('cart-item-removing'); el.style.display = 'none'; return; }

  // Two-phase exit (CSS transition-delays the collapse): the row fades out IN
  // PLACE first (height held), then collapses while already invisible. This
  // avoids ever showing a half-opacity / half-height card overlapping its
  // neighbors — which is what the single-phase fade+scale+collapse produced.
  el.classList.add('cart-item-removing');
  el.style.height = el.offsetHeight + 'px';  // pin for the height transition (one sync read)
  requestAnimationFrame(function() {
    el.style.opacity = '0';            // phase 1: fade (0–150ms)
    el.style.height = '0';             // phase 2: collapse (150–340ms, delayed via CSS)
    el.style.paddingTop = '0';
    el.style.paddingBottom = '0';
    el.style.borderBottomWidth = '0';
  });
}

// Escape closes the confirm modal (without also closing the cart drawer).
document.addEventListener('keydown', function (e) {
  if (e.key !== 'Escape') return;
  const modal = document.getElementById('tk-remove-modal');
  if (modal && modal.classList.contains('open')) {
    e.stopPropagation();
    closeRemoveConfirm();
  }
});

// Insert an optimistic cart row; if the variant is already present, increments
// its qty instead of appending a new row — prevents duplicates during rapid adds.
function _tkInsertOptimisticRow(key, variantId, name, priceRs, imgSrc, handle) {
  const list = document.getElementById('cart-items-list');
  if (!list) return;

  // ── Merge path: variant already in DOM ──────────────────────────────────
  // The gift is excluded: it is the same variant as a purchased one but a
  // separate cart line (its _free_gift property), so merging into it would bump a
  // row the server never grows and inflate the savings seal off its data-qty.
  const existing = list.querySelector('.cart-item[data-variant="' + variantId + '"]:not(.cart-item-removing):not(.cart-item--gift)');
  if (existing) {
    const oldQty = parseInt(existing.dataset.qty || '1', 10);
    const newQty = oldQty + 1;
    tkPatchCartRow(existing, { key: existing.dataset.key, quantity: newQty });
    tkCartHandleSet.add(handle);
    if (!Number.isFinite(tkCartSubtotal)) tkCartSubtotal = 0;
    tkCartSubtotal += tkSafeNum(priceRs, 0);
    const subtotalEl = document.getElementById('cart-subtotal');
    if (subtotalEl) subtotalEl.textContent = '₹' + Math.round(tkCartSubtotal);
    renderCartProgress(tkCartSubtotal);
    return;
  }

  // ── Append path: new product ─────────────────────────────────────────────
  const sheetBody = document.querySelector('.cart-sheet-body');
  const prevScroll = sheetBody ? sheetBody.scrollTop : 0;
  const oldTops = {};
  list.querySelectorAll('.cart-item').forEach(el => {
    oldTops[el.getAttribute('data-key')] = el.getBoundingClientRect().top;
  });

  const div = document.createElement('div');
  div.className = 'cart-item';
  div.setAttribute('data-key', key);
  div.setAttribute('data-variant', variantId);
  div.setAttribute('data-qty', '1');
  div.setAttribute('data-price', priceRs * 100);
  div.setAttribute('data-handle', handle);
  // setAttribute takes a raw value — pre-escaping stored a literal "&quot;" that
  // the remove dialog then escaped again and printed on screen.
  div.setAttribute('data-name', name);
  const _mrp = tkGetCompareAt(variantId, handle);
  const _mrpHTML = _mrp > priceRs ? `<span class="cart-item-mrp">₹${_mrp}</span>` : '';
  const _wLabel = (window.tkWeightMap && window.tkWeightMap[handle]) ? window.tkWeightMap[handle] : '';
  div.innerHTML =
    '<div class="cart-item-img">' +
    (imgSrc ? `<img src="${imgSrc}" alt="${tkEsc(name)}" loading="lazy" width="88" height="88">` : '<div class="cart-img-ph"></div>') +
    '</div>' +
    '<div class="cart-item-info">' +
    '<div class="cart-item-top">' +
    `<div class="cart-item-title"><span class="cart-item-link">${tkEsc(name)}</span></div>` +
    `<div class="cart-item-prices">${_mrpHTML}<span class="cart-item-price">₹${priceRs}</span></div>` +
    '</div>' +
    (_wLabel ? '<div class="cart-item-variant">' + _wLabel + '</div>' : '') +
    '<div class="cart-item-foot">' +
    '<div class="cart-item-qty">' +
    `<button class="qty-btn" onclick="drawerRemoveItem('${key}')" aria-label="Remove one">−</button>` +
    '<span class="qty-num">1</span>' +
    `<button class="qty-btn" onclick="drawerChangeQty('${key}',2)" aria-label="Add one">+</button>` +
    '</div>' +
    `<button class="cart-item-remove" onclick="drawerRemoveItem('${key}')" aria-label="Remove item">×</button>` +
    '</div></div>';

  list.appendChild(div);
  if (sheetBody) sheetBody.scrollTop = prevScroll;

  tkCartHandleSet.add(handle);
  if (!Number.isFinite(tkCartSubtotal)) tkCartSubtotal = 0;
  tkCartSubtotal += tkSafeNum(priceRs, 0);

  const footer = document.getElementById('cart-footer');
  if (footer) footer.style.display = 'block';
  const itemCount = list.querySelectorAll('.cart-item').length;
  const sheetCount = document.getElementById('cart-sheet-count');
  if (sheetCount) sheetCount.textContent = '· ' + itemCount;
  const navCount = document.getElementById('nav-cart-count');
  if (navCount) navCount.textContent = Math.max(itemCount, parseInt(navCount.textContent || '0', 10) + 1);
  const subtotalEl = document.getElementById('cart-subtotal');
  if (subtotalEl) subtotalEl.textContent = '₹' + Math.round(tkCartSubtotal);
  renderCartProgress(tkCartSubtotal);

  if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    tkFlipCartItems(list, oldTops);
  }
}

// Cross-fade a rec card with the next available pool candidate
function _tkSwapRecCard(removedCard, addedHandle) {
  const recsList = document.getElementById('cart-recs-list');
  const recsWrap = document.getElementById('cart-recs');
  if (!recsList) { removedCard.remove(); return; }

  if (!tkRecsPool) {
    removedCard.style.transition = 'opacity .18s ease';
    removedCard.style.opacity = '0';
    setTimeout(() => removedCard.remove(), 180);
    return;
  }

  // Handles already displayed (excluding the one being removed)
  const alreadyShown = new Set();
  recsList.querySelectorAll('.cart-rec-card[data-handle]').forEach(c => alreadyShown.add(c.dataset.handle));
  alreadyShown.delete(addedHandle);

  const candidate = tkRecsPool.find(p => {
    if (tkCartHandleSet.has(p.handle)) return false;
    if (alreadyShown.has(p.handle)) return false;
    return tkBuyableVariant(p) !== null;
  });

  // Fade out removed card
  removedCard.style.transition = 'opacity .18s ease';
  removedCard.style.opacity = '0';

  if (!candidate) {
    setTimeout(() => {
      removedCard.remove();
      if (recsWrap && !recsList.children.length) recsWrap.style.display = 'none';
    }, 180);
    return;
  }

  const tmp = document.createElement('div');
  tmp.innerHTML = tkRecCardHTML(candidate);
  const newCard = tmp.firstElementChild;

  // Pre-stage new card hidden, insert after old
  newCard.style.opacity = '0';
  newCard.style.transform = 'translateY(4px)';
  removedCard.parentNode.insertBefore(newCard, removedCard.nextSibling);

  setTimeout(() => {
    removedCard.remove();
    requestAnimationFrame(() => {
      newCard.style.transition = 'opacity .22s ease, transform .22s cubic-bezier(.22,.61,.36,1)';
      newCard.style.opacity = '';
      newCard.style.transform = '';
      setTimeout(() => { newCard.style.transition = ''; }, 240);
    });
  }, 180);
}

// ── Mobile Menu ──────────────────────────────────────────────────────────────
function toggleMobile() {
  const scrim = document.getElementById('mobile-scrim');
  const drawer = document.getElementById('mobile-drawer');
  const open = drawer.classList.toggle('open');
  scrim.classList.toggle('open', open);
  document.body.style.overflow = open ? 'hidden' : '';
}

function closeMobile() {
  document.getElementById('mobile-scrim').classList.remove('open');
  document.getElementById('mobile-drawer').classList.remove('open');
  document.body.style.overflow = '';
}

// ── Search ──────────────────────────────────────────────────────────────────
// One search for the header overlay, the collection page and /search (tkSearch).
// People type Gujarati, Hindi and English names in many spellings ("dhokli" for
// Dhokali, "mug" / "moong", "shev tamatar"), in Gujarati or Devanagari script, or a
// category ("jain", "sweet", "curry"). Each word is mapped to the catalogue's own word
// (SYN, SCRIPT), then matched on the title, the Devanagari name, the category and the
// ingredients; a spelling-tolerant key (consonants only, "dh"→"d") catches the rest.
// Every word must match; the best title match comes first. A search that names a dish
// we do not make ("palak paneer", "gajar halwa") is a miss: the closest dishes come
// back with miss = true, so the page can say so honestly and offer "Tell us".
// The index (/collections/all?view=search-index) loads the first time it is needed.
var tkSearch = (function () {
  var STOP = ' a an and the with for of in on to me my near best buy online order price pack packet pouch pouches food foods meal meals dish dishes recipe homemade home made thakar kitchen ready eat instant veg vegetarian pure indian style item items please want need some any show ';
  var WEAK = ' masala special spicy tasty authentic traditional hot fresh ';
  // any spelling → the catalogue's word
  var SYN = {};
  [
    ['ringana', 'ringan ringna ringna ringana brinjal brinjals baingan baigan bengan eggplant aubergine vengan vangi'],
    ['bateka', 'bateka bateta batata bataka batato potato potatoes aloo alu aaloo'],
    ['bhindi', 'bhindi bhinda bhindo bhendi okra ladyfinger'],
    ['mug', 'mug moong mung mag moongh'],
    ['chole', 'chole chhole cholay chholay chickpea chickpeas kabuli'],
    ['chana', 'chana channa chane chanaa'],
    ['rajma', 'rajma rajmah kidney'],
    ['mutter', 'mutter matar mattar mutar peas'],
    ['paneer', 'paneer panir panner paner paneer'],
    ['kaju', 'kaju kaaju cashew cashews'],
    ['methi', 'methi fenugreek'],
    ['guvar', 'guvar gavar guar gawar cluster'],
    ['dal', 'dal daal dhal dall lentil lentils'],
    ['dahi', 'dahi curd yogurt yoghurt'],
    ['tameta', 'tameta tamatar tameto tomato tomatoes tamato'],
    ['khichadi', 'khichadi khichdi khichri khichari kichdi khichidi khichdee khichadee'],
    ['kadhi', 'kadhi kadi karhi kadhee'],
    ['dhokali', 'dhokali dhokli dhokri dhokadi'],
    ['undhiyu', 'undhiyu undhiya undhiyun undiyu oondhiyu undhiu undhyu'],
    ['pav', 'pav paav paau pau'],
    ['biryani', 'biryani biriyani briyani biriani'],
    ['pulav', 'pulav pulao pulaw pilaf pilau'],
    ['jeera', 'jeera zeera jira cumin'],
    ['gathiya', 'gathiya gathia ganthiya ganthia gathiyaa'],
    ['vaal', 'vaal val lima'],
    ['udad', 'udad urad urid'],
    ['halwa', 'halwa halva halvo halavo halava halvah sheera'],
    ['kolhapuri', 'kolhapuri kholapuri kolapuri'],
    ['lasaniya', 'lasaniya lasania lasuniya'],
    ['bharela', 'bharela bharwa bharva stuffed bharelu'],
    ['sev', 'sev shev'],
    ['makhani', 'makhani makhni'],
    ['tadka', 'tadka tarka tadaka tadkaa'],
    ['rajwadi', 'rajwadi rajwari rajvadi'],
    ['panchratna', 'panchratna panchratan panchmel'],
    ['vaghareli', 'vaghareli vaghareli vagharela'],
    ['kathiyawadi', 'kathiyawadi kathyawadi kathiawadi kathiyawari kathiyavadi'],
    ['bhaji', 'bhaji bhajji'],
    ['pakoda', 'pakoda pakora pakode pakore pakodi'],
    ['desi', 'desi deshi'],
    ['suki', 'suki sukhi sooki'],
    ['papad', 'papad papadum pappadam'],
    ['gajar', 'gajar carrot carrots'],
    ['palak', 'palak spinach'],
    ['karela', 'karela bitter'],
    ['jain', 'jain jains'],
    ['sweet', 'sweet sweets dessert desserts mithai meetha mitha'],
    ['rice', 'rice chawal bhaat bhat'],
    ['curry', 'curry curries gravy sabzi sabji subzi subji shaak shak saak'],
    ['farsan', 'farsan snack snacks namkeen'],
    ['gujarati', 'gujarati gujrati gujju gujarat'],
    ['punjabi', 'punjabi punjab dhaba']
  ].forEach(function (r) { r[1].split(' ').forEach(function (w) { SYN[w] = r[0]; }); });
  var SCRIPT = {
    'ઊંધિયું': 'undhiyu', 'ઉંધિયુ': 'undhiyu', 'ઊંધિયુ': 'undhiyu', 'ઉંધિયું': 'undhiyu', 'દાળ': 'dal', 'કઢી': 'kadhi', 'ખીચડી': 'khichadi',
    'ઢોકળી': 'dhokali', 'રીંગણ': 'ringana', 'રીંગણા': 'ringana', 'બટેટા': 'bateka', 'બટાકા': 'bateka', 'બટેકા': 'bateka', 'સેવ': 'sev',
    'ટમેટા': 'tameta', 'ટામેટા': 'tameta', 'પનીર': 'paneer', 'ભાત': 'rice', 'જૈન': 'jain', 'મગ': 'mug', 'ચણા': 'chana', 'વાલ': 'vaal',
    'ગુવાર': 'guvar', 'ગાંઠિયા': 'gathiya', 'પાપડ': 'papad', 'મેથી': 'methi', 'ભરેલા': 'bharela', 'શાક': 'curry', 'મીઠાઈ': 'sweet',
    'હલવો': 'halwa', 'છોલે': 'chole', 'રાજમા': 'rajma', 'ભાજી': 'bhaji', 'પાવ': 'pav', 'બિરયાની': 'biryani', 'પુલાવ': 'pulav',
    'જીરા': 'jeera', 'અડદ': 'udad', 'કાજુ': 'kaju', 'લસણિયા': 'lasaniya', 'સૂકી': 'suki', 'ગાજર': 'gajar', 'પાલક': 'palak', 'કારેલા': 'karela',
    'दाल': 'dal', 'पनीर': 'paneer', 'छोले': 'chole', 'राजमा': 'rajma', 'खिचड़ी': 'khichadi', 'खिचडी': 'khichadi', 'कढ़ी': 'kadhi', 'कढी': 'kadhi',
    'बिरयानी': 'biryani', 'चावल': 'rice', 'पाव': 'pav', 'भाजी': 'bhaji', 'आलू': 'bateka', 'भिंडी': 'bhindi', 'बैंगन': 'ringana', 'मटर': 'mutter',
    'हलवा': 'halwa', 'मिठाई': 'sweet', 'जैन': 'jain', 'मूंग': 'mug', 'मूँग': 'mug', 'चना': 'chana', 'काजू': 'kaju', 'मेथी': 'methi', 'पुलाव': 'pulav',
    'जीरा': 'jeera', 'उड़द': 'udad', 'उड़द': 'udad', 'कोल्हापुरी': 'kolhapuri', 'सब्जी': 'curry', 'सब्ज़ी': 'curry', 'मखनी': 'makhani',
    'तड़का': 'tadka', 'गाजर': 'gajar', 'पालक': 'palak', 'करेला': 'karela', 'भरवां': 'bharela', 'ढोकली': 'dhokali', 'उंधियू': 'undhiyu', 'उंधियु': 'undhiyu'
  };
  // phrases first, so "no onion no garlic" is one idea and "mix veg" means the veg curries
  var PHRASES = [
    [/\b(no|without|bina|without any)\s+(onion|pyaz|pyaaz|kanda)\s*(and|&|no|or)?\s*(garlic|lahsun|lasan)?\b/g, ' nong '],
    [/\b(no|without)\s+garlic\b/g, ' nong '], [/\bonion\s*garlic\s*free\b/g, ' nong '],
    [/\b(sattvic|satvik|satvic|saatvik|swaminarayan)\b/g, ' nong '],
    [/\bmix(ed)?\s+veg(etable|etables|gie|gies)?\b/g, ' vegmix '],
    [/\bpav\s*bhaji\b|\bpavbhaji\b|\bpaubhaji\b|\bpaau\s*bhaji\b/g, ' pav bhaji '],
    [/\bsev\s*tamatar\b|\bshev\s*tamatar\b/g, ' sev tameta '],
    [/\bcottage\s+cheese\b/g, ' paneer '], [/\bgreen\s+gram\b/g, ' mug '], [/\blady\s*finger\b/g, ' bhindi '],
    [/\bfried\s+rice\b/g, ' fried rice '], [/\bnorth\s+indian\b/g, ' punjabi ']
  ];
  // not dishes: point to the page that answers it
  var INFO = [
    { re: /\b(licen[cs]e|fssai)\b/, label: 'Our FSSAI licence number is 10723999001893', href: '/pages/faq' },
    { re: /\b(track|tracking|where is my order|order status|awb)\b/, label: 'Track your order', href: '/pages/order-tracking' },
    { re: /\b(delivery|shipping|ship|courier|dispatch)\b/, label: 'Delivery and shipping', href: '/pages/faq' },
    { re: /\b(return|refund|cancel|replace|damaged)\b/, label: 'Returns and refunds', href: '/policies/refund-policy' },
    { re: /\b(contact|phone|call|whatsapp|email|support)\b/, label: 'Contact us', href: '/pages/contact' },
    { re: /\b(store|stores|shop near|retail|dealer|distributor|distribution|wholesale|export|bulk)\b/, label: 'Stores, trade and bulk orders', href: '/pages/stores' },
    { re: /\b(shelf life|preservative|preservatives|retort|how (is|it'?s) made|storage)\b/, label: 'How our food keeps without preservatives', href: '/pages/faq#how-its-made' }
  ];
  // categories: a word that is also a filter. The item passes by its type or tags, or by
  // naming the word in its title (which ranks higher).
  var CAT = {
    jain: function (p) { return /\bjain\b/.test(p._tg) || p.h.indexOf('jain') > -1; },
    nong: function (p) { return /\bjain\b/.test(p._tg) || /no.onion/.test(p._tg) || p.h.indexOf('jain') > -1; },
    sweet: function (p) { return p._ty === 'sweets' || /\bsweets?\b/.test(p._tg); },
    rice: function (p) { return p._ty === 'rice dishes' || /rice dishes/.test(p._tg) || /\b(rice|pulav|biryani)\b/.test(p._tl); },
    curry: function (p) { return !/rice dishes|sweets/.test(p._ty + ' ' + p._tg) && !/\b(dal|khichadi|kadhi|gathiya|papad|halwa)\b/.test(p._tw.join(' ')); },
    farsan: function (p) { return /\b(gathiya|papad)\b/.test(p._tw.join(' ')); },
    gujarati: function (p) { return p._ty === 'gujarati' || /\bgujarati\b/.test(p._tg); },
    punjabi: function (p) { return p._ty === 'north indian' || /north.indian/.test(p._tg); },
    vegmix: function (p) { return /\b(veg|undhiyu)\b/.test(p._tw.join(' ')) && !/rice/.test(p._ty + ' ' + p._tg + ' ' + p._tl); }
  };
  var CAT_LABEL = { jain: 'Jain dishes', nong: 'No onion, no garlic', sweet: 'Sweets', rice: 'Rice and pulav', curry: 'Shaak and curries',
    farsan: 'Farsan', gujarati: 'Gujarati dishes', punjabi: 'North Indian dishes', vegmix: 'Mixed vegetable dishes' };
  // words a spelling-tolerant key must not stretch to (dhokla is not dhokali)
  var EXACT_ONLY = ' dhokla khaman kadai kadhai handvo thepla dabeli dosa idli vada kheer malai kofta korma chaat raita roti rasam sambar sambhar poha upma saag gobi gobhi samosa kachori paratha bhature puri khandvi patra fafda jalebi rabdi basundi shrikhand mathri bharta bhel pani misal thepla lassi chaas achar ';
  // words about an occasion or a wish, not a dish: dropped, so "travel food" or "kids" shows the
  // favourites instead of "we don't make kids yet"
  var SOFT = ' kids kid children child breakfast lunch dinner office party festival festive diwali navratri holi gift gifting travel travelling trip journey hostel pg student students bachelor bachelors quick easy healthy tasty homestyle homemade mild spicy family size big small ';
  // a pure vegetarian kitchen: meat and egg searches get that answer, never a dish request
  var NONVEG = /\b(chicken|mutton|eggs?|omelette|fish|prawns?|shrimp|meat|keema|kheema|lamb|beef|pork|non ?veg|nonveg|tandoori chicken)\b/;
  // the collection's Most loved roster, for "nothing matched" fallbacks
  var LOVED = ['undhiyu', 'paneer-butter-masala', 'dal-dhokali', 'sev-tameta-jain', 'amritsari-dal-makhani', 'gujarati-dal', 'pav-bhaji', 'ghee-khichadi'];

  // letters of any script; built at run time so an older browser without \p{} still parses theme.js
  var NONWORD = /[.,;:!?()"'\/\\&+\-_*#@|[\]{}<>=~`^%$]/g;
  try { NONWORD = new RegExp('[^\\p{L}\\p{M}\\p{N}\\s]', 'gu'); } catch (e) {}
  function words(s) {
    s = String(s || '').toLowerCase();
    s = s.replace(NONWORD, ' ');
    return s.split(/\s+/).filter(Boolean);
  }
  function canon(w) { return SYN[w] || w; }
  function key(w) {
    if (w.length < 3 || /[^a-z]/.test(w)) return '';
    w = w.replace(/ph/g, 'f').replace(/w/g, 'v').replace(/z/g, 'j').replace(/q/g, 'k').replace(/x/g, 'ks').replace(/ck/g, 'k')
      .replace(/sh/g, 's').replace(/([bcdgjkpt])h/g, '$1').replace(/ng$/, 'g');
    var k = w[0] + w.slice(1).replace(/[aeiouy]/g, '');
    return k.replace(/(.)\1+/g, '$1');
  }
  function prep(p) {
    if (p._tw) return p;
    p.h = p.h || '';
    p._tl = String(p.t || '').toLowerCase();
    p._ty = String(p.ty || '').toLowerCase();
    p._tg = String(p.tg || '').toLowerCase();
    var tw = words(p.t + ' ' + p.h.replace(/-/g, ' ')).map(canon);
    words(p.dv).forEach(function (w) { if (SCRIPT[w]) tw.push(SCRIPT[w]); });
    p._tw = tw;
    p._tk = tw.map(key);
    var g = {}, d = {};
    words(p.g).forEach(function (w) { if (w.length > 3) g[canon(w)] = 1; });
    words(p.k).forEach(function (w) { if (w.length > 3 && !g[canon(w)]) d[canon(w)] = 1; });
    p._g = Object.keys(g);
    p._d = Object.keys(d);
    p._tt = ' ' + words(p._ty + ' ' + p._tg).join(' ') + ' ';
    p._pr = parseFloat(String(p.p || '').replace(/[^\d.]/g, '')) || 0;
    return p;
  }
  // Damerau-Levenshtein distance, stopping once it passes max (typos: a missing, extra,
  // wrong or swapped letter)
  function dist(a, b, max) {
    if (Math.abs(a.length - b.length) > max) return max + 1;
    var d = [], i, j;
    for (i = 0; i <= a.length; i++) { d[i] = [i]; }
    for (j = 0; j <= b.length; j++) d[0][j] = j;
    for (i = 1; i <= a.length; i++) {
      var low = max + 1;
      for (j = 1; j <= b.length; j++) {
        var c = a[i - 1] === b[j - 1] ? 0 : 1;
        d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + c);
        if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
        if (d[i][j] < low) low = d[i][j];
      }
      if (low > max) return max + 1;
    }
    return d[a.length][b.length];
  }
  // every word search knows: the catalogue's title words, every spelling in SYN, the categories
  var _vocabFor = null, _vocab = null;
  function vocab(items) {
    if (_vocabFor === items && _vocab) return _vocab;
    var v = {};
    items.forEach(function (p) { p._tw.forEach(function (w) { if (w.length > 2 && /^[a-z]+$/.test(w)) v[w] = 1; }); });
    Object.keys(SYN).forEach(function (w) { v[w] = 1; });
    Object.keys(CAT).forEach(function (w) { v[w] = 1; });
    _vocabFor = items; _vocab = v;
    return v;
  }
  // a word search does not know: a plural ("dals"), two words typed as one ("dalmakhani"),
  // or a typo of a known word ("bityani", "paneeer", "khicdi"). Returns the words it means,
  // or null. Words for dishes we do not make (EXACT_ONLY, and SYN's palak, gajar, karela)
  // are left alone, so a miss stays a miss.
  function mend(t, v) {
    if (v[t] || t.length < 4 || !/^[a-z]+$/.test(t) || EXACT_ONLY.indexOf(' ' + t + ' ') > -1) return null;
    var st = t.replace(/(es|s)$/, '');
    if (st !== t && v[st]) return [st];
    if (t.length >= 6) for (var i = 3; i <= t.length - 3; i++) {
      var a = t.slice(0, i), b = t.slice(i);
      if (v[a] && v[b]) return [a, b];
    }
    if (t.length < 5) return null;                                        // short words: too many near neighbours ("pani", "paneer")
    var max = t.length >= 7 ? 2 : 1, best = null, bd = max + 1;
    Object.keys(v).forEach(function (w) {
      if (w.length < 4 || (w[0] !== t[0] && max < 2)) return;
      var dd = dist(t, w, max);
      if (dd < bd) { bd = dd; best = w; }
    });
    return best ? [best] : null;
  }
  // the first vowel, so a spelling key only joins words that start alike (dhokli and
  // dhokali, halwa and halvo, but not gajar and gujarati)
  function v1(w) { var m = /[aeiou]/.exec(w.slice(1)); return m ? m[0] : ''; }
  function inList(t, list) {
    for (var i = 0; i < list.length; i++) if (list[i] === t || (t.length >= 5 && list[i].indexOf(t) === 0)) return true;
    return false;
  }
  // how well one query word fits one dish: 10 a title word, 8 a title word it starts,
  // 6 same spelling key, 5 in the category, 4 its type or tags, 2 an ingredient,
  // 1 a word of its description
  function fit(t, p, last) {
    var best = 0, i, tw = p._tw;
    for (i = 0; i < tw.length; i++) {
      if (tw[i] === t) return 10;
      if (tw[i].indexOf(t) === 0 && (t.length >= 3 || (last && t.length >= 2))) best = 8;
    }
    if (best) return best;
    var k = key(t);
    if (k.length >= 2 && t.length >= 4 && EXACT_ONLY.indexOf(' ' + t + ' ') < 0) {
      for (i = 0; i < p._tk.length; i++) {
        var pk = p._tk[i];
        if (pk && tw[i][0] === t[0] && v1(tw[i]) === v1(t) &&
            (pk === k || (pk.length >= 3 && k.length >= 3 && Math.abs(pk.length - k.length) <= 1 && (pk.indexOf(k) === 0 || k.indexOf(pk) === 0)))) return 6;
      }
    }
    if (CAT[t]) return CAT[t](p) ? 5 : (p._tt.indexOf(' ' + t + ' ') > -1 ? 4 : 0);   // a category never rests on ingredients
    if (p._tt.indexOf(' ' + t + ' ') > -1) return 4;
    if (t.length >= 4 && inList(t, p._g)) return 2;
    if (t.length >= 4 && inList(t, p._d)) return 1;
    return 0;
  }
  function parse(raw) {
    var s = ' ' + String(raw || '').toLowerCase().slice(0, 80) + ' ';
    Object.keys(SCRIPT).forEach(function (w) { if (s.indexOf(w) > -1) s = s.split(w).join(' ' + SCRIPT[w] + ' '); });
    // what the words ask for beyond a dish: a price ceiling, the cheapest, the newest, the favourites
    var want = {};
    s = s.replace(/\b(under|below|less than|within|upto|up to|max|maximum|<)\s*(rs\.?|inr|\u20b9)?\s*(\d{2,4})\s*(rs|rupees|inr|\/-)?/g, function (m, a, b, n) { want.max = +n; return ' '; })
      .replace(/(\u20b9|\brs\.?\s?)(\d{2,4})\s*(and below|or less|or under)?/g, function (m, a, n, c) { want.max = +n; return ' '; })
      .replace(/\b(cheap|cheapest|budget|affordable|sasta|lowest price|low price|economical)\b/g, function () { want.cheap = 1; return ' '; })
      .replace(/\b(new|newest|latest|just launched|launched|new arrivals?)\b/g, function () { want.fresh = 1; return ' '; })
      .replace(/\b(best ?sellers?|best ?selling|popular|most loved|top rated|top|famous|favourites?|favorites?|recommended|must try)\b/g, function () { want.loved = 1; return ' '; });
    PHRASES.forEach(function (ph) { s = s.replace(ph[0], ph[1]); });
    var all = words(s), toks = [], weak = [];
    all.forEach(function (w) {
      if (STOP.indexOf(' ' + w + ' ') > -1 || SOFT.indexOf(' ' + w + ' ') > -1) return;
      if (/^\d+(g|gm|gms|gram|grams|kg|ml|pcs|pc|x)?$/.test(w)) return;          // "300g", "pack of 3"
      if (/^(pack|packs|combo|offer|offers|deal|deals|discount|sale|rupees|rs|inr)$/.test(w)) return;
      var c = canon(w);
      if (WEAK.indexOf(' ' + w + ' ') > -1) { weak.push(c); return; }
      if (toks.indexOf(c) < 0) toks.push(c);
    });
    var typedLast = /\S$/.test(String(raw || ''));
    // still typing a new word: one letter says nothing yet ("paneer b")
    if (typedLast && toks.length > 1 && toks[toks.length - 1].length < 2) toks.pop();
    return { toks: toks, weak: weak, typedLast: typedLast, want: want };
  }
  function byLoved(a, b) {
    var ia = LOVED.indexOf(a.h), ib = LOVED.indexOf(b.h);
    return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
  }
  function run(raw, items) {
    var q = parse(raw), out = { items: [], miss: false, label: '', info: null, empty: false, q: String(raw || '').trim().slice(0, 80), fixed: '', toks: [], soldOut: false };
    var low = ' ' + out.q.toLowerCase() + ' ';
    for (var n = 0; n < INFO.length; n++) if (INFO[n].re.test(low)) { out.info = INFO[n]; break; }
    items = (items || []).map(prep);
    if (NONVEG.test(low)) {
      out.nonveg = true;
      out.info = { label: 'Thakar Kitchen is a 100% vegetarian kitchen. Here are the dishes people love most.', href: '/pages/about' };
      out.label = 'Most loved';
      out.items = items.filter(function (p) { return p.a && LOVED.indexOf(p.h) > -1; }).sort(byLoved);
      return out;
    }
    var all = items, w = q.want, labels = [];
    // a price ceiling, "new" and "popular" narrow the dishes before the words are matched
    if (w.max) { if (!q.toks.length) items = items.filter(function (p) { return p._pr && p._pr <= w.max; }); labels.push('Under \u20b9' + w.max); }
    if (w.fresh) { items = items.filter(function (p) { return /just.launched/.test(p._tg); }); labels.push('New dishes'); }
    if (w.loved && !q.toks.length) { items = items.filter(function (p) { return LOVED.indexOf(p.h) > -1; }); labels.push('Most loved'); }
    var byPrice = function (a, b) { return (b.a - a.a) || (a._pr - b._pr); };
    // a price ceiling with words: keep the matches under it; if none are, show the matches
    // cheapest first and say so (over)
    function cap(o) {
      if (!w.max) return o;
      var f = o.items.filter(function (p) { return p._pr && p._pr <= w.max; });
      if (f.length) o.items = f; else { o.over = w.max; o.items = o.items.slice().sort(byPrice); }
      return o;
    }
    // words it does not know are mended (plural, run together, typo) when that finds a dish
    var v = vocab(all), fixedAny = false, toks = [];
    q.toks.forEach(function (t) {
      var known = v[t] || CAT[t] || all.some(function (p) { return fit(t, p, false) >= 6; });
      var m = known ? null : mend(t, v);
      if (m) { fixedAny = true; m.forEach(function (x) { x = canon(x); if (toks.indexOf(x) < 0) toks.push(x); }); }
      else toks.push(t);
    });
    if (fixedAny) out.fixed = toks.join(' ');
    out.toks = toks;
    if (!toks.length) {
      out.empty = !out.info && !labels.length;
      out.label = labels.join(' \u00b7 ');
      out.items = out.info && !labels.length ? [] : items.slice().sort(w.cheap || w.max ? byPrice : function (a, b) { return (b.a - a.a) || byLoved(a, b); }).slice(0, labels.length ? 24 : 8);
      if (labels.length && !out.items.length) out.miss = true;
      return out;
    }
    var cats = toks.filter(function (t) { return CAT[t]; });
    if (cats.length === toks.length) labels = cats.map(function (c) { return CAT_LABEL[c]; }).concat(labels);
    out.label = labels.join(' \u00b7 ');
    var scored = items.map(function (p) {
      var sum = 0, hit = 0, strong = 0, named = 0;
      toks.forEach(function (t, i) {
        var f = fit(t, p, q.typedLast && i === toks.length - 1);
        if (f) { hit++; sum += f; if (f >= 5) strong++; if (f >= 6 && !CAT[t]) named++; }
      });
      q.weak.forEach(function (t) { if (fit(t, p, false) >= 6) sum += 3; });
      if (p._tl.indexOf(out.q.toLowerCase()) > -1 && out.q.length > 2) sum += 15;
      return { p: p, sum: sum + (p.a ? 1 : 0), hit: hit, strong: strong, named: named };
    });
    var rank = w.cheap ? function (a, b) { return (b.p.a - a.p.a) || (a.p._pr - b.p._pr); }
      : function (a, b) { return (b.p.a - a.p.a) || (b.sum - a.sum) || byLoved(a.p, b.p) || a.p._tl.localeCompare(b.p._tl); };
    var full = scored.filter(function (s) { return s.hit === toks.length; });
    // a dish word that only matched ingredients means we do not make that dish
    var named = toks.filter(function (t) { return !CAT[t]; });
    var titleFull = full.filter(function (s) { return named.every(function (t) { return fit(t, s.p, false) >= 6 || (q.typedLast && fit(t, s.p, true) >= 6); }); });
    if (titleFull.length || (full.length && !named.length)) {
      out.items = (titleFull.length ? titleFull : full).sort(rank).map(function (s) { return s.p; });
      // dishes that only have it as an ingredient follow ("brinjal": Bharela Ringana, then Undhiyu);
      // a description that merely mentions it ("serve with khichdi") does not count
      if (titleFull.length) full.filter(function (s) {
        return titleFull.indexOf(s) < 0 && named.every(function (t) { return fit(t, s.p, false) >= 2; });
      }).sort(rank).forEach(function (s) { out.items.push(s.p); });
      out.soldOut = out.items.length > 0 && !out.items.some(function (p) { return p.a; });
      return cap(out);
    }
    var inIng = full.filter(function (s) { return fit(named[0], s.p, false) >= 2; });
    if (inIng.length && named.length === 1 && named[0].length >= 5 && !SYN[named[0]]) {
      // one unknown word that is an ingredient ("peanuts", "cashew"): the dishes with it. A word
      // only a description mentions ("serve with pickle") is not enough.
      out.items = inIng.sort(rank).map(function (s) { return s.p; });
      out.label = 'Dishes with ' + named[0];
      return cap(out);
    }
    if (out.info && !titleFull.length) { out.items = []; return out; }   // "fssai licence", "track my order"
    out.miss = true;
    var part = scored.filter(function (s) { return s.strong > 0; })
      .sort(function (a, b) { return (b.named - a.named) || (b.strong - a.strong) || rank(a, b); });
    out.items = (part.length ? part.map(function (s) { return s.p; }) : all.filter(function (p) { return p.a; }).sort(byLoved)).slice(0, 8);
    return out;
  }

  var _items = null, _loading = null;
  function load() {
    if (_items) return Promise.resolve(_items);
    if (_loading) return _loading;
    _loading = fetch('/collections/all?view=search-index', { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(function (d) { _items = (d || []).map(prep); return _items; })
      .catch(function (e) { _loading = null; throw e; });
    return _loading;
  }
  // a dish request becomes a Shopify customer tagged "wants: <dish>" (form_type customer
  // also subscribes them to email; the form says so)
  function requestTag(q) {
    return 'wants: ' + String(q || '').toLowerCase().replace(NONWORD, ' ').replace(/,/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 40);
  }
  // the words of a title that the search matched, for highlighting
  function hits(title, toks) {
    return words(title).filter(function (x) { var c = canon(x); return toks.some(function (t) { return c === t || (t.length >= 3 && c.indexOf(t) === 0); }); });
  }
  return { run: run, load: load, items: function () { return _items; }, parse: parse, key: key, requestTag: requestTag, hits: hits, CAT_LABEL: CAT_LABEL };
})();
window.tkSearch = tkSearch;
// "We don't make it yet — want it?" A plain Shopify customer form: the shopper becomes a
// customer tagged "dish-request, wants: <dish>" and is subscribed to email (the form says
// so), so the requests can be counted in Shopify admin (Customers, the "Dish requests"
// segment). back: the dish exists but is sold out, so the form offers "email me when it's
// back" and adds the tag "back-in-stock".
var _tkReqN = 0;
function tkDishRequestHTML(q, back) {
  var tag = tkSearch.requestTag(q), shown = String(q || '').trim().slice(0, 40);
  var done = false; try { done = sessionStorage.getItem('tk-req:' + tag) === '1'; } catch (e) {}
  var thanks = back ? 'Thank you. We’ll email you when <b>' + tkEsc(shown) + '</b> is back.' : 'Thank you. We’ll email you when <b>' + tkEsc(shown) + '</b> is ready.';
  if (done) return '<p class="tk-req tk-req--done" role="status">' + thanks + '</p>';
  return '<form class="tk-req" method="post" action="/contact#contact_form" accept-charset="UTF-8" novalidate data-thanks="' + tkEsc(thanks) + '">'
    + '<input type="hidden" name="form_type" value="customer"><input type="hidden" name="utf8" value="✓">'
    + '<input type="hidden" name="contact[tags]" value="' + tkEsc('dish-request, ' + (back ? 'back-in-stock, ' : '') + tag) + '">'
    + '<p class="tk-req-t">' + (back ? '<b>' + tkEsc(shown) + '</b> is sold out right now. Want an email when it’s back?'
        : 'Want us to make <b>' + tkEsc(shown) + '</b>? Tell us, and we’ll email you when it’s ready.') + '</p>'
    + '<div class="tk-req-row"><label class="hp-sr" for="tk-req-' + (++_tkReqN) + '">Your email</label>'
    + '<input class="tk-req-in" id="tk-req-' + _tkReqN + '" type="email" name="contact[email]" required autocomplete="email" inputmode="email" enterkeyhint="send" placeholder="Your email">'
    + '<button class="tk-req-go" type="submit">' + (back ? 'Email me' : 'Tell us') + '</button></div>'
    + '<p class="tk-req-fine">You’ll also get our occasional news. Unsubscribe anytime.</p>'
    + '<p class="tk-req-err" role="alert" hidden></p></form>';
}
window.tkDishRequestHTML = tkDishRequestHTML;
// The lines every search screen shows above its results: what a typo was read as, a price
// ceiling nothing met, and the sold-out offer. The miss note stays each screen's own.
function tkSearchNotesHTML(r) {
  var h = '';
  if (r.fixed) h += '<p class="tk-sn">Showing results for <b>' + tkEsc(r.fixed) + '</b></p>';
  if (r.over) h += '<p class="tk-sn">Nothing under ₹' + r.over + ' for that. Here it is, lowest price first.</p>';
  if (r.soldOut && r.items[0]) h += '<div class="tk-sn-back">' + tkDishRequestHTML(r.items.length === 1 ? r.items[0].t : r.q, true) + '</div>';
  return h;
}
window.tkSearchNotesHTML = tkSearchNotesHTML;
document.addEventListener('submit', function (e) {
  var f = e.target;
  if (!f || !f.classList || !f.classList.contains('tk-req')) return;
  e.preventDefault();
  var email = f.querySelector('[name="contact[email]"]'), err = f.querySelector('.tk-req-err'), btn = f.querySelector('.tk-req-go');
  var val = email.value.trim().replace(/\s+/g, '');
  email.value = val;
  if (!val || !/^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i.test(val)) {
    err.textContent = 'Please enter your email address, like name@gmail.com.'; err.hidden = false; email.focus(); return;
  }
  // common slips in the domain: gmial.com, gmail.con, yaho.com
  var fix = { 'gmial.com': 'gmail.com', 'gmai.com': 'gmail.com', 'gamil.com': 'gmail.com', 'gmail.co': 'gmail.com', 'gmail.con': 'gmail.com', 'gmail.cm': 'gmail.com',
    'yaho.com': 'yahoo.com', 'yahoo.con': 'yahoo.com', 'yahoo.co': 'yahoo.co.in', 'hotmial.com': 'hotmail.com', 'outlok.com': 'outlook.com', 'rediffmail.con': 'rediffmail.com' };
  var dom = val.split('@')[1].toLowerCase();
  if (fix[dom] && !f.getAttribute('data-checked')) {
    email.value = val.split('@')[0] + '@' + fix[dom];
    f.setAttribute('data-checked', '1');
    err.textContent = 'Did you mean ' + email.value + '? Tap again to send.'; err.hidden = false; return;
  }
  err.hidden = true; btn.disabled = true; btn.classList.add('is-busy');
  var tags = f.querySelector('[name="contact[tags]"]').value.split(', ');
  var tag = tags[tags.length - 1] || '';
  var thanks = f.getAttribute('data-thanks') || 'Thank you.';
  // Posted in place; anything but a clear success (Shopify's spam check at /challenge, a
  // refusal, a network error) hands the form to Shopify's own submit, so no request is lost.
  fetch(f.action, { method: 'POST', body: new FormData(f), credentials: 'same-origin' }).then(function (r) {
    if (!/customer_posted=true/.test(r.url)) { f.submit(); return; }
    try { sessionStorage.setItem('tk-req:' + tag, '1'); } catch (x) {}
    var p = document.createElement('p');
    p.className = 'tk-req tk-req--done'; p.setAttribute('role', 'status');
    p.innerHTML = thanks;
    f.replaceWith(p);
  }).catch(function () { f.submit(); });
});

// ── Search overlay (the header's search button and the menu's "Search dishes") ──
(function () {
  var overlay = document.getElementById('search-overlay');
  var scrim = document.getElementById('search-scrim');
  var input = document.getElementById('search-input');
  var box = document.getElementById('search-suggest');
  var status = document.getElementById('search-status');
  if (!overlay || !input || !box) return;
  var POP = [['Jain', 'jain'], ['Dal', 'dal'], ['Paneer', 'paneer'], ['Undhiyu', 'undhiyu'], ['Khichadi', 'khichadi'],
    ['Kadhi', 'kadhi'], ['No onion no garlic', 'no onion no garlic'], ['Rice', 'rice'], ['Under ₹150', 'under 150']];
  var lastFocus = null, timer = 0, lastRun = null;

  // recent searches, on this phone only: the last five that led somewhere (a result, Enter)
  function recent() { try { return JSON.parse(localStorage.getItem('tk-recent') || '[]').slice(0, 5); } catch (e) { return []; } }
  function remember(q) {
    q = String(q || '').trim().slice(0, 60);
    if (q.length < 2) return;
    try { localStorage.setItem('tk-recent', JSON.stringify([q].concat(recent().filter(function (x) { return x.toLowerCase() !== q.toLowerCase(); })).slice(0, 5))); } catch (e) {}
  }
  function mark(title, toks) {
    var hit = toks && toks.length ? tkSearch.hits(title, toks) : [];
    if (!hit.length) return tkEsc(title);
    return String(title).split(/(\s+)/).map(function (w) {
      var bare = w.toLowerCase().replace(/[^a-zऀ-૿]/g, '');
      return bare && hit.indexOf(bare) > -1 ? '<mark class="ss-hl">' + tkEsc(w) + '</mark>' : tkEsc(w);
    }).join('');
  }
  function row(p, toks) {
    var oos = !p.a;
    return '<a class="ss-row' + (oos ? ' ss-row--oos' : '') + '" href="' + tkEsc(p.u || '/products/' + p.h) + '">'
      + (p.i ? '<img class="ss-thumb" src="' + tkEsc(p.i) + '" alt="" width="48" height="48" loading="lazy" decoding="async">' : '<span class="ss-thumb"></span>')
      + '<span class="ss-info"><span class="ss-title">' + mark(p.t, toks) + '</span>'
      + '<span class="ss-price">' + (oos ? '<span class="ss-oos">Sold out</span>' : tkEsc(String(p.p || '').replace(/^Rs\.\s*/, '₹'))) + '</span></span></a>';
  }
  function chips() {
    var rec = recent(), h = '';
    if (rec.length) h += '<div class="ss-recent-h"><p class="ss-heading">Your recent searches</p><button type="button" class="ss-clear">Clear</button></div><div class="ss-chips">'
      + rec.map(function (q) { return '<button type="button" class="ss-chip ss-chip--recent" data-q="' + tkEsc(q) + '">' + tkEsc(q) + '</button>'; }).join('') + '</div>';
    return h + '<p class="ss-heading">Popular searches</p><div class="ss-chips">'
      + POP.map(function (c) { return '<button type="button" class="ss-chip" data-q="' + tkEsc(c[1]) + '">' + tkEsc(c[0]) + '</button>'; }).join('') + '</div>';
  }
  function paint(raw) {
    var q = raw.trim();
    var items = tkSearch.items();
    if (!items) {
      if (q) box.innerHTML = '<p class="ss-wait">Searching…</p>';
      tkSearch.load().then(function () { paint(input.value); })
        .catch(function () { if (input.value.trim()) box.innerHTML = '<p class="ss-wait">Press Enter to search all dishes.</p>'; });
      if (!q) box.innerHTML = chips();
      return;
    }
    if (!q) { lastRun = null; box.innerHTML = chips(); input.setAttribute('aria-expanded', 'false'); status.textContent = ''; return; }
    var r = lastRun = tkSearch.run(q, items), html = '';
    if (r.info) html += '<a class="ss-info-row" href="' + tkEsc(r.info.href) + '">' + tkEsc(r.info.label) + ' →</a>';
    if (r.miss) {
      html += '<div class="ss-miss"><p class="ss-miss-t">We don’t make “' + tkEsc(q) + '” yet.</p>'
        + tkDishRequestHTML(q) + '</div>';
      if (r.items.length) html += '<p class="ss-heading">Closest dishes we make</p>';
    } else {
      html += tkSearchNotesHTML(r);
      if (r.label && r.items.length) html += '<p class="ss-heading">' + tkEsc(r.label) + '</p>';
    }
    html += r.items.slice(0, 8).map(function (p) { return row(p, r.toks); }).join('');
    if (!r.info || r.items.length) html += '<a class="ss-all" href="/search?type=product&q=' + encodeURIComponent(q) + '">See all results for “' + tkEsc(q) + '” →</a>';
    box.innerHTML = html;
    input.setAttribute('aria-expanded', 'true');
    status.textContent = r.miss ? 'We don’t make ' + q + ' yet. ' + r.items.length + ' close dishes.'
      : (r.fixed ? 'Showing results for ' + r.fixed + '. ' : '') + r.items.length + (r.items.length === 1 ? ' dish' : ' dishes');
  }
  input.addEventListener('input', function () {
    clearTimeout(timer);
    timer = setTimeout(function () { paint(input.value); }, 120);
  });
  box.addEventListener('click', function (e) {
    if (e.target.closest('.ss-clear')) { try { localStorage.removeItem('tk-recent'); } catch (x) {} paint(''); input.focus(); return; }
    if (e.target.closest('a.ss-row, a.ss-all')) { remember(input.value); return; }
    var c = e.target.closest('.ss-chip');
    if (!c) return;
    input.value = c.getAttribute('data-q');
    paint(input.value);
    input.focus();
  });
  overlay.querySelector('form').addEventListener('submit', function (e) {
    var q = input.value.trim();
    if (!q) { e.preventDefault(); input.focus(); return; }
    remember(q);
    // one clear answer: go straight to the dish rather than a results page of one
    if (lastRun && !lastRun.miss && lastRun.items.length === 1 && lastRun.q === q) { e.preventDefault(); location.href = lastRun.items[0].u || '/products/' + lastRun.items[0].h; }
  });
  // arrows move through the results; Escape closes
  overlay.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') { closeSearch(); return; }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    if (document.activeElement && document.activeElement.classList.contains('tk-req-in')) return;
    var links = Array.prototype.slice.call(box.querySelectorAll('a.ss-row, a.ss-info-row, a.ss-all, .ss-chip'));
    if (!links.length) return;
    var i = links.indexOf(document.activeElement);
    e.preventDefault();
    if (e.key === 'ArrowDown') (links[i + 1] || links[0]).focus();
    else if (i <= 0) input.focus();
    else links[i - 1].focus();
  });
  // Speak instead of type (where the browser can listen: Chrome on Android and desktop, Safari
  // with Siri on). Gujarati or Hindi phones listen in that language; SCRIPT reads the words.
  var SR = window.SpeechRecognition || window.webkitSpeechRecognition, mic = document.getElementById('search-mic');
  if (SR && mic) {
    mic.hidden = false;
    var rec = null;
    mic.addEventListener('click', function () {
      if (rec) { rec.stop(); return; }
      var lang = (navigator.language || 'en-IN').toLowerCase();
      rec = new SR();
      rec.lang = /^gu/.test(lang) ? 'gu-IN' : /^hi/.test(lang) ? 'hi-IN' : 'en-IN';
      rec.interimResults = true; rec.maxAlternatives = 1;
      mic.classList.add('is-on'); mic.setAttribute('aria-pressed', 'true');
      input.placeholder = 'Listening… say a dish';
      rec.onresult = function (ev) {
        var t = ''; for (var i = 0; i < ev.results.length; i++) t += ev.results[i][0].transcript;
        input.value = t.replace(/[.?!]+$/, ''); paint(input.value);
      };
      rec.onend = rec.onerror = function () {
        mic.classList.remove('is-on'); mic.setAttribute('aria-pressed', 'false');
        input.placeholder = 'Search undhiyu, dal, paneer…'; rec = null;
        if (input.value.trim()) remember(input.value);
      };
      try { rec.start(); } catch (x) { rec.onend(); }
    });
  }
  window.openSearch = function () {
    lastFocus = document.activeElement;
    overlay.classList.add('is-open');
    document.documentElement.classList.add('tk-glass-open');   // the page behind pauses its animations (house.css)
    if (scrim) scrim.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    paint(input.value);
    setTimeout(function () { input.focus(); }, 60);
  };
  window.closeSearch = function () {
    overlay.classList.remove('is-open');
    document.documentElement.classList.remove('tk-glass-open');
    if (scrim) scrim.classList.remove('is-open');
    document.body.style.overflow = '';
    input.value = '';
    box.innerHTML = '';
    lastRun = null;
    if (lastFocus && lastFocus.focus) try { lastFocus.focus(); } catch (e) {}
  };
  // start fetching the index as soon as someone reaches for search
  ['pointerenter', 'touchstart', 'focus'].forEach(function (ev) {
    document.querySelectorAll('.header-search, .mobile-search-trigger').forEach(function (b) {
      b.addEventListener(ev, function () { tkSearch.load().catch(function () {}); }, { passive: true, once: true });
    });
  });
})();

// ── Toast ────────────────────────────────────────────────────────────────────
function tkSetToastAction(label, handler, href) {
  const a = document.querySelector('#toast .toast-action');
  if (!a) return;
  a.textContent = label;
  a.setAttribute('href', href || '#');
  a.onclick = handler;
}

function tkResetToastAction() {
  tkSetToastAction('View basket →', function (e) {
    e.preventDefault();
    document.getElementById('toast').classList.remove('show');
    openCartDrawer();
  }, '/cart');
}

// Build a shareable cart link from the live drawer rows and share it — native
// share sheet (→ WhatsApp etc.) on mobile, copy to clipboard on desktop.
//
// We deliberately do NOT use Shopify's /cart/variantId:qty permalink: that does
// a 302 to the plain /cart page (off-brand) and leaves a redirecting entry in
// history, which traps the Back button (back → permalink → redirect forward to
// /cart). Instead we share a normal branded URL (…/?tk_cart=…); tkRestoreSharedCart
// (below) reads the param on load, adds the items, cleans the URL with
// replaceState, and opens the drawer — no redirect, no back-trap.
function tkShareCart(btn) {
  // The gift is excluded: it is earned by crossing ₹1,200, not carried in a link.
  // Sharing it would add a PAID gift dish for the recipient (the restore below
  // adds plain lines, without the _free_gift property), which would also count
  // toward their own threshold and could earn them a second one.
  const rows = document.querySelectorAll('#cart-items-list .cart-item:not(.cart-item-removing):not(.cart-item--gift)');
  const parts = [];
  rows.forEach(function (r) {
    const vid = r.dataset.variant;
    const qty = tkSafeInt(r.dataset.qty, 1) || 1;
    if (vid) parts.push(vid + ':' + qty);
  });
  if (!parts.length) { showToast('Your cart is empty'); return; }
  const url = window.location.origin + '/?tk_cart=' + parts.join(',');
  const shareData = {
    title: 'My Thakar Kitchen cart',
    text: 'Here’s my Thakar Kitchen cart — tap to open it:',
    url: url
  };
  if (navigator.share) {
    navigator.share(shareData).catch(function (err) {
      if (err && err.name === 'AbortError') return; // user dismissed the sheet
      tkCopyCartLink(url);
    });
  } else {
    tkCopyCartLink(url);
  }
}

// On any page load, if the URL carries a shared cart (…?tk_cart=id:qty,…),
// reconcile it into the cart, strip the param from history (so Back/refresh
// won't re-run), and open the drawer to reveal the order.
//
// Reconcile is IDEMPOTENT — it only tops up what's missing, never doubles:
//   • item not in cart            → add it at the shared quantity
//   • item present, qty < shared  → bump it up to the shared quantity
//   • item present, qty >= shared → leave it untouched
// So opening your own saved link on a device that already has the cart is a
// no-op, while a family member's other items still merge in alongside.
(function tkRestoreSharedCart() {
  function run() {
    var params;
    try { params = new URLSearchParams(window.location.search); } catch (e) { return; }
    var raw = params.get('tk_cart');
    if (!raw) return;
    var items = raw.split(',').map(function (p) {
      var seg = p.split(':');
      var id = parseInt(seg[0], 10), q = parseInt(seg[1], 10);
      if (!id || !(q > 0)) return null;
      // Belt-and-braces against older links minted before the gift was excluded
      // from sharing: never restore the gift variant as a paid line.
      if (id === TK_GIFT_VARIANT_ID) return null;
      return { id: id, quantity: q };
    }).filter(Boolean);
    // Clean the param out of the URL up front so a refresh/back never re-runs.
    params.delete('tk_cart');
    var qs = params.toString();
    var clean = window.location.pathname + (qs ? '?' + qs : '') + window.location.hash;
    try { window.history.replaceState({}, '', clean); } catch (e) {}
    if (!items.length) return;

    var done = function (msg) {
      if (typeof openCartDrawer === 'function') openCartDrawer();
      if (typeof showToast === 'function') showToast(msg);
    };
    var fail = function () { if (typeof showToast === 'function') showToast('Couldn’t open the shared cart'); };

    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(tkJsonOk)
      .then(function (cart) {
        var have = {};
        (cart.items || []).forEach(function (li) {
          have[li.variant_id] = (have[li.variant_id] || 0) + li.quantity;
        });
        var adds = [], updates = {};
        items.forEach(function (it) {
          var cur = have[it.id] || 0;
          if (cur === 0) adds.push({ id: it.id, quantity: it.quantity });
          else if (cur < it.quantity) updates[it.id] = it.quantity;
        });
        if (!adds.length && !Object.keys(updates).length) {
          done('Your saved cart is ready'); // already have everything — no doubling
          return;
        }
        var chain = Promise.resolve();
        if (adds.length) {
          chain = chain.then(function () {
            return fetch('/cart/add.js', {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: adds })
            });
          });
        }
        if (Object.keys(updates).length) {
          chain = chain.then(function () {
            return fetch('/cart/update.js', {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ updates: updates })
            });
          });
        }
        chain.then(function () { done('Shared cart ready — here’s the order'); }).catch(fail);
      })
      .catch(fail);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }
})();

function tkCopyCartLink(url) {
  const ok = function () { showToast('Cart link copied — open it any time to restore your cart'); };
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(url).then(ok).catch(function () { tkFallbackCopy(url, ok); });
  } else {
    tkFallbackCopy(url, ok);
  }
}

// Generic share for a page URL (e.g. the PDP share button): native share sheet
// on mobile, copy-link + toast on desktop. Defaults to the current page.
function tkShareUrl(url, title) {
  url = url || window.location.href;
  const ok = function () { if (typeof showToast === 'function') showToast('Link copied — share it anywhere'); };
  const copy = function () {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(ok).catch(function () { tkFallbackCopy(url, ok); });
    } else {
      tkFallbackCopy(url, ok);
    }
  };
  if (navigator.share) {
    navigator.share({ title: title || document.title, url: url }).catch(function (err) {
      if (err && err.name === 'AbortError') return; // user dismissed the sheet
      copy();
    });
  } else {
    copy();
  }
}

function tkFallbackCopy(url, ok) {
  try {
    const ta = document.createElement('textarea');
    ta.value = url; ta.setAttribute('readonly', '');
    ta.style.position = 'absolute'; ta.style.left = '-9999px';
    document.body.appendChild(ta); ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
    ok();
  } catch (e) {
    showToast('Could not copy the cart link');
  }
}

function showToast(msg, type) {
  const t = document.getElementById('toast');
  if (!t) return;
  tkResetToastAction();
  const msgEl = document.getElementById('toast-msg');
  if (msgEl) msgEl.textContent = msg;
  else t.textContent = msg;
  t.className = 'toast show' + (type ? ' toast-' + type : '');
  clearTimeout(t._tid);
  t._tid = setTimeout(() => t.classList.remove('show'), 3500);
}

// /cart/add.js answers 422 (sold out, not enough stock, unknown variant) with a
// JSON body, so reading it without checking the status toasted "added to basket"
// for an item that never reached the cart. Reject instead, with Shopify's reason.
function tkAddResponse(r) {
  return r.json().catch(function() { return {}; }).then(function(body) {
    if (!r.ok) { var e = new Error(body.description || body.message || 'add failed'); e.tkReason = body.description || ''; throw e; }
    return body;
  });
}

// ── PDP recommendation card quick-add ────────────────────────────────────────
// Compact variant of addToCart for the "More Favorites" section: keeps ✓
// state brief enough for a small circular button (doesn't say "✓ Added").
function recoQuickAdd(variantId, btn) {
  var label = btn ? btn.textContent : '';
  if (btn) { btn.disabled = true; btn.classList.add('atc-loading'); }
  tkOptimisticAdd(1);
  tkPulseCartCount();
  _tkEnqueue(function() {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 }),
    })
      .then(tkAddResponse)
      .then(function(item) {
        tkLineFromAdd(item);
        if (btn) {
          btn.classList.remove('atc-loading');
          btn.classList.add('atc-success');
          btn.textContent = '✓';
          setTimeout(function() {
            btn.classList.remove('atc-success');
            btn.textContent = label || '+';
            btn.disabled = false;
          }, 1400);
        }
        showToast((item.product_title || 'Item') + ' added to basket', 'success');
        return fetch('/cart.js').then(tkJsonOk).then(function(cart) {
          tkSetLines(cart);
          var el = document.getElementById('nav-cart-count');
          var mel = document.getElementById('mobile-cart-count');
          if (el) el.textContent = cart.item_count;
          if (mel) mel.textContent = '(' + cart.item_count + ')';
          tkCartSubtotal = tkSafeNum(tkComputeFoodSubtotal(cart), tkCartSubtotal);
          tkCartHandleSet = new Set((cart.items || []).map(function(i) { return i.handle; }));
          renderCartProgress(tkCartSubtotal);
          // Adding never opens the drawer, and this was the only place the gift
          // could have been granted outside it — so an add that crossed ₹1,200
          // announced the reward and never actually put it in the cart. The cart
          // response above is straight from /cart.js, so it is authoritative.
          tkSyncFreeGift(cart);
        }).catch(function() {});
      })
      .catch(function(err) {
        tkOptimisticAdd(-1);
        if (btn) { btn.classList.remove('atc-loading'); btn.disabled = false; }
        showToast((err && err.tkReason) || 'Could not add. Please try again.', 'error');
      });
  });
}

// ── Cart count pulse ─────────────────────────────────────────────────────────
function tkPulseCartCount() {
  document.querySelectorAll('#nav-cart-count, #mobile-cart-count').forEach(function (el) {
    el.classList.remove('tk-count-pulse');
    requestAnimationFrame(function() { el.classList.add('tk-count-pulse'); });
  });
}

// ── Optimistic cart count increment ──────────────────────────────────────────
function tkOptimisticAdd(delta) {
  const el = document.getElementById('nav-cart-count');
  const mel = document.getElementById('mobile-cart-count');
  if (el) el.textContent = Math.max(0, (parseInt(el.textContent, 10) || 0) + delta);
  if (mel) {
    const n = Math.max(0, (parseInt(mel.textContent.replace(/\D/g, ''), 10) || 0) + delta);
    mel.textContent = '(' + n + ')';
  }
}

// ── Card steppers ───────────────────────────────────────────────────────────
// A dish in the basket shows "− 2 +" on its card in place of the price and Add (every card: the
// collection grid and rail, home, search, the product page's rail). Cards mark their price row
// with data-tk-v (the variant Add puts in) and, from the server's cart, data-q (how many are in
// the basket already), so a card is right at first paint. tkSetLines(cart) runs on every full
// cart the theme reads (an add, the drawer, the cart page, the idle pre-warm), so the cards follow
// whatever changes the basket. The free gift line (_free_gift) never counts. Taps change the
// number at once; the request waits 300 ms and goes through the cart queue (one per dish,
// coalesced), then renderDrawerCart brings the drawer, counts, progress and gift up to date.
// Minus at 1 removes the dish and the card goes back to Add.
var tkLines = {};          // variant id -> { q: quantity, key: cart line key }
var tkLastCart = null, tkLastCartAt = 0;   // the last whole cart read (idle pre-warm, adds, the drawer): the drawer opens on it
var _tkStepWant = {};      // variant id -> quantity the shopper asked for, not yet sent
var _tkStepTimer = {};
function tkSetLines(cart) {
  var m = {};
  ((cart && cart.items) || []).forEach(function (i) {
    if (tkIsGiftItem(i)) return;
    var v = String(i.variant_id || i.id);
    if (!m[v]) m[v] = { q: 0, key: i.key, keys: [] };
    m[v].q += tkSafeInt(i.quantity, 0);
    m[v].keys = m[v].keys.concat(i._keys || [i.key]);
  });
  // a dish Shopify split over two lines: the card's stepper sets the whole dish through its first key
  Object.keys(m).forEach(function (v) { if (m[v].keys.length > 1) tkKeyGroup[m[v].key] = m[v].keys; });
  tkLines = m;
  tkLastCart = cart; tkLastCartAt = Date.now();
  tkPaintSteppers();
}
window.tkSetLines = tkSetLines;
function tkStepQty(v) {
  v = String(v);
  if (_tkStepWant[v] != null) return _tkStepWant[v];
  return tkLines[v] ? tkLines[v].q : 0;
}
function tkStepName(row) {
  return (row.getAttribute('aria-label') || '').replace(/^Add\s+/, '').replace(/\s+to basket$/, '')
    || ((row.closest('.hp-card, .hc-card') || document).querySelector('.hp-card-t') || {}).textContent || 'This dish';
}
// one listener for every stepper, drawn by the server, by tkStepperFor or inside cards a page
// clones in later (the /search grid). Capture phase, so the collection's price row (itself an
// Add button with inline handlers) never sees the stepper's taps or keys.
document.addEventListener('click', function (e) {
  var b = e.target.closest && e.target.closest('.tk-step-b');
  var row = b && b.closest('[data-tk-v]');
  if (!row) return;
  e.stopPropagation(); e.preventDefault();
  var v = row.getAttribute('data-tk-v');
  if (!tkLines[v] && _tkStepWant[v] == null) { var q = parseInt(row.getAttribute('data-q'), 10) || 0; if (q > 0) tkLines[v] = { q: q, key: null }; }
  tkStep(v, +b.getAttribute('data-d'), tkStepName(row));
}, true);
document.addEventListener('keydown', function (e) {
  if (e.target.closest && e.target.closest('.tk-step')) e.stopPropagation();
}, true);
function tkStepperFor(row) {
  var st = row.querySelector('.tk-step');
  if (st) return st;
  var name = tkStepName(row);
  st = document.createElement('div');
  st.className = 'tk-step';
  st.setAttribute('role', 'group');
  st.setAttribute('aria-label', name + ' in your basket');
  st.innerHTML = '<button type="button" class="tk-step-b" data-d="-1" aria-label="One less ' + tkEsc(name) + '">'
    + '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 12h12"/></svg></button>'
    + '<output class="tk-step-n" aria-live="polite"></output>'
    + '<button type="button" class="tk-step-b" data-d="1" aria-label="One more ' + tkEsc(name) + '">'
    + '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 12h12M12 6v12"/></svg></button>';
  row.appendChild(st);
  return st;
}
// the collection's price row is itself a button (Add); while the stepper shows, it steps out of the tab order
function tkStepRow(row, on) {
  row.classList.toggle('is-in', on);
  if (row.getAttribute('role') === 'button') row.setAttribute('tabindex', on ? '-1' : '0');
}
function tkPaintSteppers() {
  document.querySelectorAll('[data-tk-v]').forEach(function (row) {
    var q = tkStepQty(row.getAttribute('data-tk-v'));
    if (q > 0) {
      var st = tkStepperFor(row);
      var n = st.querySelector('.tk-step-n');
      if (n.textContent !== String(q)) n.textContent = q;
      if (!row.classList.contains('is-in')) tkStepRow(row, true);
    } else if (row.classList.contains('is-in')) {
      tkStepRow(row, false);
    }
  });
}
function tkStep(v, d, name) {
  v = String(v);
  var want = Math.max(0, tkStepQty(v) + d);
  _tkStepWant[v] = want;
  tkOptimisticAdd(d);
  tkPaintSteppers();
  clearTimeout(_tkStepTimer[v]);
  _tkStepTimer[v] = setTimeout(function () {
    _tkEnqueue(function () {
      var target = _tkStepWant[v];
      if (target == null) return Promise.resolve();
      var post = function (url, body) { return fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }); };
      // the line's key sets the quantity exactly; a card drawn from the server's cart may not
      // know it yet, so read the cart first (never add on top of what is already there)
      var keyed = tkLines[v] && tkLines[v].key ? Promise.resolve(tkLines[v]) : fetch('/cart.js').then(tkJsonOk).then(function (c) {
        var keep = _tkStepWant[v]; tkSetLines(c); if (keep != null) _tkStepWant[v] = keep; return tkLines[v] || null;
      });
      var req = keyed.then(function (line) {
        target = _tkStepWant[v] != null ? _tkStepWant[v] : target;
        if (line && line.key) return tkChangeRes(line.key, target);
        if (target > 0) return post('/cart/add.js', { id: +v, quantity: target }).then(function (r) { if (!r.ok) return r; return fetch('/cart.js'); });
        return null;
      });
      return req.then(function (r) {
        if (!r) { delete _tkStepWant[v]; tkPaintSteppers(); return null; }
        if (!r.ok) return r.json().catch(function () { return {}; }).then(function (j) { var e = new Error('cart'); e.tkReason = j.description || j.message; throw e; });
        return r.json();
      }).then(function (cart) {
        if (!cart) return;
        if (_tkStepWant[v] === target) delete _tkStepWant[v];
        _tkFetchVersion++;
        if (tkCartPending()) { tkSetLines(tkNormalizeCart(cart)); tkReconcileSoon(); }
        else renderDrawerCart(cart);
        if (target === 0) showToast(name + ' removed from basket', 'success');
      }).catch(function (err) {
        delete _tkStepWant[v];
        showToast((err && err.tkReason) || 'Could not update your basket. Please try again.', 'error');
        tkReconcileSoon();
        tkPaintSteppers();
      });
    }, 'step:' + v);
  }, 300);
}
// an add's answer is the line itself: the card turns into its stepper without waiting for /cart.js
function tkLineFromAdd(item) {
  if (!item || !item.variant_id || tkIsGiftItem(item)) return;
  var v = String(item.variant_id), q = tkSafeInt(item.quantity, 1);
  if (!tkLines[v] || tkLines[v].q < q) tkLines[v] = { q: q, key: item.key };
  tkPaintSteppers();
}
// first paint: the server's cart already put data-q on the rows
document.addEventListener('DOMContentLoaded', function () {
  document.querySelectorAll('[data-tk-v][data-q]').forEach(function (row) {
    var v = row.getAttribute('data-tk-v'), q = parseInt(row.getAttribute('data-q'), 10) || 0;
    if (q > 0 && !tkLines[v]) tkLines[v] = { q: q, key: null };
  });
  tkPaintSteppers();
});

// ── Add to Cart (collection / PDP pages) ────────────────────────────────────
// Product-card footer is the ATC tap zone (heatmap fix). The row forwards the
// tap to its visual .atc pill so addToCart animates the pill (not the whole row,
// which would wipe the price). Guards against re-firing mid add/success.
function tkCardAdd(variantId, footEl) {
  var btn = footEl && footEl.querySelector('.atc');
  if (!btn || btn.classList.contains('atc-loading') || btn.classList.contains('atc-success')) return;
  addToCart(variantId, 1, btn);
}

function addToCart(variantId, qty, btn) {
  qty = qty || 1;
  const originalHTML = btn ? btn.innerHTML : null;
  const isIconBtn = btn ? !!btn.querySelector('.atc-icon') : false;
  if (btn) {
    btn.disabled = true;
    btn.classList.add('atc-loading');
    btn.textContent = '…';
  }
  tkOptimisticAdd(qty);
  tkPulseCartCount();
  _tkEnqueue(function() {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: qty }),
    })
      .then(tkAddResponse)
      .then(function(item) {
        tkLineFromAdd(item);
        if (btn) {
          btn.classList.remove('atc-loading');
          btn.classList.add('atc-success');
          btn.textContent = isIconBtn ? '✓' : '✓ Added';
          setTimeout(function() {
            btn.classList.remove('atc-success');
            btn.innerHTML = originalHTML;
            btn.disabled = false;
          }, 1500);
        }
        const name = (item && item.product_title) || null;
        showToast(name ? name + ' added to basket' : 'Added to basket', 'success');
        return fetch('/cart.js').then(tkJsonOk).then(tkAfterAddCart).catch(function() {});
      })
      .catch(function(err) {
        tkOptimisticAdd(-qty);
        if (btn) {
          btn.classList.remove('atc-loading');
          btn.innerHTML = originalHTML;
          btn.disabled = false;
        }
        showToast((err && err.tkReason) || 'Could not add to cart. Please try again.', 'error');
      });
  });
}

// After an add: the cart as /cart.js has it now (counts, steppers, the reward bar, the gift).
function tkAfterAddCart(cart) {
  tkSetLines(cart);
  const el = document.getElementById('nav-cart-count');
  const mel = document.getElementById('mobile-cart-count');
  if (el) el.textContent = cart.item_count;
  if (mel) mel.textContent = '(' + cart.item_count + ')';
  tkCartSubtotal = tkSafeNum(tkComputeFoodSubtotal(cart), tkCartSubtotal);
  tkCartHandleSet = new Set((cart.items || []).map(function(i) { return i.handle; }));
  renderCartProgress(tkCartSubtotal);
  // Adding a dish never opens the drawer, and this was the only place the gift
  // could have been granted outside it — so an add that crossed ₹1,200
  // announced the reward and never actually put it in the cart. The cart
  // response above is straight from /cart.js, so it is authoritative.
  tkSyncFreeGift(cart);
  // If the drawer is open but still showing skeleton/empty (no real rows),
  // render it now — fetchAndRenderCart was blocked by this mutation's lock.
  var drawer = document.getElementById('cart-drawer');
  var list = document.getElementById('cart-items-list');
  if (drawer && drawer.classList.contains('open') && list && !list.querySelector('.cart-item:not(.cart-item-removing)')) {
    _tkFetchVersion++;
    renderDrawerCart(cart);
  }
}

// ── Stores near a pincode ─────────────────────────────────────────────────────
// The Stores page (sections/main-stores.liquid) carries its store list as JSON (#hx-stores-data):
// name, city, address, phone, maps link and optional pincode prefixes. tkStoresFor(pin, stores)
// returns the city whose stores serve that pincode: a store's own prefixes first, else its city's
// known prefixes below (postal ranges for each city and its suburbs). tkLoadStores() reads the list
// from /pages/stores once per visit (the product page's pincode check uses it).
var TK_CITY_PINS = {
  ahmedabad: ['380', '3823', '3824', '38221'],
  rajkot: ['3600'],
  morbi: ['36364'],
  jamnagar: ['3610'],
  surat: ['395', '3941', '3942'],
  vapi: ['39619'],
  vadodara: ['390', '3910'],
  gandhinagar: ['3820'],
  bhavnagar: ['3640'],
  mumbai: ['400'],
  pune: ['411']
};
function tkSlug(s) { return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''); }
function tkStoresFor(pin, stores) {
  pin = String(pin || '');
  if (!/^[1-9]\d{5}$/.test(pin) || !stores || !stores.length) return null;
  var best = null, bestLen = 0;
  stores.forEach(function (st) {
    var own = String(st.pins || '').split(/[\s,]+/).filter(Boolean);
    var pre = own.length ? own : (TK_CITY_PINS[tkSlug(st.c)] || []);
    pre.forEach(function (p) { if (pin.indexOf(p) === 0 && p.length > bestLen) { best = st.c; bestLen = p.length; } });
  });
  if (!best) return null;
  return { city: best, slug: tkSlug(best), stores: stores.filter(function (st) { return st.c === best; }) };
}
var _tkStoresP = null;
function tkLoadStores() {
  if (_tkStoresP) return _tkStoresP;
  try { var c = sessionStorage.getItem('tk-stores'); if (c) return (_tkStoresP = Promise.resolve(JSON.parse(c))); } catch (e) {}
  _tkStoresP = fetch('/pages/stores', { credentials: 'same-origin' }).then(function (r) { return r.ok ? r.text() : ''; }).then(function (h) {
    var m = /<script type="application\/json" id="hx-stores-data">([\s\S]*?)<\/script>/.exec(h || '');
    var list = m ? JSON.parse(m[1]) : [];
    try { sessionStorage.setItem('tk-stores', JSON.stringify(list)); } catch (e) {}
    return list;
  }).catch(function () { _tkStoresP = null; return []; });
  return _tkStoresP;
}
window.tkStoresFor = tkStoresFor;
window.tkLoadStores = tkLoadStores;

// ── Delivery estimate for a pincode ──────────────────────────────────────────
// We dispatch within 24 hours and the courier takes about 48 more, so the major cities of
// Gujarat get their order in 3 days; smaller towns and villages in Gujarat a day or two more
// (4 to 5); the rest of India 4 to 5 business days. Gujarat is 360000-396999, less Daman,
// Silvassa (3962xx) and Diu (362520), which are union territories. Delivery is free across
// Gujarat (the Shopify delivery profile). Used by the product page's pincode check and the
// Stores page; keep the FAQ, policy and tracking pages in step.
var TK_GJ_CITY_PINS = {
  'Ahmedabad': ['380', '3823', '3824', '38221'], 'Gandhinagar': ['3820'],
  'Surat': ['395', '3941', '3942'], 'Vadodara': ['390', '3910'], 'Rajkot': ['3600'],
  'Bhavnagar': ['3640'], 'Jamnagar': ['3610'], 'Junagadh': ['3620'], 'Anand': ['3880', '3881'],
  'Nadiad': ['38700'], 'Morbi': ['36364'], 'Vapi': ['39619'], 'Valsad': ['3960'],
  'Navsari': ['39644', '39645'], 'Bharuch': ['3920'], 'Ankleshwar': ['3930'], 'Mehsana': ['3840'],
  'Patan': ['38426'], 'Palanpur': ['3850'], 'Himmatnagar': ['3830'], 'Gandhidham': ['3702'],
  'Bhuj': ['3700'], 'Porbandar': ['36057'], 'Surendranagar': ['3630'], 'Amreli': ['36560'],
  'Godhra': ['3890'], 'Veraval': ['36226'], 'Gondal': ['36031']
};
function tkDeliveryFor(pin) {
  pin = String(pin || '');
  if (!/^[1-9]\d{5}$/.test(pin)) return null;
  var n = +pin;
  var gj = n >= 360000 && n <= 396999 && !(n >= 396200 && n <= 396249) && n !== 362520;
  if (!gj) return { area: 'india', days: '4 to 5 business days', text: 'Delivers to ' + pin + ' in 4 to 5 business days.' };
  var city = null, len = 0;
  Object.keys(TK_GJ_CITY_PINS).forEach(function (c) {
    TK_GJ_CITY_PINS[c].forEach(function (p) { if (pin.indexOf(p) === 0 && p.length > len) { city = c; len = p.length; } });
  });
  if (city) return { area: 'city', city: city, days: '3 days', text: '3-day delivery to ' + city + ': we dispatch within 24 hours and the courier takes about 48. Free delivery across Gujarat.' };
  return { area: 'gujarat', days: '4 to 5 days', text: 'Delivers to ' + pin + ' in 4 to 5 days: we dispatch within 24 hours, and smaller towns take a day or two longer than the cities. Free delivery across Gujarat.' };
}
window.tkDeliveryFor = tkDeliveryFor;

// ── Festival delivery terms at checkout ─────────────────────────────────────────
// From the festival's first cut-off (the top bar's tkFest, Theme settings > Festival notice, with
// "shoppers agree to the delivery dates before checkout" on), a tap on any Checkout (the drawer's,
// /cart's two) opens a confirmation first: in the 'local' phase only fest_local_areas (Gujarat and
// Mumbai) get their order before the festival, in 'closed' the order is dispatched after the break.
// Checkout goes on only once the shopper ticks the box and agrees; the order then carries the cart
// attribute "Delivery terms" (what they agreed to and when). Agreeing is remembered for the visit, per
// phase. Razorpay Magic Checkout catches checkout taps on the document (bubbling), so this listens on
// window in the capture phase, stops the tap, and after the agreement clicks the same button again.
var TK_MUMBAI_PINS = ['400', '4011', '4012']; // Mumbai, Thane, Navi Mumbai; Mira-Bhayandar; Vasai-Virar
function tkFestLocal(pin) {
  var d = tkDeliveryFor(pin);
  if (!d) return '';
  if (d.area === 'india' && TK_MUMBAI_PINS.some(function (p) { return String(pin).indexOf(p) === 0; })) return 'mumbai';
  return d.area;
}
window.tkFestLocal = tkFestLocal;

function tkTermsFor() {
  var f = typeof window.tkFest === 'function' ? window.tkFest() : null;
  if (!f || !f.terms || (f.phase !== 'local' && f.phase !== 'closed')) return null;
  var n = f.name;
  if (f.phase === 'local') return {
    key: 'local|' + f.localDay,
    kick: n + ' delivery',
    title: 'Before ' + n + ', we deliver only to ' + f.areas + '.',
    lines: ['Orders placed now reach <strong>' + f.areas + '</strong> before ' + n + '.',
      'Anywhere else in India, your order <strong>arrives after ' + n + '</strong>.'],
    agree: 'I understand: outside ' + f.areas + ', my order arrives after ' + n + '.',
    note: 'Before ' + n + ' only ' + f.areas + '; elsewhere after ' + n
  };
  return {
    key: 'closed|' + f.ships,
    kick: n + ' break',
    title: 'Your order ships after ' + n + '.',
    lines: ['Our kitchen is resting for ' + n + '. We are still taking orders: they are <strong>dispatched ' + f.ships + '</strong>.',
      'Delivery then takes 3 to 5 days, depending on where you are.'],
    agree: 'I understand: my order is dispatched ' + f.ships + '.',
    note: 'Dispatched ' + f.ships
  };
}
function tkTermsAgreed(t) { try { return sessionStorage.getItem('tk-terms') === t.key; } catch (e) { return false; } }

var tkTermsPass = null;
function tkTermsOpen(t, btn) {
  var esc = function (x) { return String(x).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;'); };
  var old = document.getElementById('tk-terms'); if (old) old.remove();
  var oldS = document.getElementById('tk-terms-scrim'); if (oldS) oldS.remove();
  var scrim = document.createElement('div');
  scrim.className = 'tk-confirm-scrim tk-terms-scrim'; scrim.id = 'tk-terms-scrim';
  var m = document.createElement('div');
  m.className = 'tk-confirm tk-terms'; m.id = 'tk-terms';
  m.setAttribute('role', 'dialog'); m.setAttribute('aria-modal', 'true'); m.setAttribute('aria-labelledby', 'tk-terms-t');
  m.innerHTML = '<div class="tk-confirm-card">' +
    '<button type="button" class="tk-confirm-x" data-terms-x aria-label="Close">&#215;</button>' +
    '<span class="tk-terms-kick">' + esc(t.kick) + '</span>' +
    '<h3 class="tk-confirm-title" id="tk-terms-t">' + esc(t.title) + '</h3>' +
    '<p class="tk-confirm-body">' + t.lines.map(function (l) { return '<span class="tk-confirm-line">' + l + '</span>'; }).join('') + '</p>' +
    '<label class="tk-terms-ok"><input type="checkbox" id="tk-terms-cb"><span>' + esc(t.agree) + '</span></label>' +
    '<div class="tk-confirm-actions">' +
      '<button type="button" class="tk-confirm-btn tk-confirm-cancel" data-terms-x>Not now</button>' +
      '<button type="button" class="tk-confirm-btn tk-confirm-go tk-terms-go" disabled>Agree and check out</button>' +
    '</div></div>';
  document.body.appendChild(scrim); document.body.appendChild(m);
  var cb = m.querySelector('#tk-terms-cb'), go = m.querySelector('.tk-terms-go');
  var close = function () {
    m.classList.remove('open'); scrim.classList.remove('open');
    document.removeEventListener('keydown', onKey, true);
    setTimeout(function () { m.remove(); scrim.remove(); }, 350);
    if (btn && btn.focus) try { btn.focus({ preventScroll: true }); } catch (e) {}
  };
  var onKey = function (e) { if (e.key === 'Escape') { e.stopPropagation(); close(); } };
  document.addEventListener('keydown', onKey, true);
  scrim.addEventListener('click', close);
  m.addEventListener('click', function (e) { if (e.target === m || (e.target.closest && e.target.closest('[data-terms-x]'))) close(); });
  cb.addEventListener('change', function () { go.disabled = !cb.checked; });
  go.addEventListener('click', function () {
    if (!cb.checked || go.classList.contains('atc-loading')) return;
    go.classList.add('atc-loading'); go.textContent = 'One moment…';
    try { sessionStorage.setItem('tk-terms', t.key); } catch (e) {}
    var d = new Date(Date.now() + 5.5 * 36e5);
    var when = d.getUTCDate() + ' ' + ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][d.getUTCMonth()] + ' ' + ('0' + d.getUTCHours()).slice(-2) + ':' + ('0' + d.getUTCMinutes()).slice(-2) + ' IST';
    var done = false, proceed = function () {
      if (done) return; done = true;
      m.classList.remove('open'); scrim.classList.remove('open');
      setTimeout(function () { m.remove(); scrim.remove(); }, 350);
      document.removeEventListener('keydown', onKey, true);
      tkTermsPass = btn;
      btn.click();
    };
    // the agreement rides on the order (cart attribute), then checkout opens; never wait more than 2.5 s
    fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { 'Delivery terms': 'Agreed ' + when + ': ' + t.note } }) })
      .then(proceed, proceed);
    setTimeout(proceed, 2500);
  });
  requestAnimationFrame(function () { scrim.classList.add('open'); m.classList.add('open'); setTimeout(function () { try { cb.focus({ preventScroll: true }); } catch (e) {} }, 60); });
}
window.addEventListener('click', function (e) {
  var btn = e.target && e.target.closest && e.target.closest('a[href^="/checkout"], a[href*="/checkout?"], button[name="checkout"], input[name="checkout"], [data-tk-checkout]');
  if (!btn) return;
  if (tkTermsPass === btn) { tkTermsPass = null; return; }
  if (tkGapPass === btn) tkGapPass = null;
  else {
    var g = tkNextGift(window.tkCartSubtotal || 0);
    if (g && g.gap <= 250 && !tkGapSeen()) {
      e.preventDefault(); e.stopImmediatePropagation(); e.stopPropagation();
      tkGapOpen(btn, 'checkout');
      return;
    }
  }
  var t = tkTermsFor();
  if (!t || tkTermsAgreed(t)) return;
  e.preventDefault(); e.stopImmediatePropagation(); e.stopPropagation();
  tkTermsOpen(t, btn);
}, true);

// ── Thalis and combos: one tap puts every dish of a box in the basket ─────────
// The box's button (snippets/combo-card.liquid) carries its dishes as
// data-combo='[{"id":…,"quantity":…}]'; one /cart/add.js adds them all, the cards turn into
// steppers, the drawer opens on the full basket (and the gift, if the box crossed ₹1,200),
// and the box's name is kept on the order as the cart attribute "Boxes".
document.addEventListener('click', function(e) {
  var btn = e.target.closest && e.target.closest('[data-combo]');
  if (!btn) return;
  e.preventDefault();
  tkAddCombo(btn);
});
function tkAddCombo(btn) {
  if (btn.classList.contains('atc-loading') || btn.classList.contains('atc-success')) return;
  var items;
  try { items = JSON.parse(btn.getAttribute('data-combo')); } catch (e) { return; }
  if (!items || !items.length) return;
  var name = btn.getAttribute('data-combo-name') || 'Box';
  var label = btn.innerHTML;
  var qty = items.reduce(function(s, i) { return s + (i.quantity || 1); }, 0);
  function restore() { btn.classList.remove('atc-loading', 'atc-success'); btn.innerHTML = label; btn.disabled = false; }
  btn.disabled = true;
  btn.classList.add('atc-loading');
  btn.textContent = '…';
  tkOptimisticAdd(qty);
  tkPulseCartCount();
  _tkEnqueue(function() {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items }),
    })
      .then(tkAddResponse)
      .then(function(res) {
        ((res && res.items) || []).forEach(tkLineFromAdd);
        btn.classList.remove('atc-loading');
        btn.classList.add('atc-success');
        btn.textContent = '✓ Added';
        setTimeout(restore, 1800);
        return fetch('/cart.js').then(tkJsonOk).then(function(cart) {
          tkAfterAddCart(cart);
          openCartDrawer();
          var had = String((cart.attributes || {}).Boxes || '');
          var boxes = had ? had + ', ' + name : name;
          fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ attributes: { Boxes: boxes } }) }).catch(function() {});
        }).catch(function() {});
      })
      .catch(function(err) {
        tkOptimisticAdd(-qty);
        restore();
        showToast((err && err.tkReason) || 'Could not add the ' + name + '. Please try again.', 'error');
        tkReconcileSoon();
      });
  });
}

// ── Nav scroll shrink ────────────────────────────────────────────────────────
// The header animates a large height delta (140px -> 64px) over .28s. Near the
// top of the page, mobile browsers report jittery scrollY (URL-bar collapse,
// overscroll/momentum bounce) that can cross the shrink/expand thresholds
// several times a second. Without a lock, each crossing restarts the .28s
// transition mid-flight, which is what reads as the header "fluctuating."
// A time-based lock — no new toggle until the current transition has had time
// to finish — guarantees at most one clean transition per real scroll reversal.
(function () {
  const hdr = document.getElementById('site-header');
  if (!hdr) return;
  let ticking = false;
  let isScrolled = false;
  let lastToggle = 0;
  const SHRINK_AT = 96;
  const EXPAND_AT = 16;
  const LOCK_MS = 320; // slightly longer than the .28s CSS transition
  function update() {
    const now = performance.now();
    if (now - lastToggle < LOCK_MS) { ticking = false; return; }
    const y = Math.max(0, window.scrollY);
    if (!isScrolled && y > SHRINK_AT) { isScrolled = true; hdr.classList.add('scrolled'); lastToggle = now; }
    else if (isScrolled && y < EXPAND_AT) { isScrolled = false; hdr.classList.remove('scrolled'); lastToggle = now; }
    ticking = false;
  }
  if (window.scrollY > SHRINK_AT) { isScrolled = true; hdr.classList.add('scrolled'); }
  // Two 1px markers at 96px and 16px from the top of the page tell us when they pass under the
  // top of the screen, so no script runs on every scroll frame (the old scroll handler read
  // scrollY each frame and forced the browser to finish style and layout first).
  if ('IntersectionObserver' in window) {
    var mark = function (y) { var m = document.createElement('div'); m.setAttribute('aria-hidden', 'true'); m.style.cssText = 'position:absolute;left:0;top:' + y + 'px;width:1px;height:1px;pointer-events:none;visibility:hidden'; document.body.appendChild(m); return m; };
    var past = { s: window.scrollY > SHRINK_AT, e: window.scrollY >= EXPAND_AT };
    var io = new IntersectionObserver(function (es) {
      es.forEach(function (en) { past[en.target === mShrink ? 's' : 'e'] = !en.isIntersecting && en.boundingClientRect.top < 0; });
      var now = performance.now();
      var want = isScrolled ? past.e : past.s;      // shrink past 96px, grow back only above 16px
      if (want !== isScrolled) {
        // after a recent change, wait out the header's transition, then decide again from where the page is now
        var apply = function () { var w = isScrolled ? past.e : past.s; if (w === isScrolled) return; isScrolled = w; hdr.classList.toggle('scrolled', w); lastToggle = performance.now(); };
        if (now - lastToggle < LOCK_MS) setTimeout(apply, LOCK_MS - (now - lastToggle)); else apply();
      }
    });
    var mShrink = mark(SHRINK_AT), mExpand = mark(EXPAND_AT);
    io.observe(mShrink); io.observe(mExpand);
  } else {
    window.addEventListener('scroll', function () {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
  }
  window.addEventListener('pageshow', function (e) { if (e.persisted) update(); });
})();

// ── Logo easter egg: 5 quick taps reveals a little blessing ─────────────────
// The logo is a real link (goes home), so we can't just count clicks — a
// single click would navigate away before a second could ever land. Instead
// every click is held for one shared window: if nothing else happens, it
// navigates like normal (with a small, deliberate delay); if another tap
// lands inside that window, the hold is renewed and the counter grows. Five
// taps within that rhythm cancels the navigation and shows the surprise instead.
(function () {
  var logo = document.querySelector('.brand');
  if (!logo) return;
  var count = 0;
  var navTimer = null;
  var lastClick = 0;
  var WINDOW_MS = 350; // both the "still tapping quickly" window and the normal-click nav delay

  function burst() {
    var rect = logo.getBoundingClientRect();
    var cx = rect.left + rect.width / 2, cy = rect.top + rect.height / 2;
    var colors = ['#E8581F', '#F2B829', '#161B4A', '#6AA028'];
    for (var i = 0; i < 10; i++) {
      var s = document.createElement('span');
      s.className = 'tk-egg-spark';
      var angle = (Math.PI * 2 * i) / 10 + Math.random() * 0.4;
      var dist = 34 + Math.random() * 22;
      s.style.left = cx + 'px';
      s.style.top = cy + 'px';
      s.style.background = colors[i % colors.length];
      s.style.setProperty('--tx', Math.cos(angle) * dist + 'px');
      s.style.setProperty('--ty', Math.sin(angle) * dist + 'px');
      document.body.appendChild(s);
      (function (el) { setTimeout(function () { el.remove(); }, 700); })(s);
    }
  }

  logo.addEventListener('click', function (e) {
    e.preventDefault();
    var now = Date.now();
    count = (now - lastClick > WINDOW_MS) ? 1 : count + 1;
    lastClick = now;
    clearTimeout(navTimer);

    if (count >= 5) {
      count = 0;
      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) burst();
      if (typeof showToast === 'function') {
        showToast('You found it! Ba’s blessing: may your thali always be full. \u{1F35B}', 'fun');
      }
      return;
    }
    var href = logo.getAttribute('href');
    navTimer = setTimeout(function () { window.location.href = href; }, WINDOW_MS);
  });
})();

// ── bfcache: reset overlay state on page hide / restore ──────────────────────
(function () {
  function resetOverlays() {
    document.body.style.overflow = '';
    document.body.classList.remove('tk-cart-open');
    ['cart-overlay', 'cart-drawer', 'mobile-scrim', 'mobile-drawer'].forEach(function (id) {
      var el = document.getElementById(id);
      if (el) el.classList.remove('open');
    });
    var drawer = document.getElementById('cart-drawer');
    if (drawer) {
      drawer.classList.remove('tk-dragging');
      drawer.style.transform = '';
      drawer.style.transition = '';
    }
  }
  window.addEventListener('pagehide', resetOverlays);
  window.addEventListener('pageshow', function (e) { if (e.persisted) resetOverlays(); });
})();

// ── Filter chips (homepage featured section) ─────────────────────────────────
function setFilter(btn, tag, secId) {
  const bar = secId ? document.getElementById('filter-bar-' + secId) : btn.closest('.filter-chips');
  if (bar) bar.querySelectorAll('.filter-chip').forEach(c => c.classList.remove('active', 'chip-active'));
  btn.classList.add('chip-active');
  const gridId = secId ? 'products-grid-' + secId : 'shop-products-grid';
  const grid = document.getElementById(gridId);
  if (!grid) return;
  const cards = grid.querySelectorAll('.pcard-wrap');
  const want = tag.toLowerCase();
  let visible = 0;
  cards.forEach(c => {
    const t = (c.dataset.tags || '').toLowerCase();
    const show = want === 'all' || t.includes(want);
    const slot = c.closest('.swiper-slide') || c;
    slot.style.display = show ? '' : 'none';
    if (show) visible++;
  });
  const noResId = secId ? 'no-results-' + secId : 'shop-no-results';
  const noRes = document.getElementById(noResId);
  if (noRes) noRes.style.display = visible === 0 ? 'block' : 'none';
  if (grid.swiper) { grid.swiper.update(); grid.swiper.slideTo(0, 0); }
}

// ── Scroll-in section reveal ─────────────────────────────────────────────────
(function () {
  var SEL = '.featured-section, .testi-section, .why-section, .metrics-section, .trust-section, .sp-hero, .cuisine-grid';
  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (e.isIntersecting) { e.target.classList.add('tk-visible'); io.unobserve(e.target); }
    });
  }, { threshold: 0.08, rootMargin: '0px 0px -30px 0px' });
  function init() {
    document.querySelectorAll(SEL).forEach(function (el) { el.classList.add('tk-reveal'); io.observe(el); });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();

// ── Idle pre-warm: pool + cart state ─────────────────────────────────────────
// Warm both the products pool and the cart state on page load (when the main
// thread is idle) so the FIRST cart open has data ready instead of waiting on
// the network — eliminating the first-open stall.
(function () {
  function prewarm() {

    // Silently pre-populate cart state. Don't touch the DOM — the drawer is
    // closed — just warm the scalar state so the first open reconciles
    // immediately rather than waiting for the fetch.
    var epoch = _tkWriteEpoch;
    fetch('/cart.js')
      .then(tkJsonOk)
      .then(function (rawCart) {
        // a tap since it was sent: that write's answer is newer than this read
        if (epoch !== _tkWriteEpoch || tkCartBusy()) return;
        var cart = tkNormalizeCart(rawCart);
        tkSetLines(cart);
        tkCartSubtotal = tkSafeNum(tkComputeFoodSubtotal(cart), 0);
        window.tkCartSubtotal = tkCartSubtotal;
        window.dispatchEvent(new CustomEvent('cart:subtotal'));
        tkCartHandleSet = new Set(cart.items.map(function (i) { return i.handle; }));
        var countEl = document.getElementById('nav-cart-count');
        if (countEl) countEl.textContent = tkSafeInt(cart.item_count, 0);
        // Safety net: reconcile the gift on any page load, so a cart that
        // qualifies gets it however it came to qualify — including carts left
        // over from before the add paths granted it, and the cart page, which
        // has no other trigger until a quantity is touched.
        tkSyncFreeGift(cart);
      })
      .catch(function () {});
  }

  // back to a page the browser kept (the back/forward cache): the basket may have changed on the
  // pages in between, so the count, the card steppers and the delivery bars catch up
  window.addEventListener('pageshow', function (e) { if (e.persisted) prewarm(); });
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(prewarm, { timeout: 4000 });
  } else {
    setTimeout(prewarm, 2000);
  }
  // The products pool (15 KB, for the drawer's suggestions) waits until the page has loaded and
  // the visitor first scrolls or taps, or 5 s: on a slow phone it used to share the bandwidth with
  // the hero photo. Opening the basket fetches it at once anyway.
  function pool() {
    var done = false;
    function go() { if (done) return; done = true; tkFetchRecsPool(); }
    ['scroll', 'pointerdown', 'keydown', 'touchstart'].forEach(function (t) { window.addEventListener(t, go, { once: true, passive: true }); });
    setTimeout(go, 5000);
  }
  if (document.readyState === 'complete') pool(); else window.addEventListener('load', pool);
})();

// ── Video reels: art-directed autoplay-muted-loop ───────────────────────────
// Each [data-vid-reel] may hold a desktop (16:9) and a mobile (9:16) <video>.
// We play ONLY the cut that's visible at the current breakpoint and pause the
// other, so we never download/decode both. Playback also pauses when the reel
// is scrolled out of view. Respects prefers-reduced-motion, lite mode and slow or
// metered connections (shows controls and does not autoplay instead).
(function () {
  function init() {
    var reels = document.querySelectorAll('[data-vid-reel]');
    if (!reels.length) return;

    // No autoplay with reduced motion, in lite mode, or on a slow or metered connection (data
    // saver, 2G, 3G): the reel is several megabytes, so it waits for a tap on its controls there.
    var reduce = (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) ||
      (window.tkMotion && !window.tkMotion()) || !!window.tkLowData;

    // The visible cut is the frame that is actually rendered (offsetParent set).
    function visibleVideo(reel) {
      var frames = reel.querySelectorAll('.vid-reel-frame');
      for (var i = 0; i < frames.length; i++) {
        if (frames[i].offsetParent !== null) return frames[i].querySelector('video');
      }
      return reel.querySelector('video');
    }

    reels.forEach(function (reel) {
      var inView = false;

      function sync() {
        var active = visibleVideo(reel);
        // Pause every cut that isn't the active one.
        reel.querySelectorAll('video').forEach(function (v) {
          if (v !== active) { try { v.pause(); } catch (e) {} }
        });
        if (!active) return;

        if (reduce) {
          // Let the visitor choose to play; never autoplay.
          active.setAttribute('controls', '');
          return;
        }
        if (inView) {
          if (active.preload === 'none') active.preload = 'metadata';
          var p = active.play();
          if (p && p.catch) p.catch(function () {});
        } else {
          try { active.pause(); } catch (e) {}
        }
      }

      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          inView = entries[0].isIntersecting;
          sync();
        }, { threshold: 0.25 }).observe(reel);
      } else {
        inView = true;
        sync();
      }

      // Re-evaluate which cut is visible after a breakpoint/orientation change.
      var rt;
      window.addEventListener('resize', function () {
        clearTimeout(rt);
        rt = setTimeout(sync, 200);
      });
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();

// ── Video feature: step-progress controller ──────────────────────────────────
// Listens to the active video's timeupdate and advances [data-vfeat-steps]
// indicators. Divides video duration evenly by step count. Integrates with the
// art-direction swap — always reads from the currently-visible <video>.
(function () {
  function init() {
    var containers = document.querySelectorAll('[data-vfeat-parent]');
    if (!containers.length) return;

    containers.forEach(function (container) {
      var stepsEl = container.querySelector('[data-vfeat-steps]');
      if (!stepsEl) return;

      var steps = stepsEl.querySelectorAll('[data-vfeat-step]');
      var fill  = stepsEl.querySelector('[data-vfeat-fill]');
      var stepCount = steps.length;
      if (!stepCount) return;

      var reel = container.querySelector('[data-vid-reel]');
      if (!reel) return;

      var currentStep = -1;
      var boundVideo = null;

      function visibleVideo() {
        var frames = reel.querySelectorAll('.vid-reel-frame');
        for (var i = 0; i < frames.length; i++) {
          if (frames[i].offsetParent !== null) return frames[i].querySelector('video');
        }
        return reel.querySelector('video');
      }

      function setActive(idx) {
        if (idx === currentStep) return;
        currentStep = idx;
        for (var i = 0; i < steps.length; i++) {
          steps[i].classList.toggle('is-active', i === idx);
          steps[i].classList.toggle('is-done', i < idx);
        }
      }

      function updateFill(progress) {
        if (fill) fill.style.transform = 'scaleX(' + Math.min(progress, 1) + ')';
      }

      function onTimeUpdate() {
        var vid = this;
        var dur = vid.duration;
        if (!dur || !isFinite(dur)) return;

        var t = vid.currentTime;
        var segLen = dur / stepCount;
        var idx = Math.min(Math.floor(t / segLen), stepCount - 1);

        setActive(idx);

        var fillProgress;
        if (stepCount <= 1) {
          fillProgress = t / dur;
        } else {
          var dotPos = idx / (stepCount - 1);
          var nextDotPos = Math.min((idx + 1) / (stepCount - 1), 1);
          var segProgress = (t - idx * segLen) / segLen;
          fillProgress = dotPos + (nextDotPos - dotPos) * segProgress;
        }
        updateFill(fillProgress);
      }

      function bind() {
        var vid = visibleVideo();
        if (vid === boundVideo) return;
        if (boundVideo) boundVideo.removeEventListener('timeupdate', onTimeUpdate);
        boundVideo = vid;
        if (vid) vid.addEventListener('timeupdate', onTimeUpdate);
        setActive(0);
        updateFill(0);
      }

      bind();

      var rt;
      window.addEventListener('resize', function () {
        clearTimeout(rt);
        rt = setTimeout(bind, 200);
      });

      reel.addEventListener('seeking', function () {
        var vid = visibleVideo();
        if (vid && vid.currentTime < 0.1) {
          setActive(0);
          updateFill(0);
        }
      });
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  if (window.Shopify && Shopify.designMode) document.addEventListener('shopify:section:load', init);
})();

// ── Tap to the next page: acknowledge at once, preview the destination while it loads ──────────
// People decide within a tenth of a second whether a tap "took"; a frozen screen after that reads
// as a miss (they tap again or go back). So: the tapped card presses in at once (and Android gives
// a 6 ms tick). If the next page has not appeared 150 ms later, a preview of it covers the screen,
// built from what the card already knows: for a dish, its photo grows from the card into the arch
// the dish page shows it in, with its name, rating and price where the page puts them; for a
// collection, the page's frame. Everything sits where the real page will, so when it arrives
// (the 0.2 s crossfade, house.css @view-transition) nothing moves. Rules: never for waits under
// 150 ms, never for a tap something else handled (drawer links, new tabs, downloads, same page),
// gone when the page comes back from the back/forward cache, and after 4 s it steps aside with a
// "Still loading…" note so the old page is usable again. Back on the grid, the card you opened
// glows once. Styles: house.css "TAP TO NEXT PAGE".
(function () {
  var DELAY = 150, GIVE_UP = 4000;
  var showTimer = 0, giveTimer = 0, pv = null, slow = null;
  var reduced = function () { return !(window.tkMotion ? window.tkMotion() : !matchMedia('(prefers-reduced-motion: reduce)').matches); };
  var esc = function (v) { return String(v == null ? '' : v).replace(/[&<>"']/g, function (c) { return '&#' + c.charCodeAt(0) + ';'; }); };

  function cleanup() {
    clearTimeout(showTimer); clearTimeout(giveTimer);
    if (pv) { pv.remove(); pv = null; }
    if (slow) { slow.remove(); slow = null; }
    document.documentElement.classList.remove('tk-pv-on');
    document.querySelectorAll('.tk-tapped').forEach(function (el) { el.classList.remove('tk-tapped'); });
  }

  function cardOf(a) { return a.closest('.hp-card, .hc-card, .hh-slide, .cart-rec-card, .ss-row'); }
  function txt(root, sel) { var el = root && root.querySelector(sel); return el ? el.textContent.replace(/\s+/g, ' ').trim() : ''; }

  function dishData(a, card) {
    var img = card && card.querySelector('img');
    var src = img && img.complete && img.naturalWidth >= 120 ? (img.currentSrc || img.src) : '';
    var fill = !!src && img.naturalWidth / (img.naturalHeight || 1) <= 0.92;   // a 4:5 photograph fills the frame, a square pack shot sits in it   // srcset images report a density-corrected width (a 240px file on a 2x card says 171)
    var pic = card && card.querySelector('.hp-card-pic, .hh-slide-pic, .cart-rec-img, img');
    var name = txt(card, '.hp-card-t, .hh-slide-name, .ss-title, .cart-rec-name, .hc-card-t') || (a.textContent || '').replace(/\s+/g, ' ').trim();
    if (name.length > 60) name = '';
    var r = /([0-9]\.[0-9])\s*\(([0-9,]+)\)/.exec(txt(card, '.hp-card-r, .cart-rec-rating'));
    return {
      src: src, fill: fill, from: pic ? pic.getBoundingClientRect() : null, name: name,
      rating: r ? r[1] : '', count: r ? r[2] : '',
      price: txt(card, '.hp-card-row b, .hh-slide-price, .ss-price, .cart-rec-price'),
      per: txt(card, '.hp-card-per')
    };
  }

  function topHTML() {
    var ann = document.querySelector('.announcement-bar, #shopify-section-announcement-bar');
    var hdr = document.getElementById('site-header');
    var h = (ann ? ann.outerHTML : '') + (hdr ? hdr.outerHTML : '');
    return h.replace(/\sid="([^"]*)"/g, function (m, id) { return id === 'hamburger' ? ' data-pv-burger' : ''; }).replace(/\saria-current="[^"]*"/g, '').replace(/\sscrolled\b/g, '');
  }

  function dishHTML(d) {
    var photo = d.src ? '<img src="' + esc(d.src) + '" alt=""' + (d.fill ? ' class="is-fill"' : '') + '>' : '';
    var stars = d.rating ? '<i>★★★★★</i><b>' + esc(d.rating) + '</b><span>' + esc(d.count) + ' reviews</span>' : '<span class="tk-pv-bar tk-pv-sheen" style="width:58%"></span>';
    return '<div class="tk-pv-dish">'
      + '<span class="tk-pv-crumbs"><span class="tk-pv-bar" style="width:220px;height:10px"></span></span>'
      + '<div class="tk-pv-gal"><div class="tk-pv-thumbs"><i></i><i></i><i></i><i></i></div>'
      + '<div class="tk-pv-arch tk-pv-main">' + photo + '</div><div class="tk-pv-arch"></div></div>'
      + '<div class="tk-pv-buy"><div class="tk-pv-id"><span class="tk-pv-kick"><span class="tk-pv-bar tk-pv-sheen" style="width:46%"></span></span>'
      + (d.name ? '<p class="tk-pv-title">' + esc(d.name) + '</p>' : '<span class="tk-pv-bar tk-pv-sheen" style="height:34px;width:78%"></span>')
      + '<span class="tk-pv-rate">' + stars + '</span></div>'
      + '<div class="tk-pv-price"><span>' + (d.price ? '<b>' + esc(d.price) + '</b>' + (d.per ? '<small>' + esc(d.per) + '</small>' : '') : '<span class="tk-pv-bar tk-pv-sheen" style="height:26px;width:40%"></span>') + '</span>'
      + '<span class="tk-pv-bar" style="width:72%;height:10px"></span></div>'
      + '<div class="tk-pv-packs"><span class="tk-pv-bar" style="width:34%;height:12px;margin-top:4px"></span><div><span class="tk-pv-ghost tk-pv-sheen"></span><span class="tk-pv-ghost tk-pv-sheen"></span><span class="tk-pv-ghost tk-pv-sheen"></span></div></div>'
      + '<div class="tk-pv-cta"><span class="tk-pv-ghost"></span><span class="tk-pv-btn">Add to basket</span></div>'
      + '</div></div>';
  }
  function stickyHTML(d) {
    var photo = d.src ? '<img src="' + esc(d.src) + '" alt="">' : '';
    return '<div class="tk-pv-sticky"><span class="tk-pv-th">' + photo + '</span><span>' + (d.price ? '<b>' + esc(d.price) + '</b>' : '') + '<small>' + esc(d.name) + '</small></span><span class="tk-pv-btn">Add to basket</span></div>';
  }

  function collHTML() {
    var cards = '';
    for (var i = 0; i < 8; i++) cards += '<span class="tk-pv-card"><span class="tk-pv-sheen"></span><span class="tk-pv-bar" style="width:40%;height:10px"></span><span class="tk-pv-bar" style="width:82%;height:16px"></span><span class="tk-pv-bar" style="width:56%;height:10px"></span><span class="tk-pv-bar" style="width:100%;height:40px"></span></span>';
    return '<div class="tk-pv-coll"><div class="tk-pv-hero"><span class="tk-pv-bar" style="width:26%;height:14px"></span><span class="tk-pv-bar tk-pv-sheen" style="width:70%;height:28px"></span><span class="tk-pv-bar" style="width:84%;height:12px"></span><span class="tk-pv-search"></span></div>'
      + '<div class="tk-pv-cbar"><i style="width:62px"></i><i style="width:104px"></i><i style="width:124px"></i><i style="width:44px;margin-left:auto"></i></div>'
      + '<div class="tk-pv-cats"><i style="width:196px"></i><i style="width:72px"></i><i style="width:124px"></i><i style="width:90px"></i></div>'
      + '<div class="tk-pv-grid">' + cards + '</div></div>';
  }

  function show(kind, data, ev) {
    if (ev.defaultPrevented || document.hidden) { cleanup(); return; }
    // tapped from the search panel or the menu: they step aside so the preview is what shows
    var so = document.getElementById('search-overlay'), md = document.getElementById('mobile-drawer');
    if (so && so.classList.contains('is-open') && window.closeSearch) closeSearch();
    if (md && md.classList.contains('open') && window.closeMobile) closeMobile();
    pv = document.createElement('div');
    pv.className = 'tk-pv' + (reduced() ? '' : ' is-in');
    pv.setAttribute('aria-hidden', 'true');
    pv.innerHTML = ''
      + '<div class="tk-pv-top">' + topHTML() + '</div><div class="tk-pv-body hp-wrap">' + (kind === 'dish' ? dishHTML(data) : collHTML()) + '</div>'
      + (kind === 'dish' ? stickyHTML(data) : '');
    document.body.appendChild(pv);
    document.documentElement.classList.add('tk-pv-on');
    // the dish's picture grows out of the card into its place in the arch (transform only)
    var main = pv.querySelector('.tk-pv-main');
    if (main && data.from && data.from.width && !reduced() && main.animate) {
      var to = main.getBoundingClientRect(), f = data.from;
      if (to.width) {
        var sx = f.width / to.width, sy = f.height / to.height;
        main.animate([{ transform: 'translate(' + (f.left - to.left) + 'px,' + (f.top - to.top) + 'px) scale(' + sx + ',' + sy + ')' }, { transform: 'none' }], { duration: 340, easing: 'cubic-bezier(.2,.7,.2,1)' });
      }
    }
    giveTimer = setTimeout(function () {
      if (pv) { pv.remove(); pv = null; }
      document.documentElement.classList.remove('tk-pv-on');
      document.querySelectorAll('.tk-tapped').forEach(function (el) { el.classList.remove('tk-tapped'); });
      slow = document.createElement('div'); slow.className = 'tk-pv-slow'; slow.setAttribute('role', 'status'); slow.textContent = 'Still loading…';
      document.body.appendChild(slow);
      setTimeout(function () { if (slow) { slow.remove(); slow = null; } }, 6000);
    }, GIVE_UP - DELAY);
  }

  document.addEventListener('click', function (e) {
    if (e.defaultPrevented || e.button > 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var a = e.target.closest && e.target.closest('a[href]');
    if (!a || (a.target && a.target !== '_self') || a.hasAttribute('download')) return;
    var u; try { u = new URL(a.href, location.href); } catch (x) { return; }
    if (u.origin !== location.origin || (u.pathname === location.pathname && u.search === location.search)) return;
    if (/^\/(cart|checkout|checkouts|account|apps|challenge)(\/|$)/.test(u.pathname)) return;
    cleanup();
    var card = cardOf(a);
    if (card) card.classList.add('tk-tapped');
    try { if (navigator.vibrate && !reduced()) navigator.vibrate(6); } catch (x) {}
    try { sessionStorage.setItem('tk-tap', JSON.stringify({ from: location.pathname + location.search, to: u.pathname })); } catch (x) {}
    var kind = /^\/products\/[^/]+\/?$/.test(u.pathname) ? 'dish' : /^\/collections\/[^/]+\/?$/.test(u.pathname) ? 'coll' : '';
    if (!kind) { giveTimer = setTimeout(cleanup, GIVE_UP); return; }   // other pages: the press alone, undone if the page never changes
    var data = kind === 'dish' ? dishData(a, card) : null;
    showTimer = setTimeout(function () { show(kind, data, e); }, DELAY);
  });

  // back on the page you left: clear the preview, and let the card you opened glow once
  function welcomeBack() {
    var t; try { t = JSON.parse(sessionStorage.getItem('tk-tap') || 'null'); } catch (x) {}
    if (!t || t.from !== location.pathname + location.search) return;
    var a = [].slice.call(document.querySelectorAll('a[href]')).find(function (l) { try { return new URL(l.href).pathname === t.to && l.getClientRects().length; } catch (x) { return false; } });
    var card = a && (cardOf(a) || a);
    if (card && !reduced()) { card.classList.remove('tk-was-here'); requestAnimationFrame(function () { card.classList.add('tk-was-here'); }); setTimeout(function () { card.classList.remove('tk-was-here'); }, 2000); }
    try { sessionStorage.removeItem('tk-tap'); } catch (x) {}
  }
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) { cleanup(); welcomeBack(); return; }
    var n = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
    if (n && n.type === 'back_forward') welcomeBack();
  });
})();

// ── Fetch ahead: dish pages already on screen ───────────────────────────────────────────────────
// Once scrolling stops, the dish pages whose cards are on screen are fetched in the background
// (speculation rules, prefetch only, at most 4 per page), so a tap usually finds the page already
// here and skips Shopify's build and the download. Only on a fast connection (4G, no data saver;
// tkLowData excludes 2G/3G), only where the browser has speculation rules, and the basket,
// checkout and account pages never. The press-and-hover rule in the layout covers the rest.
(function () {
  var LIMIT = 4;
  var c = navigator.connection || {};
  if (!(window.HTMLScriptElement && HTMLScriptElement.supports && HTMLScriptElement.supports('speculationrules'))) return;
  if (window.tkLowData || c.saveData || (c.effectiveType && c.effectiveType !== '4g')) return;
  var done = new Set([location.pathname]), seen = new Map(), timer = 0, io;
  function add(urls) {
    var s = document.createElement('script');
    s.type = 'speculationrules';
    s.textContent = JSON.stringify({ prefetch: [{ source: 'list', urls: urls }] });
    document.head.appendChild(s);
  }
  function pick() {
    if (done.size - 1 >= LIMIT) { io && io.disconnect(); return; }
    var urls = [];
    [...seen.entries()].sort(function (a, b) { return a[1] - b[1]; }).forEach(function (e) {
      if (urls.length + done.size - 1 < LIMIT && !done.has(e[0])) { urls.push(e[0]); done.add(e[0]); }
    });
    if (urls.length) add(urls);
  }
  function settle() { clearTimeout(timer); timer = setTimeout(pick, 600); }
  function start() {
    io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        var path; try { path = new URL(en.target.href).pathname; } catch (x) { return; }
        if (en.isIntersecting) seen.set(path, en.boundingClientRect.top); else seen.delete(path);
      });
      settle();
    }, { threshold: 0.6 });
    document.querySelectorAll('a.hp-card-t[href*="/products/"], a.hh-slide-name[href*="/products/"], .hc-card a.hp-card-t[href*="/products/"]').forEach(function (a) {
      try { if (new URL(a.href).origin === location.origin) io.observe(a); } catch (x) {}
    });
    window.addEventListener('scroll', settle, { passive: true });
  }
  window.addEventListener('load', function () { setTimeout(start, 2000); });
})();

// ── Decoration rests while the page moves ───────────────────────────────────────────────────────
// Drawings with moving parts inside them (the home kitchen scenes' steam, flames, toran and lamp;
// About's chulha, steam and ladle) are repainted by the main thread on every frame they move, and
// during a scroll that work competes with the scroll and drops frames. html.tk-scrolling pauses
// every animation inside an SVG while the page scrolls, and they carry on from where they were
// 180 ms after it stops (house.css). The class changes once per gesture, not per frame.
(function () {
  var d = document.documentElement, t = 0, on = false;
  window.addEventListener('scroll', function () {
    if (!on) { on = true; d.classList.add('tk-scrolling'); }
    clearTimeout(t);
    t = setTimeout(function () { on = false; d.classList.remove('tk-scrolling'); }, 180);
  }, { passive: true });
})();
